

//


import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InputIconSelect, InputSelect, InputSwitch, InputText, InputTextArea } from '../Input';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { API_CARD_DEFS_URL, API_CARD_ELEMENT_SET_MATERIALIZE_URL, API_COLLECTIONS_URL, isAdmin } from '../../utils';
import { getCardScope, inUseWords, isRetiredCard, lookedUpClause } from '../../utils/cardScopes';
import { linkedLookupGroup, lookupScopeCards, lookupScopeCardIds, lookupTargetIds } from '../../utils/lookupScope';
import { canManage } from '../../utils/permissions';
import { cardEditorChangeCardPath } from '../../utils/changeCardEntry';
import { cardImportPath, savedCardHeaderActions } from './cardHeaderActions';
import { api } from '../../api/client';
import { recordView } from '../../services/views';
import ExportModal from '../common/ExportModal';
import HistoryModal from '../history/HistoryModal';
import { fetchCardHistory } from '../../services/history';
import RecordBadge from '../common/RecordBadge';
import DiscoverHint from './DiscoverHint';
import useDiscoverHint from '../../hooks/useDiscoverHint';
import CardPageFrame, { CardLoadState, CardOwnerFact, cardAwaitsShell, cardShellRedirect, useCardShell } from './CardPageFrame';
import { collectionCardPath, collectionCardsPath } from '../../config/collectionShell';
import Guidance from '../common/Guidance';
import GlobalLiveNotice from '../collections/GlobalLiveNotice';
import FormActionsFooter from '../common/FormActionsFooter';
import { CARDS_CRUMB, collectionCrumbs } from '../../utils/breadcrumbs';
import { buildActionMenu } from '../common/actionMenu';
import { produce } from 'immer';
import { Alert, App, Button, Collapse, Dropdown, Input, Modal, Segmented, Tag, Tooltip, Typography } from 'antd';
import { getDataTypeMeta } from '../../utils/dictionary';
import { DUPLICATE_BLANK, DUPLICATE_BLANK_TOGGLE, ELEMENT_OPTION_TOGGLES, FOLLOWABLE_TOGGLE, followableByDefault, fractionsAvailable, getOptionKeysForElement, isFollowableElement, isIdentifierNumber, lengthLimitText, percentAvailable, pruneOptionsForType, rankAvailable, toggleDescription } from '../../config/elementOptionPolicy';
import { lookupFamilyHasIdentity } from '../../utils/lookupMe';
import { classify, transformInfo } from '../../config/elementCoercion';
import DataTypeBadge from '../common/DataTypeBadge';
import DataTypePicker from '../common/DataTypePicker';
import { pluralize, slugify } from '../../utils/strings';
import _ from 'lodash';
import NoElementsEmptyState from './NoElementsEmptyState';
import { ARROW_DOWN, ARROW_UP, CLOSE, PLUS, DRAG_HANDLE, DUPLICATE, KEBAB, SLIDERS } from '../icons';
import { useScreenShortcuts } from '../../contexts/ShortcutsContext';
import { CardOptionLabel, buildCardHierarchyOptions, linkedCardLabel } from './CardVisuals';
import { CollectionScope } from '../../contexts/CollectionScopeContext';
import ToggleRow from '../common/ToggleRow';
import PrimaryDateList from './PrimaryDateList';
import { formatCount } from '../../utils/counts';
import ExtractElementSetModal from './ExtractElementSetModal';
import { attachableElementSets } from '../../utils/elementSets';
import CardElementSets, { SET_ERROR_CODES } from './CardElementSets';
import RecordOptions from './RecordOptions';
import RequiredWhenOptions from './RequiredWhenOptions';
import ScanOptions from './ScanOptions';
import LookupFilterOptions from './LookupFilterOptions';
import WorklistOptions from './WorklistOptions';
import QueryRefusalPick from '../items/QueryRefusalPick';
import useElementChoices from '../../hooks/useElementChoices';
import { elementRefusalOf, elementRefusalText, ownerElementOfField, refusalSentence, withElementChoice, withFieldPicks } from '../../utils/elementRefusal';
import { cardCommentSetting, cardPagePath, cardPrimaryDateSetting, chainElementSets, commentsAllowedForChain, commentsExpandedForChain, elementSetIdsOf, isDateElement, mergeChainElements, mergeChainEntries, primaryDateForChain, strictCommentAuditingForChain, primaryDateToken, resolveChain, setElementsOf, worklistForChain } from '../../utils/cards';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ulid } from 'ulid';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import useGlobalLookupTargets from '../../hooks/useGlobalLookupTargets';
import useLinkedSources from '../../hooks/useLinkedSources';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import DisplayTemplateEditor from './DisplayTemplateEditor';
import TileLayoutModal from './TileLayoutModal';
import FormLayoutModal from './FormLayoutModal';
import useFormLayout from '../../hooks/useFormLayout';
import useServerFeatures, { useServerLimits } from '../../hooks/useServerFeatures';
import RelatedListsOptions from './RelatedListsOptions';
import {
	DEFAULT_LISTS_PER_CARD,
	affectedSentences,
	firstListProblem,
	inheritedListsOf,
	isRelatedListsRefusal,
	forSave,
	mergeOwnLists,
	overlappingLists,
	ownListsOf,
	relatedListRefusalFields,
} from '../../utils/relatedLists';
import CardHome from './CardHome';
import CardDangerZone from './CardDangerZone';
import StaleRecordModal from '../common/StaleRecordModal';
import { checkStoredValues, destructiveElementEdits } from './hiddenValuesConfirm';
import { firstEmptyGroup, firstUntypedColumn } from '../../utils/groupColumns';
import { reapplyOver } from '../../utils/reapply';
import { CARD_TAB, CARD_TAB_LABELS, useCardTab } from './cardTabs';
import DrivenFromModal from './DrivenFromModal';
import FormulaModal from './FormulaModal';
import DrivenBackfillWarning from './DrivenBackfillWarning';
import { confirmDestructive } from '../common/confirmDestructive';
import { previewCardChange, previewDrivenChange, retireCard, unretireCard } from '../../services/cardDefs';
import { changedDrivenElements } from '../../utils/drivenChanges';
import { formulaElementResolver, formulaSummary, isFormulaDriven, mergedElementsForCard } from '../../utils/formulaTokens';
import { describeStoredDrivenRule } from '../../utils/drivenSummary';
import { isIdentityElement } from '../../utils/userElements';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import DateOptions from './DateOptions';
import MeasurementOptions from './MeasurementOptions';
import CurrencyOptions from './CurrencyOptions';
import { useOptionalPreferences } from '../../contexts/PreferencesContext';
import { KEEPR_CURRENCY } from '../../config/preferenceDefs';
import { newMoneyCurrency } from '../../utils/newMoneyCurrency';
import NumberOptions from './NumberOptions';
import SequenceOptions from './SequenceOptions';
import UserOptions from './UserOptions';
import PhoneOptions from './PhoneOptions';
import RatingOptions from './RatingOptions';
import TextOptions from './TextOptions';
import DefaultValueField from './DefaultValueField';
import RepeatingGroupOptions from './RepeatingGroupOptions';
import { COLUMN_OPTION_KEYS_EXCLUDED, GLOBAL_COLUMN_LOOKUP_SENTENCE, GLOBAL_COLUMN_TYPES_EXCLUDED, GROUP_ONLY_GROWS_SENTENCE, GROUP_PANEL_OPTIONS, COLUMN_TYPE_REASONS, COLUMN_TYPES_EXCLUDED, NESTING_SENTENCE, REPEATING_GROUP, columnsOf, isRepeatingGroup } from '../../config/repeatingGroups';
import { explicitDefault as explicitElementDefault, storedDefaultProblem, unchangedDefault } from '../../utils/elementDefaults';
import { choiceListOf, choiceTypeChangeWords, defaultForMultiToggle, withInertChoicesAsStored } from '../../utils/choiceValues';
import { FILE_DETACH, SHAPE_INSTRUCTION_KEY, SHAPE_LIST_TYPES, conversionAnswers, fileAcceptTightened, mergeConversionAnswers, refusalAsksClear, refusalAsksDetach, refusalAsksOwnUnits, shapeDirection, shapeRefusalFor, shapeWords } from '../../utils/shapeChange';
import FileOptions from './FileOptions';
import { isFileElement, typeChangeNeedsEmpty } from '../../utils/fileValues';
import { confirmationsLabel, isConfirmationsElement } from '../../utils/confirmationValues';
import { useCollections } from '../../contexts/CollectionsContext';
import MultiToggleDialog from './MultiToggleDialog';
import LocationOptions from './LocationOptions';
import ConversionUnitField from './ConversionUnitField';
import { conversionsFromResponse, describeConversion, effectiveElementNames, indexSavedElements, namespacedElementNames, planConversion, withConversionInstructions } from '../../utils/conversions';
import { isDateType } from '../../utils/dateBounds';
import { getMeasure } from '../../utils/units';
import { defaultUnitFor, unitSymbol, measuresLabel } from '../../utils/measurements';
import { describeStoredTemplate } from '../../utils/displayTemplate';
import { cardCollectionsPath } from '../../utils/listLinks';
import { escapeBelongsElsewhere } from '../../utils/escapeOwner';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import useElementGrants from '../../hooks/useElementGrants';
import ElementAccessModal from './ElementAccessModal';
import CopyGroupModal from './CopyGroupModal';
import { namesInUse as copyNamesInUse, rulesNotCopiedLines, withGroupCopies } from '../../utils/groupCopy';
import { governingGrant } from '../../utils/elementAccess';
import { isMaskedElement } from '../../utils/maskedElements';


const normalizeId = (maybe) => {
	if (!maybe) return undefined;
	if (typeof maybe === 'string') return maybe;
	if (typeof maybe === 'object') {
		if (typeof maybe.$oid === 'string') return maybe.$oid;
		if (maybe._id) return normalizeId(maybe._id);
		if (maybe.id) return normalizeId(maybe.id);
	}
	try {
		return maybe.toString();
	} catch (e) {
		return undefined;
	}
};

const buildParentChain = (cards = [], startingParentId) => {
	if (!startingParentId) return [];
	const byId = new Map(cards.map(c => [normalizeId(c?._id), c]));
	const chain = [];
	let cursor = startingParentId;
	const guard = new Set();
	while (cursor && !guard.has(cursor)) {
		guard.add(cursor);
		const node = byId.get(cursor);
		if (!node) break;
		chain.unshift(node);
		cursor = normalizeId(node.parentCardId);
	}
	return chain;
};

const buildDescendantSet = (cards = [], rootId) => {
	if (!rootId) return new Set();
	const map = new Map();
	cards.forEach(card => {
		const parentId = normalizeId(card?.parentCardId);
		if (!map.has(parentId)) map.set(parentId, []);
		map.get(parentId).push(card);
	});
	const visited = new Set();
	const stack = [rootId];
	while (stack.length) {
		const current = stack.pop();
		const children = map.get(current) || [];
		children.forEach(child => {
			const childId = normalizeId(child?._id);
			if (!childId || visited.has(childId)) return;
			visited.add(childId);
			stack.push(childId);
		});
	}
	return visited;
};

// An element's label is the reader's words for it, { singular, plural }, and
// the editor's two fields (and the item form's inputs) are built on the pair
// being there. The API completes it on every write now, but cards written
// before that — or by a writer that skipped it — may carry a plain string,
// half the pair, or nothing at all. Returns the completed label, or null when
// the element's own is already whole.
const completeLabel = (el) => {
	const raw = el?.label;
	const asObject = raw && typeof raw === 'object' ? raw : null;
	const text = (value) => (value ?? '').toString().trim();
	// A legacy string label is the singular; with none, the element's name
	// stands in — the same fallback every reader on the platform uses.
	const singular = text(asObject ? asObject.singular : raw) || text(el?.name);
	const plural = text(asObject ? asObject.plural : '') || pluralize(singular);
	if (asObject && asObject.singular === singular && asObject.plural === plural) return null;
	return { ...asObject, singular, plural };
};

const createEmptyCard = () => ({
	name: '',
	description: '',
	color: '',
	icon: '',
	parentCardId: '',
	elements: [],
	// The element sets the card ATTACHES (docs/plans/element-sets-and-me.md),
	// ordered ids; none until somebody adds one.
	elementSets: [],
	displayTemplate: '',
	// Card-level options. Comments are off until a manager turns them on, and
	// an absent setting already reads off, so a new card states nothing.
	options: {},
});



// Stable empties for a cache that has not loaded element sets (or a test's
// mock that never mentions them), so memos keyed on them do not churn.
/**
 * 409 duplicates_exist, read under the element's Unique values switch: how
 * many values are shared, then each sample value with the items that hold it
 * as record badges. The API names only the items this person can read; the
 * rest are counted ("and 2 you can't open").
 */
function UniqueDuplicatesAlert({ refusal, cardsById }) {
	const samples = Array.isArray(refusal.samples) ? refusal.samples : [];
	const shared = Number(refusal.count) || samples.length;
	const lead = shared === 1
		? 'One value is used by more than one item.'
		: `${shared.toLocaleString('en-US')} values are used by more than one item.`;
	return (
		<Alert
			className='card-def-element__option-error card-def-element__duplicates'
			type='error'
			showIcon
			message={`${lead} Change or clear the duplicates, then turn this on.`}
			description={samples.length ? (
				<ul className='card-def-element__duplicate-list'>
					{samples.map((sample, i) => (
						<li key={i} className='card-def-element__duplicate'>
							<span className='card-def-element__duplicate-value'>
								{sample.value ? `“${sample.value}”` : 'A value you can’t see'}
								{` · ${Number(sample.count) || 0} items`}
							</span>
							<span className='card-def-element__duplicate-items'>
								{(sample.items || []).map(item => (
									<RecordBadge
										key={item._id}
										kind='item'
										record={cardsById?.[normalizeId(item.card_id)] || {}}
										title={item.title || 'Untitled'}
										size='xs'
										collection={item.collection_id}
										to={`/collections/${normalizeId(item.collection_id)}/items/${normalizeId(item._id)}`}
									/>
								))}
								{sample.hidden ? <Typography.Text type='secondary'>{`and ${sample.hidden} you can’t open`}</Typography.Text> : null}
							</span>
						</li>
					))}
				</ul>
			) : null}
		/>
	);
}

/** A 400 invalid_options about the card's primary date (utils/cardPrimaryDate.js names the option). */
const isPrimaryDateRefusal = coded => coded?.code === 'invalid_options' && !coded?.element && /primaryDate/.test(coded?.message || '');
const NO_SETS = Object.freeze([]);
const NO_SETS_BY_ID = Object.freeze({});
const NO_KEYS = Object.freeze([]);
const NO_COLLECTIONS = Object.freeze([]);

/**
 * "Files are off in Kitchen. Turn them on in its settings." — G9's one line,
 * the door to each collection's own "Allow attachments" (Settings tab). With
 * several, each name is its own door.
 */
function FilesOffDoor({ collections }) {
	const settingsPath = c => `/collections/${c._id}/form?tab=settings`;
	if (collections.length === 1) {
		const [only] = collections;
		return (
			<span>
				{`Files are off in ${only.name}, so this element is hidden there. `}
				<Link to={settingsPath(only)}>Turn them on in its settings</Link>
			</span>
		);
	}
	return (
		<span>
			{`Files are off in ${collections.length} collections that use this card, so this element is hidden there: `}
			{collections.map((c, i) => (
				<Fragment key={c._id}>
					{i ? ', ' : ''}
					<Link to={settingsPath(c)}>{c.name}</Link>
				</Fragment>
			))}
		</span>
	);
}

// Hoisted to top-level so its identity is stable across renders
function SortableElement({ id, children }) {
	const {
		attributes,
		listeners,
		setActivatorNodeRef,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id });
	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.6 : 1,
		position: isDragging ? 'relative' : undefined,
		zIndex: isDragging ? 999 : undefined,
	};

	return (
		<div ref={setNodeRef} style={style}>
			{children({ attributes, listeners, setActivatorNodeRef })}
		</div>
	);
}

function SortableChoice({ id, children }) {
	const { attributes, listeners, setActivatorNodeRef, setNodeRef, transform, transition, isDragging } = useSortable({ id });
	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		background: isDragging ? 'rbga(0, 0, 0, .02)' : undefined,
		position: isDragging ? 'relative' : undefined,
		zIndex: isDragging ? 999 : undefined,
	};

	return (
		<div ref={setNodeRef} style={style}>
			{children({ attributes, listeners, setActivatorNodeRef })}
		</div>
	);
}





// Never compared when a stale save is reapplied: ids, lifecycle stamps,
// response-only annotations. They always take the card as it stands.
const CARD_NOT_REAPPLIED = ['_id', 'updatedAt', 'createdAt', 'lastUpdate', 'createdOn', 'createdBy', 'usage'];
const CARD_FIELD_LABELS = {
	name: 'Name', description: 'Description', icon: 'Icon', color: 'Color', key: 'Key',
	displayTemplate: 'Display value', parentCardId: 'Parent card', elements: 'Elements',
	elementSets: 'Element sets', options: 'Card options',
};


/** A save that met another at the same moment (409 `element_id_conflict`): nothing lost, save again. */
export const ELEMENT_ID_CONFLICT_TEXT = 'The card was not saved because another save happened at the same moment. Save again: nothing you changed was lost.';

/**
 * Which filter on the card a refused element pick is about, in words (element
 * ids step 3): the worklist's, or the one on an element's row — a lookup's
 * picker filter, a driven total's.
 */
export function filterRefusalWhere(coded, labelOf = name => name) {
	if (!coded) return '';
	if (coded.filterField === 'filter') return 'In the worklist’s filter';
	if (coded.element) return `In the filter on ${labelOf(coded.element) || coded.element}`;
	return 'In a filter on this card';
}

export default function CardDefinitionForm() {

	//
	const [card, setCard] = useState(() => createEmptyCard());
	const { isDirty, resetDirty, guardDiscard } = useDirtyForm(card);
	const resetDirtyRef = useRef(resetDirty);
	// Read by handleChange (defined before the lock is computed each render).
	const editingLockedRef = useRef(false);

	useEffect(() => {
		resetDirtyRef.current = resetDirty;
	}, [resetDirty]);

	const markPristine = useCallback((nextSnapshot) => {
		resetDirtyRef.current(nextSnapshot);
	}, []);
	// The definition as the API last returned it (loaded or saved). A
	// number <-> measurement type change is a CONVERSION of stored item
	// values (utils/conversions.js), and only an element this copy holds can
	// have any; the editors read their saved counterpart by name from here.
	const [savedCard, setSavedCard] = useState(null);
	// Whether the stored card has answered (an existing card only). The blank
	// `card` above is a placeholder, never a draft: `savedCard` is what Save
	// PATCHes against and the form draws nothing, and offers no write, until
	// this reads 'ready' (docs/platform-standards.md §14; KPR-729).
	// 'loading' | 'ready' | 'error' (the read failed: Retry) | 'missing' (the
	// API answered that it is not there, or not this person's).
	const [loadState, setLoadState] = useState('loading');
	const [loadAttempt, setLoadAttempt] = useState(0);
	const savedElements = useMemo(() => indexSavedElements(savedCard?.elements), [savedCard]);
	const [allCards, setAllCards] = useState([]);
	const [allCardsLoading, setAllCardsLoading] = useState(false);
	const [exportOpen, setExportOpen] = useState(false);
	const [historyOpen, setHistoryOpen] = useState(false);

	const { cardId } = useParams();
	const [ searchParams ] = useSearchParams();
	// Inside the collection shell (`/collections/:id/cards/:cardId`, KPR-1282) or the platform page.
	const { inShell, shellCollectionId, shellCollection, routeCollectionId } = useCardShell();

	// 'new' is the canonical new-card route; '-1' is the legacy sentinel.
	const isNewRecord = cardId + '' === 'new' || cardId + '' === '-1';

	// New cards belong somewhere from birth: `/collections/:id/cards/new` (KPR-1284;
	// the old `/cards/new?collectionId=` redirects there), optionally ?parentCardId=
	// to extend an existing (usually global) card. Admins with no collectionId
	// create GLOBAL cards. Anyone else with neither is blocked below — the
	// API would reject the save anyway.
	const targetCollectionId = isNewRecord ? (routeCollectionId || searchParams.get('collectionId') || '') : '';
	const presetParentCardId = isNewRecord ? (searchParams.get('parentCardId') || '') : '';
	const admin = isAdmin();
	const createBlocked = isNewRecord && !targetCollectionId && !admin;
	// Discover first (KPR-1440): the name being typed on a new card is searched;
	// a strong match shows one dismissable line, and nothing else changes.
	const [discoverDismissed, setDiscoverDismissed] = useState(false);
	const discoverPackage = useDiscoverHint(card?.name, isNewRecord && !createBlocked && !discoverDismissed);
	// An existing card that has not been read (or failed to be): a skeleton or
	// an error with Retry stands where the form would be. `savedCard` must be
	// there too, so no path can reach a PATCH without the stored card it patches.
	const awaitingCard = !isNewRecord && (loadState !== 'ready' || !savedCard || cardAwaitsShell({ inShell, shellCollectionId, shellCollection, card: savedCard }));

	const navigate = useNavigate();
	const { message, modal, notification } = App.useApp();
	// Keep the app-wide card-definitions cache in sync with edits made here.
	// The cache also holds every element set this viewer may read — what the
	// Element sets section offers, and what the parent chain's sets resolve
	// through.
	const {
		refresh: refreshCardDefinitions,
		getElementsForCard,
		cardsById: cachedCardsById,
		elementSets: cachedElementSets = NO_SETS,
		elementSetsById: cachedElementSetsById = NO_SETS_BY_ID,
	} = useCardDefinitions();

	const mapCardFromApi = useCallback((incoming = {}) => ({
		...incoming,
		parentCardId: normalizeId(incoming?.parentCardId) || '',
		// Ids as strings, in order, whatever spelling the API stored.
		elementSets: elementSetIdsOf(incoming),
		displayTemplate: incoming?.displayTemplate || '',
		// Heal legacy choices saved without a stored value: derive it from the
		// label so selections persist as distinct strings — legacy elements
		// saved with NO dataType at all, which the item form has always
		// rendered as short text — and elements saved with no LABEL, whose
		// words come from the element's name (completeLabel above). Resolving
		// it here (into both the draft and its pristine twin, so nothing reads
		// as an edit) is what makes the next save WRITE what the reader
		// already sees: an old card heals itself the first time someone saves
		// it, with no migration. Only an element being authored right now is
		// untyped.
		elements: (incoming?.elements || []).map(el => {
			const dataType = el?.dataType || 'text-small';
			const label = completeLabel(el);
			const healed = (el?.dataType === dataType && !label)
				? el
				: { ...el, dataType, ...(label ? { label } : null) };
			// A Choice's list only: a list another type carries is inert, sent
			// back exactly as stored (KPE-033; the API refuses a changed one),
			// so it is never healed.
			const choices = el?.options?.choices;
			if (dataType !== 'choice' || !Array.isArray(choices) || !choices.length) return healed;
			return {
				...healed,
				options: {
					...healed.options,
					choices: choices.map(c => (
						c && !c.value && c.label ? { ...c, value: slugify(c.label) } : c
					)),
				},
			};
		}),
	}), []);

	const fetchAllCards = useCallback(() => {
		setAllCardsLoading(true);
		api.get(API_CARD_DEFS_URL).then(res => {
			if (res.status === 200) {
				setAllCards(Array.isArray(res.data) ? res.data : []);
			}
		}).catch(() => {
			setAllCards([]);
		}).finally(() => setAllCardsLoading(false));
	}, []);

	useEffect(() => {
		fetchAllCards();
	}, [fetchAllCards]);


	useEffect(() => {
		if (isNewRecord) {
			const fresh = mapCardFromApi({ ...createEmptyCard(), parentCardId: presetParentCardId });
			setCard(fresh);
			setSavedCard(null);
			markPristine(fresh);

			return;
		}

		// Another card's draft must not stand under this one's URL while it loads.
		const blank = mapCardFromApi(createEmptyCard());
		setLoadState('loading');
		setSavedCard(null);
		setCard(blank);
		markPristine(blank);

		// Stale-response guard: navigating /cards/A -> /cards/B reuses this
		// component, and a slow A response landing after B's would otherwise
		// win — showing card A (and A's owning-collection banner/permissions)
		// under B's URL.
		let isCurrent = true;
		api.get(API_CARD_DEFS_URL + '/' + cardId, {
			params: {

			},
		})
			.then(data => {
				if (!isCurrent) return false;
				if (data.status !== 200 || !data.data) {
					setLoadState('error');
					return false;
				}

				const mapped = mapCardFromApi(data.data);
				setCard(mapped);
				setSavedCard(mapped);
				markPristine(mapped);
				setLoadState('ready');
			})
			.catch((error) => {
				// 401 is handled globally by the api client. Only an answer is a
				// verdict: a 404/403 is a card that is not there (or not theirs);
				// no response, or a 5xx, is worth another try.
				if (!isCurrent) return;
				const status = error?.response?.status;
				setLoadState(status === 404 ? 'missing' : status === 403 ? 'no-access' : 'error');
			});
		return () => { isCurrent = false; };

	}, [isNewRecord, cardId, presetParentCardId, mapCardFromApi, markPristine, loadAttempt]);

	const currentCardId = normalizeId(card?._id);

	const exportTarget = useMemo(
		() => (currentCardId ? { kind: 'card', cardId: currentCardId } : null),
		[currentCardId]
	);
	// const hierarchyList = useMemo( () => buildCardHierarchyList( allCards ), [ allCards ] );

	const cardLookupOptions = useMemo(() => buildCardHierarchyOptions(allCards), [allCards]);

	const descendantSet = useMemo(() => buildDescendantSet(allCards, currentCardId), [allCards, currentCardId]);

	// A RETIRED global card (KPR-280) takes no new card built on it — the API
	// answers 400 card_retired — so it is not offered as a parent, unless it
	// already is this card's (a card that extends it keeps saving).
	const retiredIds = useMemo(
		() => new Set((allCards || []).filter(isRetiredCard).map(c => normalizeId(c?._id)).filter(Boolean)),
		[allCards]
	);
	const parentOptions = useMemo(() => {
		const selectedValue = normalizeId(card?.parentCardId);
		return cardLookupOptions.filter(opt => {
			const id = opt.value;
			if (!id) return false;
			// A saved card keeps the parent it has, retired or not; a NEW card
			// is a new adopter (below, a retired preset parent is dropped).
			if (selectedValue && id === selectedValue) return !(isNewRecord && retiredIds.has(id));
			if (id === currentCardId) return false;
			if (retiredIds.has(id)) return false;
			return !descendantSet.has(id);
		});
	}, [cardLookupOptions, card?.parentCardId, currentCardId, descendantSet, retiredIds, isNewRecord]);

	// A NEW card opened to extend a card that has retired since the link was
	// made (an old bookmark, a Card Library open before the retire): the API
	// would refuse the save (400 card_retired) after the whole card was built,
	// so the parent is dropped now and the Hierarchy panel says why.
	const [retiredPresetName, setRetiredPresetName] = useState(null);
	useEffect(() => {
		if (!isNewRecord) return;
		const id = normalizeId(card?.parentCardId);
		if (!id || !retiredIds.has(id)) return;
		setRetiredPresetName((allCards || []).find(c => normalizeId(c?._id) === id)?.name || 'That card');
		setCard(prev => ({ ...prev, parentCardId: '' }));
	}, [isNewRecord, card?.parentCardId, retiredIds, allCards]);

	const parentChain = useMemo(() => buildParentChain(allCards, normalizeId(card?.parentCardId)), [allCards, card?.parentCardId]);
	// The same chain with each ancestor's element sets folded in — what an
	// item of this card inherits from above (utils/cards resolveChain).
	const resolvedParentChain = useMemo(() => resolveChain(parentChain, cachedElementSetsById), [parentChain, cachedElementSetsById]);

	// ---- Element sets (docs/plans/element-sets-and-me.md) -------------------
	// The draft's attached sets, resolved; the ancestors' (inherited, not
	// this card's to shed); and, further down, what is left to offer under
	// "Add element set…".
	const draftSetIds = useMemo(() => elementSetIdsOf(card), [card]);
	const draftSets = useMemo(
		() => draftSetIds.map(id => cachedElementSetsById[id]).filter(Boolean),
		[draftSetIds, cachedElementSetsById]
	);
	const draftSetElements = useMemo(() => draftSets.flatMap(setElementsOf), [draftSets]);
	const inheritedSets = useMemo(() => chainElementSets(resolvedParentChain), [resolvedParentChain]);
	// Elements the card has without owning a row: its ancestors' (their sets
	// already folded in by resolveChain) and its own attached sets'. The
	// empty Elements list only explains itself when there are none of these
	// either (KPR-324) — a card extending a full parent is not "empty".
	const carriedElementCount = useMemo(
		() => mergeChainElements(resolvedParentChain).length + draftSetElements.length,
		[resolvedParentChain, draftSetElements]
	);

	// The home reads the SAVED card, never the draft, so its ancestry — and
	// everything resolved through it — is the saved card's too.
	const savedParentChain = useMemo(
		() => buildParentChain(allCards, normalizeId(savedCard?.parentCardId)),
		[allCards, savedCard?.parentCardId]
	);
	const savedCommentsAllowed = useMemo(
		() => commentsAllowedForChain([...savedParentChain, savedCard].filter(Boolean)),
		[savedParentChain, savedCard]
	);
	// RELATED LISTS (KPE-029, KPR-720): the card's own, as the draft holds them
	// and as they are stored, and the ones it inherits (the card read's
	// computed copy, so always the SAVED card's). `limit` is the contract's.
	const draftRelatedLists = useMemo(() => ownListsOf(card), [card]);
	const savedRelatedLists = useMemo(() => ownListsOf(savedCard), [savedCard]);
	// The computed copy rides the card READS; a save answers with the stored
	// document alone, so after one the cards list (re-read by the save) has it.
	const inheritedRelatedLists = useMemo(() => {
		if (Array.isArray(savedCard?.relatedLists)) return inheritedListsOf(savedCard);
		const listed = allCards.find(entry => normalizeId(entry?._id) === normalizeId(savedCard?._id));
		return inheritedListsOf(listed);
	}, [savedCard, allCards]);
	const serverLimits = useServerLimits();
	const relatedListsLimit = serverLimits.relatedListsPerCard || DEFAULT_LISTS_PER_CARD;
	// (With every node's element sets folded in: a set's date can be the
	// primary date, exactly like an inherited one.)
	const savedPrimaryDate = useMemo(
		() => primaryDateForChain(resolveChain([...savedParentChain, savedCard].filter(Boolean), cachedElementSetsById)),
		[savedParentChain, savedCard, cachedElementSetsById]
	);
	// The SAVED card's merged elements with where each came from — what the
	// form layout editor arranges (the API validates a layout against the
	// stored card, so a draft's unsaved elements are not offered).
	const savedFormEntries = useMemo(() => {
		if (!savedCard) return [];
		const ownId = normalizeId(savedCard?._id);
		return mergeChainEntries(resolveChain([...savedParentChain, savedCard].filter(Boolean), cachedElementSetsById))
			.map(({ element, card: node, set }) => ({ element, fromCard: normalizeId(node?._id) === ownId ? null : node, fromSet: set }));
	}, [savedParentChain, savedCard, cachedElementSetsById]);
	// The form layout that applies to this card (its own, or the nearest
	// ancestor's): the home lists elements in it, the Display tab says whose.
	const { layout: savedFormLayout } = useFormLayout(isNewRecord ? null : currentCardId);

	const selectedParent = useMemo(() => (allCards || []).find(c => normalizeId(c?._id) === normalizeId(card?.parentCardId)), [allCards, card?.parentCardId]);

	// An unsaved card has no id, and a parentless card's parentCardId
	// normalizes to the same undefined — without the guard every top-level
	// card would count as a child of a card that does not exist yet.
	const childCards = useMemo(
		() => (currentCardId ? (allCards || []).filter(c => normalizeId(c?.parentCardId) === currentCardId) : []),
		[allCards, currentCardId]
	);

	const sortedChildCards = useMemo(() => [...childCards].sort((a, b) => ((a?.name || '').localeCompare(b?.name || ''))), [childCards]);

	// --- Comments (KPE-005, KPR-305) ----------------------------------------
	// Comments replace the card's notes section. Each switch shows the state
	// an item would get: this card's own setting when it has one, else the
	// nearest ancestor that states one, else OFF (the owner's ruling). A save
	// writes only what this card says itself, so a child never pins the value
	// it inherits. The notes-era Notes switch is gone from the editor; a
	// stored `options.notes` rides along in the card's options untouched.
	const commentsChain = useMemo(() => [...parentChain, card], [parentChain, card]);
	const commentsAllowed = useMemo(() => commentsAllowedForChain(commentsChain), [commentsChain]);
	const strictCommentAuditing = useMemo(() => strictCommentAuditingForChain(commentsChain), [commentsChain]);
	const commentsExpanded = useMemo(() => commentsExpandedForChain(commentsChain), [commentsChain]);
	const inheritedComments = useMemo(() => {
		for (let i = parentChain.length - 1; i >= 0; i -= 1) {
			const setting = cardCommentSetting(parentChain[i], 'allowComments');
			if (setting !== undefined) return { name: parentChain[i]?.name || 'The parent card', enabled: setting };
		}
		return null;
	}, [parentChain]);
	const commentsHelp = 'Lets people who can edit an item add comments to it.'
		+ (inheritedComments ? ` ${inheritedComments.name} has it ${inheritedComments.enabled ? 'on' : 'off'}; this card's own setting wins.` : '');
	const strictAuditingHelp = commentsAllowed
		? 'A comment deleted while this is on leaves a "Deleted comment" row that shows who deleted it and when. Comments deleted while it was off stay hidden.'
		: 'Turn on Allow comments first.';
	const commentsExpandedHelp = 'Opens the Comments section unfolded on a new item.';

	// --- Primary date (docs/plans/primary-date.md) ---------------------------
	// Created, Updated, or one of the card's date elements — its own and its
	// ancestors'. An unsaved element is offered under the name the API will
	// give it (effectiveElementNames), which is the name the setting stores.
	// The select shows the resolved answer; a choice the current elements
	// no longer hold reads as Created, and the save writes that, so what is
	// stored is never a name the API would refuse.
	const effectiveElements = useMemo(() => {
		const list = card?.elements || [];
		const names = effectiveElementNames(list);
		return list.map((el, i) => (el ? { ...el, name: names[i] } : el));
	}, [card?.elements]);
	// This card's whole contribution as the API resolves it: own elements
	// under their effective names, then its attached sets' elements.
	const leafElements = useMemo(() => [...effectiveElements, ...draftSetElements], [effectiveElements, draftSetElements]);
	// Inherited like Notes and the primary date: the most specific setting in
	// the chain wins, and `false` on a child turns a parent's list off.
	// The stored setting, not the resolved policy: this panel EDITS the
	// card's own answer, and showing an inherited one as if it were this
	// card's would make the switch lie about what it writes.
	const recordPolicySetting = card?.options?.record ?? null;

	// Every element a worklist may name, exactly as the API resolves them:
	// inherited, this card's own (under the draft's names), and each attached
	// set's — merged descendant-wins. The picker offering less than the write
	// path accepts is how an author ends up hand-editing JSON.
	const nameableElements = useMemo(
		() => mergeChainElements([ ...resolvedParentChain, card ? { ...card, elements: leafElements } : null ].filter(Boolean)),
		[ resolvedParentChain, card, leafElements ]
	);

	const worklist = useMemo(
		() => worklistForChain([ ...resolvedParentChain, { ...card, elements: leafElements } ]),
		[ resolvedParentChain, card, leafElements ]
	);

	const primaryDate = useMemo(
		() => primaryDateForChain([...resolvedParentChain, { ...card, elements: leafElements }]),
		[resolvedParentChain, card, leafElements]
	);
	const primaryDateOptions = useMemo(() => {
		const options = [
			{ value: 'created', label: 'Created' },
			{ value: 'updated', label: 'Updated' },
		];
		const seen = new Set();
		// An inherited date is labelled with its card; a set's with its set
		// ("Due · To do"), wherever in the chain the set is attached.
		resolvedParentChain.forEach(node => {
			(node?.elements || []).forEach(el => {
				if (!isDateElement(el) || seen.has(el.name)) return;
				seen.add(el.name);
				options.push({ value: el.name, label: `${el.label?.singular || el.name} · ${el.fromSet?.name || node?.name || 'Parent card'}` });
			});
		});
		effectiveElements.forEach(el => {
			if (!isDateElement(el) || seen.has(el.name)) return;
			seen.add(el.name);
			options.push({ value: el.name, label: el.label?.singular || el.name });
		});
		draftSetElements.forEach(el => {
			if (!isDateElement(el) || seen.has(el.name)) return;
			seen.add(el.name);
			options.push({ value: el.name, label: `${el.label?.singular || el.name} · ${el.fromSet?.name || 'Element set'}` });
		});
		return options;
	}, [resolvedParentChain, effectiveElements, draftSetElements]);
	const inheritedPrimaryDate = useMemo(() => {
		for (let i = parentChain.length - 1; i >= 0; i -= 1) {
			const setting = cardPrimaryDateSetting(parentChain[i]);
			if (setting !== undefined) return { name: parentChain[i]?.name || 'The parent card', setting };
		}
		return null;
	}, [parentChain]);
	// The row's value: a string, or the ordered list ("Scheduled, then Due").
	const primaryDateValue = useMemo(() => primaryDateToken(primaryDate), [primaryDate]);
	const describeTokens = useCallback(
		tokens => (Array.isArray(tokens) ? tokens : [tokens]).map(token => primaryDateOptions.find(o => o.value === token)?.label || token).join(', then '),
		[primaryDateOptions]
	);
	const primaryDateHelp = (Array.isArray(primaryDateValue)
		? 'Items sort and lead with the first of these that has a value.'
		: 'The date items sort by and lead with, on every card and in its own list column. Created unless you choose a date element.')
		+ (inheritedPrimaryDate ? ` ${inheritedPrimaryDate.name} uses ${describeTokens(inheritedPrimaryDate.setting)}; this card's own setting wins.` : '');

	const lineageNodes = useMemo(() => {
		const nodes = [...parentChain];
		const label = (card?.name && card?.name.trim()) ? card.name : (isNewRecord ? 'New card' : 'Current card');
		nodes.push({ _id: currentCardId || 'current', name: label });
		return nodes;
	}, [parentChain, card?.name, currentCardId, isNewRecord]);

	const displayElementOptions = useMemo(() => {
		// Every element an item of this card gets — inherited, own (under the
		// draft's own names), and each attached set's — a set element named
		// for its set the way an inherited one is named for its card.
		const chain = [...resolvedParentChain, card ? { ...card, elements: leafElements } : null].filter(Boolean);
		const seen = new Set();
		const items = [];
		chain.forEach(node => {
			(node?.elements || []).forEach(el => {
				const name = el?.name;
				if (!name || seen.has(name)) return;
				seen.add(name);
				// A file is never part of a title (PR 10b): its value is an
				// attachment id, which no title can print. Nor a "Me too" count
				// (KPR-390): it moves without the item being saved, so a stored
				// title holding it would be wrong after the next confirmation.
				if (isFileElement(el) || isConfirmationsElement(el)) return;
				// Nor a masked value (KPR-1348): a title is printed everywhere
				// the item is, and the API refuses a title that names one.
				if (isMaskedElement(el)) return;
				const cardName = el?.fromSet?.name || node?.name || 'Card';
				items.push({
					name,
					label: el?.label?.singular || name,
					cardName,
					dataType: el?.dataType,
					// The preview formats a measurement through its own display
					// rules, and `unit(...)` checks its argument against the measure.
					options: el?.options,
				});
			});
		});
		return items;
	}, [resolvedParentChain, card, leafElements]);


	// The tile-layout editor wants real element definitions, not the flattened
	// options the template preview reads — and the SAVED card's, resolved
	// through the cache the way the items page resolves them, because that is
	// the list the API validates a layout against. The draft's own list is
	// what the display-title preview reads; it can run ahead of the server.
	const savedTileElementDefs = useMemo(
		() => (currentCardId && typeof getElementsForCard === 'function' ? getElementsForCard(currentCardId) || [] : []),
		[getElementsForCard, currentCardId]
	);

	// What the driven-from modal needs to know about the card being edited, and
	// nothing else: a narrow, memoized object so editing a name or an element
	// doesn't re-render every element panel through the CardDefElements memo.
	// A brand-new card has no id yet — only its parent chain can be linked to.
	const drivenTargetCard = useMemo(() => ({
		_id: currentCardId,
		name: card?.name,
		parentCardId: normalizeId(card?.parentCardId),
	}), [currentCardId, card?.name, card?.parentCardId]);

	const handleChange = useCallback((path, value) => {
		if (editingLockedRef.current) return;
		setCard(prevState => produce(prevState, draft => {
			_.set(draft, path, value);
		}));
	}, []);

	const displaySummary = useMemo(() => {
		const labelMap = new Map(displayElementOptions.map(opt => [opt.name, opt.label]));
		return describeStoredTemplate(card?.displayTemplate, labelMap) || 'Uses card name by default';
	}, [card?.displayTemplate, displayElementOptions]);

	// Ownership scope drives what this form may do. Global cards are
	// keepr-controlled: read-only for everyone but admins (extend them from a
	// collection's Card Library instead). Collection-owned cards are editable
	// only by that collection's managers — checked against the owning
	// collection's myAccess, fetched below. The API enforces the same.
	const scope = isNewRecord ? null : getCardScope(card);
	const owningCollectionId = scope === 'collection' ? normalizeId(card?.collection_id) : null;
	// undefined = loading/not applicable; null = unreadable; doc = loaded.
	// The record drives BOTH the manage check and the banner's badge/link. In
	// the shell of the collection that owns it, the layout's document is it.
	const shellOwns = Boolean(owningCollectionId) && owningCollectionId === shellCollectionId && Boolean(shellCollection);
	const [fetchedOwner, setOwningCollection] = useState(undefined);
	const owningCollection = shellOwns ? shellCollection : fetchedOwner;

	useEffect(() => {
		setOwningCollection(undefined);
		if (!owningCollectionId || shellOwns) return;
		let isMounted = true;
		api.get(`${API_COLLECTIONS_URL}/${owningCollectionId}`)
			.then(({ data, status }) => {
				if (isMounted && status === 200) setOwningCollection(data || null);
			})
			.catch(() => {
				// Can't read the owning collection -> certainly can't manage it.
				if (isMounted) setOwningCollection(null);
			});
		return () => { isMounted = false; };
	}, [owningCollectionId, shellOwns]);

	// ---- Lookup scope (utils/lookupScope.js) --------------------------------
	// A lookup may only point at a card inside this definition's own scope:
	// the collection's cards for a collection card, global cards for a global
	// one. A NEW collection card has no owning record loaded above, so the
	// target collection is read here — its cards[] refs are half the scope.
	const shellTargets = Boolean(targetCollectionId) && targetCollectionId === shellCollectionId && Boolean(shellCollection);
	const [fetchedTarget, setTargetCollection] = useState(undefined);
	const targetCollection = shellTargets ? shellCollection : fetchedTarget;
	useEffect(() => {
		setTargetCollection(undefined);
		if (!targetCollectionId || shellTargets) return undefined;
		let isMounted = true;
		api.get(`${API_COLLECTIONS_URL}/${targetCollectionId}`)
			.then(({ data, status }) => { if (isMounted && status === 200) setTargetCollection(data || null); })
			.catch(() => { if (isMounted) setTargetCollection(null); });
		return () => { isMounted = false; };
	}, [targetCollectionId, shellTargets]);

	// ---- The collection the tile layout's two anchored tiers write to -------
	// "This collection" and "Just me" need a collection in hand. A collection
	// card has its own; a GLOBAL card has none, so a link from inside a
	// collection is opened inside it (KPR-1285; the shell's document), or says
	// which one it came from with `?collectionId=`. Without either, those two tiers say where to go instead (layoutTiers.js).
	const visitedCollectionId = !isNewRecord && !owningCollectionId ? (routeCollectionId || searchParams.get('collectionId') || '') : '';
	const [fetchedVisited, setVisitedCollection] = useState(undefined);
	const visitedCollection = inShell && visitedCollectionId === shellCollectionId ? shellCollection : fetchedVisited;
	useEffect(() => {
		setVisitedCollection(undefined);
		if (!visitedCollectionId || inShell) return undefined;
		let isMounted = true;
		api.get(`${API_COLLECTIONS_URL}/${visitedCollectionId}`)
			.then(({ data, status }) => { if (isMounted && status === 200) setVisitedCollection(data || null); })
			.catch(() => { if (isMounted) setVisitedCollection(null); });
		return () => { isMounted = false; };
	}, [visitedCollectionId, inShell]);
	const tileCollectionId = owningCollectionId || (visitedCollection ? visitedCollectionId : null);
	const tileCollection = (owningCollectionId ? owningCollection : visitedCollection) || null;

	// ---- Who can see or change each element (KPR-509) ------------------------
	// A rule lives in a collection, so it is set in the collection in hand —
	// the card's own, or the one a global card was opened from — and only by
	// a manager of it. The API says whether it offers rules at all
	// (hooks/useElementGrants: an older API, the switch off, or a refusal all
	// mean no door), so nothing here assumes it does. A rule saves through its
	// own endpoint, never this page's Save, and so is offered whether or not
	// this person may edit the card itself.
	const accessCollectionId = !isNewRecord && tileCollection && canManage(tileCollection) ? tileCollectionId : null;
	const changeCardPath = !isNewRecord && accessCollectionId ? cardEditorChangeCardPath(accessCollectionId, currentCardId, true) : null;
	const elementGrants = useElementGrants(accessCollectionId, accessCollectionId ? currentCardId : null);
	const accessChainIds = useMemo(
		() => [...savedParentChain.map(node => normalizeId(node?._id)), currentCardId].filter(Boolean),
		[savedParentChain, currentCardId]
	);
	const accessCardNames = useMemo(() => {
		const names = {};
		[...savedParentChain, savedCard].forEach(node => {
			const id = normalizeId(node?._id);
			if (id) names[id] = node?.name || '(untitled card)';
		});
		return names;
	}, [savedParentChain, savedCard]);
	const [accessElement, setAccessElement] = useState(null);
	const elementAccess = useMemo(() => (elementGrants.available ? {
		grants: elementGrants.grants,
		chainIds: accessChainIds,
		onOpen: setAccessElement,
	} : null), [elementGrants.available, elementGrants.grants, accessChainIds]);
	// The editor row's door and chip, for the card's own SAVED elements: a
	// rule names the element's id, which a new element has none of yet. By
	// id, never by the draft's name — an unsaved rename (or two names
	// swapped) must not open another element's rule.
	const accessFor = useCallback((draftElement) => {
		if (!elementAccess || !draftElement?.id) return null;
		const saved = (savedCard?.elements || []).find(el => el?.id === draftElement.id) || null;
		if (!saved) return null;
		const limitedBy = (id) => {
			const governing = governingGrant(elementAccess.grants, elementAccess.chainIds, id);
			const rules = governing?.grant?.rules;
			return Boolean((Array.isArray(rules) && rules.length) || governing?.grant?.keeprOnly === true);
		};
		return {
			limited: limitedBy(saved.id),
			onOpen: () => setAccessElement(saved),
			// A REPEATING GROUP's columns (KPR-688): each SAVED column (by its id,
			// as an element is) has a rule of its own, narrowing the group's.
			...(isRepeatingGroup(saved) ? {
				columnFor: (draftColumn) => {
					if (!draftColumn?.id) return null;
					const savedColumn = columnsOf(saved).find(col => col?.id === draftColumn.id) || null;
					if (!savedColumn) return null;
					return { limited: limitedBy(savedColumn.id), onOpen: () => setAccessElement({ ...savedColumn, group: saved }) };
				},
			} : {}),
		};
	}, [elementAccess, savedCard]);

	const lookupOwner = useMemo(() => {
		const collectionId = owningCollectionId || targetCollectionId || null;
		return {
			scope: isNewRecord ? (targetCollectionId ? 'collection' : 'global') : scope,
			collection: (owningCollectionId ? owningCollection : targetCollection) || null,
			collectionId,
			// A global card looks up only itself (KPR-237 G-C2).
			ownerCardId: currentCardId || null,
		};
	}, [isNewRecord, scope, owningCollectionId, targetCollectionId, owningCollection, targetCollection, currentCardId]);
	// The currency a new currency element starts in (ruled L3,
	// utils/newMoneyCurrency.js): the collection's, else the author's own,
	// else US dollars. A global card has no collection.
	const prefs = useOptionalPreferences()?.prefs || null;
	const newMoneyCode = useMemo(
		() => newMoneyCurrency({ collection: lookupOwner.scope === 'collection' ? lookupOwner.collection : null, prefs }),
		[lookupOwner.scope, lookupOwner.collection, prefs]
	);
	const lookupScopeLoading = allCardsLoading
		|| (!!lookupOwner.collectionId && (owningCollectionId ? owningCollection : targetCollection) === undefined);
	// The scope, plus whatever the SAVED card already points at: a target that
	// predates the rule stays readable (flagged, and offered only to the
	// element that holds it) instead of collapsing to a bare id.
	// GLOBAL collections' cards (plan §4.5): a collection card may look up a
	// card any published global collection owns. The followed ones are
	// offered in their own group; any other is found by search, below the
	// picker. A target saved into a collection that has since retired stays
	// shown, flagged, like any out-of-scope target.
	const savedTargetIds = useMemo(() => lookupTargetIds(savedCard?.elements), [savedCard]);
	// LINKED collections' cards (keepr-api § Lookups across a link): a
	// collection card may look up a card another collection shares with this
	// one through a live link. Offered in their own "Linked" group.
	const linkedTargets = useLinkedSources(lookupOwner.collectionId, {
		enabled: lookupOwner.scope === 'collection' && Boolean(lookupOwner.collectionId),
	});
	// Each lent card reads as the qualified card badge: the lender, then the card.
	const linkedGroup = useMemo(() => linkedLookupGroup(linkedTargets.borrowedCards, { labelFor: linkedCardLabel }), [linkedTargets.borrowedCards]);
	const linkedTargetIds = useMemo(() => new Set((linkedGroup?.options || []).map(opt => opt.value)), [linkedGroup]);
	const knownCardIds = useMemo(
		() => new Set([...allCards.map(c => normalizeId(c._id)), ...linkedTargetIds]),
		[allCards, linkedTargetIds]
	);
	const globalTargets = useGlobalLookupTargets({
		enabled: lookupOwner.scope === 'collection' && Boolean(lookupOwner.collectionId),
		excludeCollectionId: lookupOwner.collectionId,
		keepIds: savedTargetIds,
		knownIds: knownCardIds,
	});
	const lookupCardOptions = useMemo(() => {
		const inScope = lookupScopeCardIds(lookupOwner, allCards);
		const local = buildCardHierarchyOptions(lookupScopeCards(lookupOwner, allCards, savedTargetIds))
			// A linked card is offered (in scope) under "Linked", not flagged here.
			.filter(opt => !linkedTargetIds.has(opt.value))
			.map(opt => (inScope.has(opt.value) ? opt : { ...opt, outOfScope: true }));
		const localIds = new Set(local.map(opt => opt.value));
		const foreign = globalTargets.cards
			.filter(card => !localIds.has(normalizeId(card._id)) && !linkedTargetIds.has(normalizeId(card._id)))
			.map(card => ({
				value: normalizeId(card._id),
				// A global collection's card, qualified by it.
				label: (
					<CardOptionLabel
						card={card}
						// Its listing title when there is one, else the id alone:
						// never a placeholder standing in for a name.
						collection={card.globalCollection?.id
							? (card.globalCollection.title ? { _id: card.globalCollection.id, name: card.globalCollection.title } : card.globalCollection.id)
							: null}
					/>
				),
				searchText: `${card.name} · ${card.globalCollection?.title || 'another collection'}`,
				published: globalTargets.publishedIds.has(card.globalCollection?.id),
			}));
		// A saved target whose collection is no longer published stays in the
		// flat list, flagged, like any other out-of-scope target.
		const stale = foreign.filter(opt => !opt.published).map(({ published, ...opt }) => ({ ...opt, outOfScope: true }));
		const global = foreign.filter(opt => opt.published).map(({ published, ...opt }) => opt);
		const flat = [...local, ...stale];
		return [
			...flat,
			...(linkedGroup ? [linkedGroup] : []),
			...(global.length ? [{ label: 'Global collections', options: global }] : []),
		];
	}, [lookupOwner, allCards, savedTargetIds, globalTargets.cards, globalTargets.publishedIds, linkedGroup, linkedTargetIds]);

	// A repeating group's lookup COLUMNS look up only the cards the card's own
	// collection owns — not a global card, not another collection's, not a
	// linked one (keepr-api KPR-675, `lookup_out_of_scope`) — so a column's
	// picker is the scope list cut down to those. A GLOBAL card's group may
	// sit there (KPR-692: the only-grows gate reads its columns and limits) but
	// has no lookup column, which its column pickers do not offer.
	const globalOwner = lookupOwner.scope === 'global';
	// ...and only while the server's REPEATING_GROUPS switch is ON. The owner's
	// ruling: the type is never offered, and nothing said about it, while the
	// switch is off. The contract says so (`features.repeatingGroups`, asked
	// once when the editor loads); anything but `true` — off, an API from
	// before the flag, no answer yet — offers nothing, because an unknown
	// answer cannot promise the save will be taken. A group the card ALREADY
	// stores keeps its type on its own row (CardDefElement).
	const { features: serverFeatures } = useServerFeatures();
	const switchOn = serverFeatures.repeatingGroups === true;
	const groupsOffered = switchOn;
	const columnCardOptions = useMemo(() => {
		const ownerId = lookupOwner.collectionId;
		if (!ownerId) return [];
		const own = new Set((allCards || [])
			.filter(card => getCardScope(card) === 'collection' && normalizeId(card?.collection_id) === normalizeId(ownerId))
			.map(card => normalizeId(card?._id)));
		return lookupCardOptions.filter(opt => !opt.options && own.has(opt.value));
	}, [lookupCardOptions, allCards, lookupOwner.collectionId]);
	const cardTypesWithheld = useMemo(() => (groupsOffered ? NO_KEYS : [REPEATING_GROUP]), [groupsOffered]);
	const columnTypesWithheld = globalOwner ? GLOBAL_COLUMN_TYPES_EXCLUDED : NO_KEYS;

	// The two usage facts the cards page shows on a tile — who owns the card,
	// or how many collections use it — for a saved card, from the same
	// `include=usage` annotation, so the panel and the tile can never disagree.
	// Read-only and silent on failure: the scope text stands alone without it.
	const [usage, setUsage] = useState(null);
	useEffect(() => {
		setUsage(null);
		if (isNewRecord || !cardId) return undefined;
		let isMounted = true;
		api.get(`${API_CARD_DEFS_URL}/${cardId}`, { params: { include: 'usage' } })
			.then(({ data }) => { if (isMounted) setUsage(data?.usage || null); })
			.catch(() => { /* a nicety; the form works without it */ });
		return () => { isMounted = false; };
	}, [isNewRecord, cardId]);
	// A GLOBAL card in use only grows (keepr-api KPR-280, ruling G14): the
	// API's `usage.inUse`, which only an admin is sent. Null when the card is
	// not in use or the API did not say — the form then behaves as before.
	const usedByWords = getCardScope(savedCard) === 'global' ? inUseWords(usage?.inUse) : null;
	// A PUBLISHED collection's card that other collections look up only grows
	// too (keepr-api KPR-281, ruling R2/G15): the API's `usage.lookedUp`,
	// which only a manager of the collection (or an admin) is sent. The
	// clause says why THIS card is held — looked up itself, extended by a card
	// that is, or extending one. Null otherwise: the form behaves as before.
	const lookedUpWords = getCardScope(savedCard) === 'collection' ? lookedUpClause(usage?.lookedUp) : null;
	const onlyGrows = Boolean(usedByWords || lookedUpWords);
	// A card in use that holds a repeating group says what the rule means for
	// its columns and limits too (keepr-api KPR-692).
	const holdsGroup = (savedCard?.elements || []).some(isRepeatingGroup);

	// G9's door (docs/plans/data-types-file-element.md §15.5): a file element
	// on a card used in a collection whose attachments are off says so and
	// links to that collection's setting — no second switch. A collection
	// card asks its own collection; a global card the collections its usage
	// names (their documents carry the switch; asked once, only while the
	// card has a file element). Silent on failure, as the usage fact is.
	// A repeating group's file column counts (KPR-934): it needs the switch too.
	const hasFileElement = (card?.elements || []).some(el => isFileElement(el) || (isRepeatingGroup(el) && columnsOf(el).some(isFileElement)));
	const usageCollectionIds = useMemo(
		() => (Array.isArray(usage?.collections) ? usage.collections.map(c => normalizeId(c?._id)).filter(Boolean) : []),
		[usage]
	);
	const ownCollectionDoc = owningCollectionId ? owningCollection : targetCollection;
	// The documents of the collections that use this card, from the shared
	// list — a nicety (the editor works without it): nothing held yet, or no
	// provider, reads as no docs.
	const sharedList = useCollections();
	const usageCollectionDocs = useMemo(() => {
		if (!hasFileElement || ownCollectionDoc || owningCollectionId || targetCollectionId || !usageCollectionIds.length) return null;
		if (!sharedList?.available) return null;
		return sharedList.collections.filter(c => usageCollectionIds.includes(normalizeId(c?._id)));
	}, [hasFileElement, ownCollectionDoc, owningCollectionId, targetCollectionId, usageCollectionIds, sharedList]);
	const filesOffCollections = useMemo(() => {
		if (!hasFileElement) return NO_COLLECTIONS;
		const docs = ownCollectionDoc ? [ownCollectionDoc] : (usageCollectionDocs || []);
		const off = docs
			.filter(c => c && typeof c === 'object' && c.allowAttachments !== true)
			.map(c => ({ _id: normalizeId(c._id), name: c.name || '(untitled collection)' }));
		return off.length ? off : NO_COLLECTIONS;
	}, [hasFileElement, ownCollectionDoc, usageCollectionDocs]);

	const readOnly = !isNewRecord && (
		(scope === 'global' && (!admin || inShell))
		|| (scope === 'collection' && !admin && owningCollection !== undefined && !canManage(owningCollection))
	);
	// Nothing on a blocked-create or read-only card is editable — every input
	// funnels through handleChange, so gating it (plus the fieldset below for
	// native controls) keeps antd's non-native pickers from dirtying state the
	// page can never save.
	const editingLocked = readOnly || createBlocked || awaitingCard;
	editingLockedRef.current = editingLocked;

	// The lock can engage a beat AFTER first render (the owning-collection
	// fetch resolves against the viewer). Anything typed in that window can
	// never be saved — drop the dirty flag so the unsaved-changes guard
	// doesn't nag about it forever.
	useEffect(() => {
		if (editingLocked) markPristine();
	}, [editingLocked, markPristine]);

	// ---- The page's tabs (docs/plans/card-editor-dates-groups-filters.md
	// Part 3) ----------------------------------------------------------------
	//
	// `/cards/:id` is the card's read-only HOME; `?tab=` opens one editing
	// domain over the same draft, with ONE Save for all of them. A new card
	// has no home to show yet, so it opens on Basics with the Overview tab
	// disabled until the first save. A read-only viewer gets the home alone —
	// no editing tabs, and so no "View only" alert to explain a form that is
	// not on screen.
	const canEditCard = !editingLocked;
	const tabItems = [
		{ key: CARD_TAB.overview, label: CARD_TAB_LABELS.overview, disabled: isNewRecord },
		...(canEditCard ? [
			{ key: CARD_TAB.basics, label: CARD_TAB_LABELS.basics },
			{ key: CARD_TAB.elements, label: CARD_TAB_LABELS.elements },
			{ key: CARD_TAB.display, label: CARD_TAB_LABELS.display },
		] : []),
		...((canEditCard && !isNewRecord && currentCardId) ? [
			{ key: CARD_TAB.danger, label: CARD_TAB_LABELS.danger },
		] : []),
	];
	const [activeTab, setTab] = useCardTab(
		tabItems.filter(tab => !tab.disabled).map(tab => tab.key),
		isNewRecord ? CARD_TAB.basics : CARD_TAB.overview
	);
	// The tile layout lives in its own collection with its own tiers, so it
	// is edited outside this form's draft and saves on its own.
	const [tileLayoutOpen, setTileLayoutOpen] = useState(false);
	const [formLayoutOpen, setFormLayoutOpen] = useState(false);
	const onOverview = activeTab === CARD_TAB.overview;

	// Which tabs hold unsaved edits — the dot on the tab's label. Measured
	// against the card as it was last loaded or saved (a new one against the
	// empty card it was born as), and only while the form is dirty at all.
	const pristineCard = useMemo(
		() => savedCard || mapCardFromApi({ ...createEmptyCard(), parentCardId: presetParentCardId }),
		[savedCard, mapCardFromApi, presetParentCardId]
	);
	const dirtyTabs = useMemo(() => {
		const dirty = new Set();
		if (!isDirty) return dirty;
		const changed = (...paths) => paths.some(path => !_.isEqual(_.get(card, path), _.get(pristineCard, path)));
		if (changed('name', 'description', 'color', 'icon', 'parentCardId')) dirty.add(CARD_TAB.basics);
		if (changed('elements', 'elementSets', 'options.allowComments', 'options.strictCommentAuditing', 'options.commentsExpanded', 'options.primaryDate')) dirty.add(CARD_TAB.elements);
		if (changed('displayTemplate', 'options.relatedLists')) dirty.add(CARD_TAB.display);
		return dirty;
	}, [isDirty, card, pristineCard]);

	// Recently viewed records what someone OPENED — the home, never an
	// editing tab — and only once the card itself has landed.
	const viewedRef = useRef(null);
	useEffect(() => {
		if (!onOverview || isNewRecord || !cardId) return;
		if (normalizeId(savedCard?._id) !== normalizeId(cardId)) return;
		if (viewedRef.current === cardId) return;
		viewedRef.current = cardId;
		recordView('card', cardId);
	}, [onOverview, isNewRecord, cardId, savedCard]);

	// A saved numeric element switched to measurement (or back) travels with
	// its conversion instruction — `options.convertFrom` / `convertTo`
	// { unit } — resolved from the editor's pick or the default unit; any
	// stale instruction is stripped. `missingConversions` names the elements
	// whose unit is still unchosen: the save is blocked until it is.
	const conversionPlan = useMemo(
		() => withConversionInstructions(card?.elements, savedElements),
		[card?.elements, savedElements]
	);
	const missingConversions = conversionPlan.missing;

	// "Allow multiple" switched on a SAVED element of a list type — a Choice,
	// a date, a date and time, an item lookup, a user (owner ruling B16): ask the
	// change-preview — with the saved card and only this element changed, so
	// no other unsaved edit answers instead — whether its items hold values in
	// the old shape. `{ refusal }` opens the element's dialog; null lets the
	// switch move (no values, or the question could not be asked: the save's
	// own 409 then raises the same dialog).
	const checkShapeChange = useCallback(async (name, nextElement) => {
		if (isNewRecord || !cardId || !Array.isArray(savedCard?.elements)) return null;
		if (!savedCard.elements.some(el => el?.name === name)) return null;
		const elements = savedCard.elements.map(el => (el?.name === name ? nextElement : el));
		try {
			const preview = await previewCardChange(cardId, { elements: withConversionInstructions(elements, savedElements).elements });
			const refusal = preview?.wouldApply === false ? shapeRefusalFor(preview.refusal, name) : null;
			return refusal ? { refusal } : null;
		} catch (error) {
			return null;
		}
	}, [isNewRecord, cardId, savedCard, savedElements]);

	// Elements still waiting for a data type. A new element is born without
	// one (docs/plans/card-editor-dates-groups-filters.md Part 1): every
	// option a row offers depends on the type, so there is nothing to
	// configure and nothing to save until it is chosen. The API refuses the
	// same payload with 400 `invalid_element_type`; this says so first, on
	// the row, without the round trip.
	const untypedElements = useMemo(() => (card?.elements || [])
		.map((element, index) => ({ element, index }))
		.filter(({ element }) => !element?.dataType), [card?.elements]);

	// A default that no longer fits its element (a removed choice, a tightened
	// limit, bound or pattern), or one beside a driven, unique or numbered
	// element: the API answers 400 invalid_options { option: 'defaultValue' }
	// (keepr-api utils/elementDefaults.js). Asked only where the default or the
	// rules it is judged by moved since the save — the API's echo, so a stored
	// default that predates a rule never blocks an unrelated edit — and said on
	// the element's Default value field, without the round trip.
	const defaultProblems = useMemo(() => {
		const list = card?.elements || [];
		const names = effectiveElementNames(list);
		return list
			.map((element, index) => ({ element, index, name: names[index] }))
			.filter(({ element, name }) => element?.dataType && !unchangedDefault(element, savedElements ? savedElements[name] : null))
			.map(entry => ({ ...entry, message: storedDefaultProblem(entry.element) }))
			.filter(entry => entry.message);
	}, [card?.elements, savedElements]);

	// The server sets ownership from these on create and ignores them on
	// update (system fields are stripped).
	const savePayload = useMemo(() => {
		// `notesExpanded` is the old spelling of `commentsExpanded` (KPR-305):
		// a card's own stored value is carried over under the new key, and
		// the old one is never sent again.
		const { notesExpanded: _legacyExpanded, ...ownOptions } = card?.options || {};
		const ownExpanded = cardCommentSetting(card, 'commentsExpanded');
		const withOptions = {
			...card,
			options: {
				...ownOptions,
				...(ownExpanded !== undefined ? { commentsExpanded: ownExpanded } : {}),
				// A new list goes with a blank id, which the API replaces.
				...(Array.isArray(card?.options?.relatedLists) ? { relatedLists: forSave(card.options.relatedLists) } : {}),
				primaryDate: primaryDateToken(primaryDate),
			},
		};
		// A new card's elements have no saved copy, so none sends a choice list
		// unless it is a Choice (withInertChoicesAsStored, KPE-033); a saved
		// card's go through the conversion plan, which applies the same rule.
		const base = isNewRecord
			? { ...withOptions, ...(Array.isArray(card?.elements) ? { elements: withInertChoicesAsStored(card.elements, {}, effectiveElementNames(card.elements)) } : {}) }
			: { ...withOptions, elements: conversionPlan.elements };
		if (!isNewRecord) return base;
		if (targetCollectionId) return { ...base, collection_id: targetCollectionId };
		if (admin) return { ...base, scope: 'global' };
		return base;
	}, [card, primaryDate, conversionPlan, isNewRecord, targetCollectionId, admin]);

	// ---- Header actions: Cancel · Save · ⋯ (the platform action grammar) ----

	const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | failed
	// The API's coded refusal from the last failed save ({ code, element,
	// message }), for the element editors that can show it inline — a
	// `measure_immutable` lands on the measurement element it names.
	const [saveError, setSaveError] = useState(null);
	// The open "Someone else changed this card" dialog (a 409 stale_card):
	// { theirs, merged, overlap }.
	const [staleCard, setStaleCard] = useState(null);
	// A save (or its preview) refused with 409 values_need_conversion — "Allow
	// multiple" turned on or off, or a type change into or out of a Choice that
	// allows multiple, over stored values (owner ruling B16): the element's
	// dialog, raised here when the switch-time check did not run (a type
	// change, a check that could not be asked). Its answer re-runs the save.
	const [shapeRefusal, setShapeRefusal] = useState(null);
	const [resavePending, setResavePending] = useState(false);

	/**
	 * A refused save, recorded and MADE FINDABLE.
	 *
	 * Save is one action across every tab, but a refusal that names an
	 * element is read on that element's row — which only the Elements tab
	 * renders. Pressed from Basics or Display (or by ⌘↵ from the home), the
	 * refusal would otherwise be a toast that fades over a form showing
	 * nothing wrong. So a coded refusal carrying an element name or index
	 * takes the page to the tab that holds it.
	 */
	const failSave = useCallback((coded) => {
		setSaveStatus('failed');
		setSaveError(coded);
		const namesElement = typeof coded?.element === 'string' || typeof coded?.index === 'number';
		// A related list's refusal is read on the list, which the Display tab holds.
		if (isRelatedListsRefusal(coded)) setTab(CARD_TAB.display);
		// A set-level refusal (a conflict, a redundant or out-of-scope set) is
		// read on the Element sets section, which the same tab holds — and a
		// refused primary date on its row there.
		else if (namesElement || SET_ERROR_CODES.includes(coded?.code) || isPrimaryDateRefusal(coded)) setTab(CARD_TAB.elements);
		// A title token that names no element is read where the title is set.
		else if (coded?.code === 'invalid_template') setTab(CARD_TAB.display);
		// The worklist's filter (`filter`, a card-level text, "Outstanding
		// means") is edited on the Elements tab, with the card-level switches.
		else if (coded?.filterField === 'filter') setTab(CARD_TAB.elements);
	}, [setTab]);

	// AN ELEMENT A FILTER COULD MEAN SEVERAL OF (400 `ambiguous_element`,
	// element ids step 3) — in a lookup's picker filter, a driven total's, or
	// the worklist's: the person picks one, or any of these cards, and the
	// card is saved again with the pick (`elementChoices`). The refusal's
	// `element` is the name the TEXT holds; the row it sits on is the
	// element its `field` names (`elements.<name>.options.filter`), and that
	// is the row the form opens — never one that happens to share the name.
	// A pick answers exactly the filter texts it was made for: any change to
	// a lookup's, a driven total's or the worklist's filter forgets it.
	const filterTextsKey = useMemo(() => JSON.stringify([
		card?.options?.worklist?.filter || '',
		(card?.elements || []).map(el => [el?.name || '', el?.options?.filter || '', el?.options?.drivenFrom?.where?.filter || '']),
	]), [card?.options?.worklist?.filter, card?.elements]);
	const filterPicks = useElementChoices(filterTextsKey);
	// ONE save carries one `elementChoices` map, keyed by the name alone, so
	// two filters naming the same ambiguous name would take turns being
	// refused (the P4 review's R-CD2). Each pick is ALSO written into its own
	// filter's text in what is sent (`withFieldPicks` — writeElementPick, the
	// text the server writes for it), keyed by the refusal's `field`; the
	// draft itself is untouched. Forgotten with the texts they answered.
	const fieldPicksRef = useRef({});
	useEffect(() => { fieldPicksRef.current = {}; }, [filterTextsKey]);
	// A group copied in the draft (KPR-859) rides as `copyGroups`, its marks
	// off the elements (utils/groupCopy.js); the previews' API reads past it.
	const sendable = useCallback(payload => filterPicks.withChoices(withFieldPicks(withGroupCopies(payload), fieldPicksRef.current)), [filterPicks]);
	const labelOfElementName = useCallback(name => (card?.elements || []).find(el => el?.name === name)?.label?.singular || name, [card?.elements]);
	const failFilter = useCallback((body) => {
		const field = typeof body?.field === 'string' ? body.field : '';
		failSave({
			code: body?.code || null,
			element: ownerElementOfField(field),
			filterField: field,
			filterElement: typeof body?.element === 'string' ? body.element : null,
			message: body?.message || null,
		});
		// "Outstanding means" itself, on the Elements tab, once it is drawn.
		if (field === 'filter') {
			setTimeout(() => {
				const input = typeof document !== 'undefined' ? document.getElementById('worklistFilter') : null;
				if (input && typeof input.scrollIntoView === 'function') input.scrollIntoView({ block: 'center' });
			}, 0);
		}
	}, [failSave]);

	// Where Cancel (and a delete) lands: the Card Library this card is
	// administered from when there is one, else the global card list.
	const libraryCollectionId = shellCollectionId || owningCollectionId || targetCollectionId || visitedCollectionId;
	const backPath = libraryCollectionId ? collectionCardsPath(libraryCollectionId) : '/cards';

	const saveCard = useCallback(async () => {
		// A blank or half-loaded card is never saveable: Save PATCHes the whole card.
		if (awaitingCard || (!isNewRecord && !savedCard)) return;
		if (!isDirty || editingLocked || saveStatus === 'saving') return;
		// A related list with no name or no source card cannot be saved, and the
		// API would say so by position: say it the same way, without the round
		// trip. Lists the save does not change are not judged (the API's rule).
		const listProblem = firstListProblem(draftRelatedLists, {
			inheritedNames: inheritedRelatedLists.map(entry => entry.name),
			saved: savedRelatedLists,
		});
		if (listProblem) {
			failSave(listProblem);
			message.error(`The card was not saved: ${listProblem.message}`);
			return;
		}
		if (untypedElements.length) {
			const first = untypedElements[0];
			const named = (first.element?.label?.singular || '').trim();
			failSave({
				code: 'data_type_required',
				index: first.index,
				element: first.element?.name || null,
				message: 'Choose a data type',
			});
			message.error(`The card was not saved: ${named ? `"${named}"` : `element ${first.index + 1}`} needs a data type.`);
			return;
		}
		// The same for a COLUMN of a repeating group, and for a group with none:
		// the API refuses both (400 invalid_element_type with the column,
		// invalid_options on `columns`); the sentence lands on the row.
		const untypedColumn = firstUntypedColumn(card?.elements);
		if (untypedColumn) {
			failSave({
				code: 'data_type_required',
				element: untypedColumn.element,
				groupIndex: untypedColumn.groupIndex,
				column: untypedColumn.column,
				columnIndex: untypedColumn.columnIndex,
				message: 'Choose a data type',
			});
			message.error(`The card was not saved: the column ${untypedColumn.columnLabel ? `"${untypedColumn.columnLabel}"` : `${untypedColumn.columnIndex + 1}`} of ${untypedColumn.groupLabel ? `"${untypedColumn.groupLabel}"` : 'a repeating group'} needs a data type.`);
			return;
		}
		const emptyGroup = firstEmptyGroup(card?.elements);
		if (emptyGroup) {
			failSave({
				code: 'invalid_options',
				element: emptyGroup.element,
				groupIndex: emptyGroup.groupIndex,
				option: 'columns',
				message: `Add at least one column to ${emptyGroup.groupLabel ? `"${emptyGroup.groupLabel}"` : 'the repeating group'}: each entry needs something to record.`,
			});
			message.error(`The card was not saved: ${emptyGroup.groupLabel ? `"${emptyGroup.groupLabel}"` : 'a repeating group'} has no columns.`);
			return;
		}
		if (missingConversions.length) {
			// The API would answer 400 conversion_unit_required; say so here,
			// inline on the element, without the round trip.
			const first = missingConversions[0];
			if (first.kind === 'currency') {
				// No currencies chosen yet, so there is nothing to pick the
				// existing numbers' currency from: the API would answer 400
				// invalid_options for the list — say that.
				failSave({ code: 'invalid_options', element: first.element, message: `Choose the currencies "${first.element}" allows before saving.` });
				message.error(`The card was not saved: "${first.element}" needs at least one currency.`);
				return;
			}
			if (!first.measure) {
				// No measure yet, so no unit control to point at: the API would
				// answer 400 invalid_options for the measure — say that.
				failSave({ code: 'invalid_options', element: first.element, message: `Choose what "${first.element}" measures before saving.` });
				message.error(`The card was not saved: "${first.element}" needs a measure.`);
				return;
			}
			failSave({
				code: 'conversion_unit_required',
				element: first.element,
				message: first.key === 'convertFrom'
					? `Choose the unit the existing "${first.element}" values are in before saving.`
					: `Choose the unit to convert the stored "${first.element}" values to before saving.`,
			});
			message.error(`The card was not saved: "${first.element}" needs a conversion unit.`);
			return;
		}
		if (defaultProblems.length) {
			const first = defaultProblems[0];
			failSave({ code: 'invalid_options', element: first.name, option: 'defaultValue', message: first.message });
			message.error(`The card was not saved: the default value of "${first.element?.label?.singular || first.name}" does not fit it.`);
			return;
		}
		// A save that removes or retypes an element, detaches an element set,
		// or moves the card under a parent that does not carry an inherited
		// element, leaves what items hold under those names where it is —
		// hidden for a removal, unconverted for a retype — and the server's
		// change-preview is the only party that can count the items (delete
		// audit F9). Ask it first; when anything holds a value, say so in a
		// danger confirm (hiddenValuesConfirm.js). A change the preview
		// refuses is the save's refusal.
		if (!isNewRecord) {
			const { removed, retyped, columns: columnEdits } = destructiveElementEdits(savedCard?.elements, savePayload?.elements);
			const draftSetIdSet = new Set(elementSetIdsOf(card));
			const detached = elementSetIdsOf(savedCard).filter(id => !draftSetIdSet.has(id));
			const reparented = savePayload && Object.prototype.hasOwnProperty.call(savePayload, 'parentCardId')
				&& (normalizeId(savePayload.parentCardId) || '') !== (normalizeId(savedCard?.parentCardId) || '');
			if (removed.length || retyped.length || columnEdits.length || detached.length || reparented) {
				// A removed column is no longer in the draft: its label is the saved
				// copy's.
				const columnLabelOf = (group, column) => (
					columnsOf(savedElements[group]).find(col => col?.name === column)?.label?.singular || column
				);
				const labelOf = (name) => {
					const own = savedElements[name]?.label?.singular;
					if (own) return own;
					for (const id of detached) {
						const hit = setElementsOf(cachedElementSetsById[id]).find(el => el?.name === name);
						if (hit?.label?.singular) return hit.label.singular;
					}
					return name;
				};
				const { proceed, refusal } = await checkStoredValues({
					modal,
					request: () => previewCardChange(cardId, sendable(savePayload)),
					labelOf,
					columnLabelOf,
				});
				if (refusal && filterPicks.take(refusal.details || refusal)) {
					failFilter(refusal.details || refusal);
					message.error(`The card was not saved: ${elementRefusalText(elementRefusalOf(refusal.details || refusal)) || refusalSentence(refusal.details || refusal)}`);
					return;
				}
				if (refusal) {
					failSave({ ...refusal, ...relatedListRefusalFields(refusal.details) });
					// A type change into or out of a list type that allows
					// multiple, over stored values: the dialog asks (B16) — and
					// so does an element made an item lookup over values items
					// hold, whose answer is `clear` (KPR-1136).
					const shape = shapeRefusalFor(refusal.details, refusal.element);
					if (shape) {
						setShapeRefusal(shape);
						return;
					}
					message.error(`The card was not saved: ${refusal.message}`);
					return;
				}
				if (!proceed) return;
			}
		}
		// A save that turns an element driven (or re-points a driven one)
		// makes the API recompute EVERY existing item the moment the
		// definition lands — whatever people typed into that element is
		// gone. Ask first, with the numbers and a few real rows, and a door to
		// the full list; a config the save would refuse is refused here, inline.
		if (!isNewRecord) {
			const changed = changedDrivenElements(savePayload?.elements, savedElements);
			if (changed.length) {
				let previews;
				try {
					previews = await Promise.all(changed.map(async ({ element }) => ({
						element,
						preview: await previewDrivenChange(cardId, { elementName: element.name, elements: sendable(savePayload).elements, elementChoices: filterPicks.choices() }),
					})));
				} catch (err) {
					const refusal = err?.response?.data;
					if (filterPicks.take(err)) {
						failFilter(refusal);
						message.error(`The card was not saved: ${elementRefusalText(elementRefusalOf(refusal)) || refusalSentence(refusal)}`);
						return;
					}
					failSave({ code: refusal?.code || 'driven_preview', element: refusal?.element || null, message: refusal?.message || 'Unable to check the existing values right now.' });
					message.error(refusal?.message ? `The card was not saved: ${refusal.message}` : 'The card was not saved: unable to check the existing values.');
					return;
				}
				const overwriting = previews.filter(entry => (entry.preview?.withValues || 0) > 0);
				if (overwriting.length) {
					const confirmed = await new Promise(resolve => {
						confirmDestructive(modal, {
							title: 'Replace existing values?',
							width: 680,
							okText: 'Save and replace',
							cancelText: 'Keep editing',
							content: <DrivenBackfillWarning previews={overwriting} collectionId={owningCollectionId} cardId={cardId} />,
							onOk: () => resolve(true),
							onCancel: () => resolve(false),
						});
					});
					if (!confirmed) return;
				}
			}
		}
		setSaveStatus('saving');
		setSaveError(null);
		try {
			// `ifUpdatedAt` is the card's `updatedAt` as this form last read
			// it (loaded, or the previous save's answer): a colleague's save
			// in between is a 409 `stale_card`, never a silent overwrite of
			// their elements (delete audit F6).
			// The person's picks for an element a filter could mean several of
			// ride with the card (`elementChoices`, never stored).
			const response = isNewRecord
				? await api.post(API_CARD_DEFS_URL, sendable(savePayload))
				: await api.patch(`${API_CARD_DEFS_URL}/${cardId}`, sendable(savedCard?.updatedAt ? { ...savePayload, ifUpdatedAt: savedCard.updatedAt } : savePayload));
			if (![200, 201].includes(response.status)) {
				throw new Error(`Unexpected status ${response.status}`);
			}
			filterPicks.reset();
			fieldPicksRef.current = {};
			message.success('Card saved.');
			// What a number <-> measurement conversion did to the stored item
			// values, in the API's words (the same sentence its history row
			// carries). A backfill that stopped part-way left the definition
			// saved; an administrator finishes it with the maintenance task.
			// `conversionWarning` may arrive with no summary at all (the
			// failure came before the first batch) — it is never dropped.
			const conversions = conversionsFromResponse(response?.data);
			conversions.forEach(conversion => {
				const text = describeConversion(conversion);
				// An element made an item lookup cleared its values (KPR-1136).
				if (conversion?.incomplete) {
					notification.warning({ message: conversion?.lookup ? 'Values partly cleared' : 'Values partly converted', description: text, duration: 0 });
				} else {
					notification.success({ message: conversion?.lookup ? 'Values cleared' : 'Values converted', description: text, duration: 8 });
				}
			});
			const conversionWarning = response?.data?.conversionWarning;
			if (conversionWarning && !conversions.some(conversion => conversion?.incomplete)) {
				notification.warning({ message: 'Values not converted', description: conversionWarning, duration: 0 });
			}
			// A calculated element whose config stopped resolving is retired
			// AFTER the write — the pre-write 400 only judges new and changed
			// configs — so the card saves and the element quietly keeps its
			// last value while still calling itself calculated. The API says
			// so in `automationsWarning`; nothing was reading it. duration: 0
			// because this one does not expire on its own: the card needs a
			// person to look at it.
			const automationsWarning = response?.data?.automationsWarning;
			if (automationsWarning) {
				notification.warning({ message: 'Calculated fields', description: automationsWarning, duration: 0 });
			}
			// A lookup a related list reads was removed, retyped or pointed
			// elsewhere by this save: the card saved, and each list that can no
			// longer find items says so (relatedListsAffected, keepr-api KPR-716).
			// What the API could not finish for a copied group (KPR-859): a
			// column rule, or a starter copy's record. The card saved; each line
			// says why, and stays until read.
			const rulesMissed = rulesNotCopiedLines(response?.data?.groupCopy);
			if (rulesMissed.length) {
				notification.warning({
					message: 'Not everything was copied',
					description: <ul className='mb-0'>{rulesMissed.map(line => <li key={line}>{line}</li>)}</ul>,
					duration: 0,
				});
			}
			const affectedLists = affectedSentences(response?.data?.relatedListsAffected);
			if (affectedLists.length) {
				notification.warning({
					message: 'Related lists affected',
					description: <ul className='mb-0'>{affectedLists.map(line => <li key={line}>{line}</li>)}</ul>,
					duration: 0,
				});
			}
			setSaveStatus('idle');
			const next = response?.data?.payload;
			const newId = response?.data?.insertedId || response?.data?.status?.insertedId || next?._id;
			if (next) {
				const mapped = mapCardFromApi(next);
				setCard(mapped);
				setSavedCard(mapped);
				markPristine(mapped);
				fetchAllCards();
			} else {
				// No echo of the stored document. The draft IS the card as
				// saved now, and the Overview reads `savedCard`: leaving it
				// behind would show the home the card as it was BEFORE this
				// save, with no dot anywhere to say so.
				setSavedCard(card);
				markPristine();
			}
			refreshCardDefinitions();
			// Leave the 'new' route after a create — staying on it would make
			// the next Save POST a duplicate card.
			if (isNewRecord && newId) {
				navigate(inShell ? collectionCardPath(shellCollectionId, normalizeId(newId)) : `/cards/${normalizeId(newId)}`, { replace: true });
			}
		} catch (error) {
			const data = error?.response?.data;
			// Somebody saved the card after this form read it. Nothing was
			// written; `card` is the card as it stands (or null: gone, or no
			// longer readable). Reload and reapply puts this draft's edits
			// over it — elements merged by name, so a colleague's new element
			// is not dropped by an edit to another (utils/reapply.js).
			if (error?.response?.status === 409 && data?.code === 'stale_card') {
				setSaveStatus('failed');
				if (!data.card) {
					message.error('This card was deleted, or you can no longer open it, so your changes were not saved.');
					return;
				}
				const theirs = mapCardFromApi(data.card);
				const { merged, overlap } = reapplyOver({
					base: savedCard || {},
					mine: card,
					theirs,
					ignore: CARD_NOT_REAPPLIED,
					lists: { elements: element => element?.name, elementSets: id => normalizeId(id) },
					objects: ['options'],
				});
				// `options` merges one key deep, so the lists would be mine whole:
				// a colleague's new list is kept, and one I did not touch takes theirs.
				const mineLists = card?.options?.relatedLists;
				const sharedLists = [];
				if (!_.isEqual(mineLists, savedCard?.options?.relatedLists) && Array.isArray(mineLists)) {
					merged.options = {
						...(merged.options || {}),
						relatedLists: mergeOwnLists(savedCard?.options?.relatedLists, mineLists, theirs?.options?.relatedLists),
					};
					// A list both of us changed keeps mine: said, by its name.
					sharedLists.push(...overlappingLists(savedCard?.options?.relatedLists, mineLists, theirs?.options?.relatedLists).map(name => `Related list “${name}”`));
				}
				setStaleCard({
					theirs,
					merged,
					overlap: [...new Set([...overlap.map(key => CARD_FIELD_LABELS[key] || key), ...sharedLists])],
				});
				return;
			}
			// Another save claimed an element id this one drew, at the same
			// moment (409 `element_id_conflict`, element ids step 3): nothing
			// was written, and the draft is all still here — save again.
			if (error?.response?.status === 409 && data?.code === 'element_id_conflict') {
				setSaveStatus('failed');
				message.error(ELEMENT_ID_CONFLICT_TEXT);
				return;
			}
			// An element a lookup's, a driven total's or the worklist's filter
			// could mean several of (or none, or too long once picked): the
			// pick at the top of the form, and the row that holds the filter.
			if (filterPicks.take(error)) {
				failFilter(data);
				message.error(`The card was not saved: ${elementRefusalText(elementRefusalOf(data)) || refusalSentence(data)}`);
				return;
			}
			// "Allow multiple" turned on or off over stored values, with no
			// answer on the element (owner ruling B16): the same dialog the
			// switch raises, then the save again. So too an element made an
			// item lookup over values items hold (KPR-1136: 409
			// type_change_needs_empty with `answer: 'clear'` — a lookup added
			// back over a removed element's values is first heard of here).
			const shapeEntry = shapeRefusalFor(data, data?.element);
			if (shapeEntry) {
				failSave({ code: data.code, element: data.element || null, message: data.message || null });
				setShapeRefusal(shapeEntry);
				return;
			}
			// `count`/`cap` ride along for the 409 backfill_too_large (a
			// conversion too big to run on save; nothing was written).
			const coded = data && typeof data.code === 'string'
				? {
					code: data.code,
					element: data.element || null,
					message: data.message || null,
					// `invalid_element_type` names the element by INDEX (the
					// payload's order) and the type it refused, because an
					// untyped element may not have a name yet.
					...(typeof data.index === 'number' ? { index: data.index } : {}),
					...(typeof data.dataType === 'string' ? { dataType: data.dataType } : {}),
					...(typeof data.count === 'number' ? { count: data.count } : {}),
					...(typeof data.cap === 'number' ? { cap: data.cap } : {}),
					// 409 duplicates_exist (Unique values turned on over values
					// two items already share): `items` in all, and `samples` —
					// the first values, each naming only the items this person
					// can read (keepr-api utils/uniqueValues.js).
					...(typeof data.items === 'number' ? { items: data.items } : {}),
					...(Array.isArray(data.samples) ? { samples: data.samples } : {}),
					// The element-set refusals name the set and what it collides
					// with (docs/plans/element-sets-and-me.md).
					...(data.setId ? { setId: normalizeId(data.setId) } : {}),
					...(data.conflictsWith ? { conflictsWith: data.conflictsWith } : {}),
					// An option the element's type does not take names the key
					// (keepr-api elementOptionKeys); a title token that names no
					// element names the token.
					...(typeof data.option === 'string' ? { option: data.option } : {}),
					...(typeof data.token === 'string' ? { token: data.token } : {}),
					// A refusal about a repeating group's COLUMN names it beside the
					// group (`element`), and the ceiling or the place a group was
					// refused for (keepr-api KPR-675).
					...(typeof data.column === 'string' ? { column: data.column } : {}),
					...(typeof data.ceiling === 'number' ? { ceiling: data.ceiling } : {}),
					...(typeof data.where === 'string' ? { where: data.where } : {}),
					// A refusal about a related list: which one and which field.
					...relatedListRefusalFields(data),
				}
				: null;
			failSave(coded);
			// A refusal that carries a sentence names what to fix ("elements.weight:
			// options.min must not exceed options.max.", "A card may only extend a
			// global card or a card owned by the same collection."), coded or not;
			// only a failure with no message is a shrug.
			const refusal = coded?.message
				|| (error?.response?.status === 400 && typeof data?.message === 'string' ? data.message : null);
			message.error(coded?.code === 'measure_immutable'
				? 'The card was not saved: a measurement\'s measure cannot change while items hold values for it.'
				: coded?.code === 'backfill_too_large'
					// Unique values turned on over too many items: the API's
					// sentence names the task that does it instead
					// (backfillUniqueClaims); a conversion keeps its own words.
					? (/backfillUniqueClaims|convertChoiceValues/.test(coded.message || '')
						? `The card was not saved: ${coded.message}`
						: `The card was not saved: too many items to convert on save${coded.element ? ` ("${coded.element}")` : ''}. An administrator can run the backfillMeasurement maintenance task.`)
					: (refusal
						? `The card was not saved: ${refusal}`
						: 'Something went wrong. The card may not have saved.'));
		}
	}, [
		isDirty, editingLocked, awaitingCard, saveStatus, isNewRecord, savePayload, card, cardId, message, notification, modal,
		draftRelatedLists, savedRelatedLists, inheritedRelatedLists,
		savedCard, savedElements, owningCollectionId, inShell, shellCollectionId, untypedElements, failSave, cachedElementSetsById,
		missingConversions, defaultProblems, mapCardFromApi, markPristine, fetchAllCards, refreshCardDefinitions, navigate,
		filterPicks, failFilter, sendable,
	]);

	// The save-time dialog's answers (owner ruling B16). Convert: the answer
	// goes on the element and the save runs again. Cancel: a switch the
	// author flipped goes back (its default with it); a type change stays in
	// the draft, the refusal on its element.
	const shapeIndex = useCallback(name => effectiveElementNames(card?.elements).indexOf(name), [card?.elements]);
	// One save may need two answers (the 10b contract): "Allow multiple" off
	// AND "Photos only" on sends `['first', 'detach']`. The API asks for what
	// is still unanswered; the answer joins the one the element carries.
	const convertShape = useCallback((conversion) => {
		const index = shapeIndex(shapeRefusal?.element);
		setShapeRefusal(null);
		if (index < 0) return;
		const element = card?.elements?.[index];
		const merged = mergeConversionAnswers(element?.options?.[SHAPE_INSTRUCTION_KEY], conversion, element, savedElements[shapeRefusal.element] || null);
		handleChange(`elements[${index}].options.${SHAPE_INSTRUCTION_KEY}`, merged === undefined ? conversion : merged);
		setResavePending(true);
	}, [shapeIndex, shapeRefusal, handleChange, card?.elements, savedElements]);
	// A save-time refusal that asks a file element's "Photos only" question
	// (PR 10b) rather than the flip's: the element against its saved copy
	// says which, when the API's entry does not.
	const shapeAsksDetach = useMemo(() => {
		if (!shapeRefusal) return false;
		const index = shapeIndex(shapeRefusal.element);
		const element = index >= 0 ? card?.elements?.[index] : null;
		return refusalAsksDetach(shapeRefusal, element, savedElements[shapeRefusal.element] || null);
	}, [shapeRefusal, shapeIndex, card?.elements, savedElements]);
	const cancelShape = useCallback(() => {
		const entry = shapeRefusal;
		setShapeRefusal(null);
		const index = shapeIndex(entry?.element);
		const element = index >= 0 ? card.elements[index] : null;
		// "Photos only" goes back to any file, as a flipped switch goes back.
		if (element && shapeAsksDetach) {
			handleChange(`elements[${index}].options.accept`, undefined);
			return;
		}
		// An own unit taken away (KPR-1109): the own units go back to the saved
		// element's, and with them the default value removing one pruned and the
		// min, max and display rules the last one's removal gave back (an element
		// with own units does not take them).
		if (element && refusalAsksOwnUnits(entry)) {
			const saved = savedElements[entry.element]?.options || {};
			const next = { ...(element.options || {}) };
			['ownUnits', 'defaultValue', 'min', 'max', 'displayRules'].forEach((key) => {
				if (saved[key] === undefined) delete next[key];
				else next[key] = saved[key];
			});
			handleChange(`elements[${index}].options`, next);
			return;
		}
		// A measure taken away (KPR-1085): the measures go back to the saved
		// element's, so nothing is cleared — and with them the allowlist and the
		// default value the switch or the picker pruned (a list of one measure's
		// units beside several measures is a save the API refuses).
		if (element && refusalAsksClear(entry)) {
			const saved = savedElements[entry.element]?.options || {};
			const next = { ...(element.options || {}) };
			['measures', 'units', 'defaultValue'].forEach((key) => {
				if (saved[key] === undefined) delete next[key];
				else next[key] = saved[key];
			});
			handleChange(`elements[${index}].options`, next);
			return;
		}
		if (!element || !SHAPE_LIST_TYPES.includes(element.dataType) || entry.from !== entry.to) return;
		const back = entry.direction === 'off';
		// A Choice stores false; the panels' types drop the key when off.
		handleChange(`elements[${index}].options.allowMultiple`, element.dataType === 'choice' ? back : (back || undefined));
		if (element.dataType === 'choice' && explicitElementDefault(element) !== undefined) {
			handleChange(`elements[${index}].options.defaultValue`, defaultForMultiToggle(element, back));
		}
	}, [shapeRefusal, shapeIndex, card?.elements, handleChange, shapeAsksDetach, savedElements]);
	useEffect(() => {
		if (!resavePending) return;
		setResavePending(false);
		saveCard();
	}, [resavePending, saveCard]);

	// "Copy into this card" (materialize): the set's elements become this
	// card's own, with the same names and the set's group stamped, and the set
	// is detached — a server-side act on the SAVED card, one change-log row,
	// values never moving. Offered only while the draft is clean, so what the
	// API rewrites is what the author is looking at.
	const materializeDisabledReason = isNewRecord || !currentCardId
		? 'Save the card first'
		: (isDirty ? 'Save your changes first' : null);
	const materializeSet = useCallback(async (set) => {
		const setId = normalizeId(set?._id);
		if (!currentCardId || !setId) return;
		try {
			const response = await api.post(API_CARD_ELEMENT_SET_MATERIALIZE_URL(currentCardId, setId));
			const next = response?.data?.payload;
			if (next) {
				const mapped = mapCardFromApi(next);
				setCard(mapped);
				setSavedCard(mapped);
				markPristine(mapped);
				fetchAllCards();
			}
			message.success(`${set?.name || 'The element set'} copied into this card.`);
			refreshCardDefinitions();
		} catch (error) {
			const data = error?.response?.data;
			failSave(data && typeof data.code === 'string' ? { code: data.code, setId, message: data.message || null } : null);
			message.error(data?.message ? `The element set was not copied: ${data.message}` : 'The element set was not copied.');
		}
	}, [currentCardId, mapCardFromApi, markPristine, fetchAllCards, message, refreshCardDefinitions, failSave]);

	// What "Add element set…" offers: everything this card may attach that
	// it does not already carry, itself or through an ancestor — the scope
	// rule verbatim from parents: a collection card takes global sets and its
	// own collection's, a global card global sets only.
	const availableSets = useMemo(() => {
		const carried = new Set([...draftSetIds, ...inheritedSets.map(entry => normalizeId(entry.set?._id))]);
		const collectionId = scope === 'global' ? null : (owningCollectionId || targetCollectionId || null);
		return attachableElementSets(cachedElementSets, { collectionId }).filter(set => !carried.has(normalizeId(set?._id)));
	}, [draftSetIds, inheritedSets, scope, owningCollectionId, targetCollectionId, cachedElementSets]);
	const elementSetRefusal = SET_ERROR_CODES.includes(saveError?.code) ? saveError : null;
	// A 400 invalid_options about options.primaryDate (an unresolvable name,
	// a token out of place, a list too long) is read on the Primary date row.
	const primaryDateRefusal = isPrimaryDateRefusal(saveError) ? saveError.message : null;

	// ---- The primary date OFFER (docs/plans/element-sets-and-me.md, "Card-level
	// options"): a set attached in THIS draft that declares
	// `options.suggestPrimaryDate` (To do: scheduled, then due), on a card
	// with no explicit choice of its own — absent, or the platform default
	// the editor writes out loud — is offered as the card's primary date. An
	// offer, never a silent change: "Use it" writes the draft, the card's
	// Save stores it. A set the saved card already carried is not re-offered
	// on every visit; the select still lists its dates.
	const savedSetIds = useMemo(() => new Set(elementSetIdsOf(savedCard)), [savedCard]);
	const primaryDateSuggestion = useMemo(() => {
		const own = cardPrimaryDateSetting(card);
		if (own !== undefined && own !== 'created') return null;
		for (const set of draftSets) {
			if (savedSetIds.has(normalizeId(set?._id))) continue;
			const raw = set?.options?.suggestPrimaryDate;
			const tokens = (Array.isArray(raw) ? raw : [raw]).filter(token => typeof token === 'string' && token.trim()).map(token => token.trim());
			if (!tokens.length) continue;
			// Only dates the draft actually resolves — the set's own, as stored.
			const dates = new Map(setElementsOf(set).filter(isDateElement).map(el => [el.name, el.label?.singular || el.name]));
			const usable = tokens.filter(token => dates.has(token));
			if (!usable.length) continue;
			if (_.isEqual(primaryDateValue, usable.length === 1 ? usable[0] : usable)) return null;
			// The set's own words ("Scheduled, then Due"); the line beneath names the set.
			return { set, tokens: usable, description: usable.map(token => dates.get(token)).join(', then ') };
		}
		return null;
	}, [card, draftSets, savedSetIds, primaryDateValue]);
	const usePrimaryDateSuggestion = useCallback((tokens) => {
		// Two or more entries are the ordered list; one is the plain string.
		handleChange('options.primaryDate', tokens.length > 1 ? tokens : tokens[0]);
	}, [handleChange]);

	// ---- "Make an element set from these elements" (extract): some of the
	// SAVED card's own elements become a new collection set the card attaches,
	// the stored values renamed to the set's names — a server-side act on the
	// saved card, so, like materialize, only while the draft is clean, and
	// only on a collection card (the set belongs to the collection).
	const [extractOpen, setExtractOpen] = useState(false);

	// ---- "Copy a group from…" (KPR-859, CopyGroupModal): a repeating group
	// another card of this collection defines, or one an installed starter
	// supplied, put in the DRAFT as a new element marked with its source — the
	// save sends the mark as `copyGroups` (utils/groupCopy.js), so nothing is
	// written until Save and Cancel discards it. Offered while groups are
	// switched on, on a collection's card, to someone who can edit it.
	const [copyGroupOpen, setCopyGroupOpen] = useState(false);
	const copyCollectionId = owningCollectionId || (isNewRecord && targetCollectionId ? normalizeId(targetCollectionId) : null);
	const copyGroupOffered = groupsOffered && !editingLocked && !!copyCollectionId;
	const copyNames = useMemo(() => (copyGroupOpen ? copyNamesInUse({
		elements: card?.elements || [],
		carried: [...mergeChainElements(resolvedParentChain), ...draftSetElements],
		descendants: (allCards || []).filter(c => descendantSet.has(normalizeId(c?._id))),
	}) : new Set()), [copyGroupOpen, card?.elements, resolvedParentChain, draftSetElements, allCards, descendantSet]);
	const openCopyGroup = useCallback(() => setCopyGroupOpen(true), []);
	const handleCopyGroup = useCallback((element) => {
		if (editingLockedRef.current) return;
		setCard(prevState => produce(prevState, draft => {
			draft.elements = [...(draft.elements || []), element];
		}));
		setCopyGroupOpen(false);
		message.success(`“${element?.label?.singular || element?.name || 'The group'}” is on this card. Save the card to keep it.`);
	}, [message]);
	const extractDisabledReason = isNewRecord || !currentCardId
		? 'Save the card first'
		: scope !== 'collection'
			? 'Only a collection card can make an element set'
			: isDirty
				? 'Save your changes first'
				: !(savedCard?.elements || []).length
					? 'This card has no elements of its own'
					: null;
	const handleExtracted = useCallback((data) => {
		const next = data?.payload;
		if (next) {
			const mapped = mapCardFromApi(next);
			setCard(mapped);
			setSavedCard(mapped);
			markPristine(mapped);
			fetchAllCards();
		}
		const setName = data?.elementSet?.name || 'The element set';
		const count = (data?.renamed || []).length;
		message.success(`${setName} created from ${formatCount(count, 'element')}${data?.moved ? `; ${formatCount(data.moved, 'stored value')} renamed` : ''}.`);
		refreshCardDefinitions();
	}, [mapCardFromApi, markPristine, fetchAllCards, message, refreshCardDefinitions]);

	// The deletion itself. The confirmation is the Danger zone tab's typed
	// word (CardDangerZone), the platform's one destructive grammar — the ⋯
	// menu's "Delete card…" is the door to that tab, never the act.
	const deleteCard = useCallback(async () => {
		if (!currentCardId) return;
		try {
			await api.delete(`${API_CARD_DEFS_URL}/${currentCardId}`);
			message.success('Card deleted.');
			markPristine();
			refreshCardDefinitions();
			navigate(backPath);
		} catch (error) {
			message.error(error?.response?.data?.message || 'Delete failed.');
		}
	}, [currentCardId, message, markPristine, refreshCardDefinitions, navigate, backPath]);

	// RETIRE / RETURN (KPR-280, ruling G14) — an admin's act on a global card,
	// immediate, not part of the Save. Only `retiredAt` and `updatedAt` move:
	// they go into the saved card AND the draft, so the draft's next save is
	// not taken for a stale one (`ifUpdatedAt`), and an untouched draft stays
	// untouched.
	const applyRetirement = useCallback((payload) => {
		if (!payload) return;
		const mapped = mapCardFromApi(payload);
		const next = { ...card, retiredAt: mapped.retiredAt, updatedAt: mapped.updatedAt };
		if (!next.retiredAt) delete next.retiredAt;
		setSavedCard(mapped);
		setCard(next);
		if (!isDirty) markPristine(next);
		refreshCardDefinitions();
	}, [card, mapCardFromApi, isDirty, markPristine, refreshCardDefinitions]);
	const retireCurrentCard = useCallback(async () => {
		if (!currentCardId) return;
		try {
			const data = await retireCard(currentCardId);
			applyRetirement(data?.payload);
			message.success('Card retired. It has left the Card Library; every collection that uses it keeps working.');
		} catch (error) {
			message.error(error?.response?.data?.message || 'The card was not retired.');
		}
	}, [currentCardId, applyRetirement, message]);
	const unretireCurrentCard = useCallback(async () => {
		if (!currentCardId) return;
		try {
			const data = await unretireCard(currentCardId);
			applyRetirement(data?.payload);
			message.success('The card is back in the Card Library.');
		} catch (error) {
			message.error(error?.response?.data?.message || 'The card was not returned to the Card Library.');
		}
	}, [currentCardId, applyRetirement, message]);

	// Cancel leaves the editing tabs for the home, putting the draft back to
	// the card as it was last saved. A tab switch is not leaving the page, so
	// the unsaved-changes guard would never ask — this asks. A card that has
	// never been saved has no home to return to: Cancel leaves the page, as it
	// always did, behind that same guard.
	// The stale-save dialog's two ways on. Reapply: the card as it stands
	// becomes the saved card (the base, and the conversion plan's source)
	// and the draft is this person's edits over it, left dirty to check and
	// save again. Discard: the card as it stands.
	const reapplyStaleCard = useCallback(() => {
		if (!staleCard) return;
		setSavedCard(staleCard.theirs);
		markPristine(staleCard.theirs);
		setCard(staleCard.merged);
		setSaveStatus('idle');
		setSaveError(null);
		setStaleCard(null);
	}, [staleCard, markPristine]);
	const discardStaleCard = useCallback(() => {
		if (!staleCard) return;
		setSavedCard(staleCard.theirs);
		setCard(staleCard.theirs);
		markPristine(staleCard.theirs);
		setSaveStatus('idle');
		setSaveError(null);
		setStaleCard(null);
	}, [staleCard, markPristine]);

	const handleCancel = useCallback(() => {
		if (isNewRecord || !savedCard) {
			// Leaving drops the unsaved card: the platform's one question first.
			guardDiscard(() => navigate(backPath));
			return;
		}
		guardDiscard(() => {
			setCard(savedCard);
			markPristine(savedCard);
			setSaveStatus('idle');
			setSaveError(null);
			setTab(CARD_TAB.overview);
		}, { content: 'The card goes back to how it was last saved.' });
	}, [isNewRecord, savedCard, navigate, backPath, markPristine, guardDiscard, setTab]);

	// The same map in ⋯ → Keyboard shortcuts, registered beside the handler.
	useScreenShortcuts('Card editor', [
		{ keys: ['mod', 'enter'], label: 'Save the card' },
		{ keys: ['esc'], label: 'Cancel' },
	]);

	// Form shortcuts: ⌘↵ save · Esc cancel — the same map as the item form.
	const shortcutRef = useRef(null);
	shortcutRef.current = { saveCard, handleCancel };
	useEffect(() => {
		const handler = (event) => {
			const current = shortcutRef.current;
			if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
				event.preventDefault();
				current.saveCard();
			} else if (event.key === 'Escape') {
				// Esc belongs to any open overlay first.
				if (escapeBelongsElsewhere(event)) return;
				event.preventDefault();
				current.handleCancel();
			}
		};
		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, []);

	// The card's ⋯ menu, in the taxonomy: navigate · share · destructive.
	const headerActions = [
		selectedParent ? {
			key: 'parent',
			group: 'navigate',
			glyph: 'card',
			label: 'Open parent card',
			onClick: () => navigate(cardPagePath(selectedParent)),
		} : null,
		owningCollection ? {
			key: 'collection',
			group: 'navigate',
			glyph: 'card',
			label: 'Collection cards',
			onClick: () => navigate(`/collections/${normalizeId(owningCollection._id)}/cards`),
		} : null,
		{
			key: 'cards',
			group: 'navigate',
			glyph: 'items',
			label: 'All cards',
			onClick: () => navigate('/cards'),
		},
		...savedCardHeaderActions({
			isNewRecord, readOnly, editingLocked, currentCardId, changeCardPath, importSheetPath: cardImportPath(tileCollection, tileCollectionId, currentCardId),
			onHistory: () => setHistoryOpen(true),
			onChangeCard: () => navigate(changeCardPath), onImport: path => navigate(path),
			onExport: () => setExportOpen(true),
			onDelete: () => setTab(CARD_TAB.danger),
		}),
	];

	// ---- Scope: what this card is, said once (kicker tag + side panel) ----
	const scopeTag = (() => {
		if (isNewRecord) {
			if (targetCollectionId) return <Tag color='purple'>Collection card</Tag>;
			return admin ? <Tag color='blue'>Global</Tag> : null;
		}
		if (scope === 'global') return isRetiredCard(savedCard) ? <><Tag color='blue'>Global</Tag><Tag>Retired</Tag></> : <Tag color='blue'>Global</Tag>;
		if (scope === 'collection') return <Tag color='purple'>Collection card</Tag>;
		if (scope === 'user') return <Tag color='gold'>Unassigned</Tag>;
		return null;
	})();

	const scopeText = (() => {
		if (createBlocked) return null;
		if (isNewRecord && targetCollectionId) {
			return targetCollection ? null : 'This card will belong to the collection you came from, and its sub-collections can use it.';
		}
		if (isNewRecord && admin) {
			return 'Creating a global card. Every keepr user will be able to use it.';
		}
		if (scope === 'global') {
			return admin
				? 'Provided by keepr for everyone. Edits here affect every user.'
				: "Provided by keepr and read-only. To customize it, extend it from a collection's Card Library.";
		}
		if (scope === 'user') return 'This card is not part of any collection yet, so items cannot use it.';
		// The owner fact below says it, naming the collection; this is for one that could not be read.
		if (scope === 'collection' && !owningCollection) {
			return readOnly
				? "Owned by a collection you don't manage — read-only."
				: 'Owned and managed by this collection.';
		}
		return null;
	})();

	// The scope panel's usage fact: the owning collection's badge, from the
	// collection document in hand (KPR-1282), or the "Used in N collections"
	// door to the collections list, as the card's tile on the cards page says.
	const usageFact = (() => {
		if (isNewRecord) return targetCollectionId ? <CardOwnerFact collection={targetCollection} /> : null;
		if (scope === 'collection') return <CardOwnerFact collection={owningCollection} readOnly={readOnly} />;
		if (!usage) return null;
		const usedIn = Array.isArray(usage.collections) ? usage.collections.length : 0;
		if (!usedIn) return null;
		return (
			<div className='card-def-form__usage'>
				<Link to={cardCollectionsPath({ key: card?.key, _id: currentCardId })}>
					{`Used in ${usedIn} collection${usedIn === 1 ? '' : 's'}`}
				</Link>
			</div>
		);
	})();

	// The badge previews the identity live as the color and icon are edited.
	const cardVisual = { icon: card?.icon, color: card?.color, name: card?.name };
	const displayName = (card?.name || '').trim() || (isNewRecord ? 'New card' : 'Untitled card');

	// The form's actions, defined once: the context bar shows them at the top
	// (beside "No changes" and the ⋯ menu), FormActionsFooter repeats the same
	// node at the end of the main column. A locked form gets neither.
	const saveButton = editingLocked ? null : (
		<Button type='primary' onClick={saveCard} loading={saveStatus === 'saving'} disabled={!isDirty || saveStatus === 'saving'}>
			{saveStatus === 'failed' ? 'Retry save' : (isNewRecord ? 'Create card' : 'Save changes')}
		</Button>
	);
	const formActions = editingLocked ? null : <><Button onClick={handleCancel}>Cancel</Button>{saveButton}</>;

	// The page's chrome (CardPageFrame): the platform page's own context bar and
	// tabs, or, inside the collection shell (KPR-1282), the shell's crumb tail,
	// actions slot and one ⋯, the card's tile and title leading the content and
	// its sections a left sub-nav. One Save, enabled when dirty; when clean the
	// reason sits beside it as muted text rather than a bare disabled button.
	const frame = {
		inShell, collectionId: shellCollectionId, cardId: isNewRecord ? 'new' : cardId, awaiting: awaitingCard,
		breadcrumb: owningCollection && !awaitingCard ? collectionCrumbs(owningCollection, owningCollectionId, 'Card definition') : [CARDS_CRUMB, { label: 'Card definition' }],
		record: awaitingCard ? { icon: '', color: '', name: '' } : cardVisual,
		title: awaitingCard ? 'Card definition' : displayName,
		crumbTail: isNewRecord ? 'New card' : ((savedCard?.name || '').trim() || 'Untitled card'),
		actions: awaitingCard || onOverview ? null : <>
			{!editingLocked && !isNewRecord && !isDirty && saveStatus !== 'saving' ? <span className='item-form-draft' data-testid='no-changes'>No changes</span> : null}
			{formActions}
		</>,
		// A phone's shell header has room for the primary alone; Cancel is at the form's foot.
		phoneActions: awaitingCard || onOverview ? null : saveButton,
		menuActions: headerActions, tabs: tabItems, activeTab, onTab: setTab, dirtyTabs, globalCard: inShell && scope === 'global' && savedCard ? { card: savedCard, collection: shellCollection, usage, admin, cardsById: cachedCardsById, onArrangeTile: () => setTileLayoutOpen(true) } : null,
	};
	// A card this collection does not own is not drawn under it (its owner's page, or the platform page).
	const shellRedirect = cardShellRedirect({ shellCollectionId, shellCollection, routeCollectionId, card: savedCard, cardId, ready: loadState === 'ready', search: searchParams.toString(), admin });
	if (shellRedirect) return <Navigate replace to={shellRedirect} />;

	// An existing card that has not answered: no actions, no sections, no ⋯
	// (nobody knows yet what this person may do here) over a skeleton, the
	// failure with Retry, or the plain statement that it is not there. Nothing
	// below it is drawn, so nothing can be edited or saved.
	if (awaitingCard) {
		return <CollectionScope collectionId={null}>
			<div className='item-form-page card-def-form'>
				<CardPageFrame {...frame}>
					<CardLoadState inShell={inShell} loadState={loadState} collectionId={shellCollectionId} onRetry={() => setLoadAttempt(n => n + 1)} onLeave={() => navigate('/cards')} />
				</CardPageFrame>
			</div>
		</CollectionScope>;
	}

	// The card's own page is at home in the collection that owns it: a
	// badge here is qualified only by ANOTHER collection (RecordBadge
	// `collection`, contexts/CollectionScopeContext).
	return <CollectionScope collectionId={owningCollectionId || null}>
		<div className='item-form-page card-def-form'>
			<CardPageFrame {...frame}>
			{/* A card owned by a GLOBAL collection: changing it changes what
			    every reader sees and what other collections' lookups point at. */}
			{scope === 'collection' && owningCollection ? (
				<GlobalLiveNotice collection={owningCollection} strong className='card-def-form__alert' />
			) : null}

			<DiscoverHint pkg={discoverPackage} onDismiss={() => setDiscoverDismissed(true)} />

			{createBlocked ? (
				<Guidance
					tone='caution'
					className='card-def-form__alert'
					title='Cards are created from inside a collection'
					why="Open the collection's Cards tab and use its “Create a card” button — the new card will belong to that collection."
					links={['cards-and-elements/create-a-card']}
				/>
			) : null}

			{/* One tab panel, as the settings surfaces render one. */}
			<div role='tabpanel' className='settings-tabpanel'>
				{/* An element a filter on this card could mean several of: read
				    from every tab, since Save is one action across them. */}
				{filterPicks.refusal ? (
					<div className='card-def-form__filter-pick'>
						<Typography.Text type='secondary' className='small'>{filterRefusalWhere(saveError, labelOfElementName)}</Typography.Text>
						<QueryRefusalPick
							inline
							refusal={filterPicks.refusal}
							disabled={saveStatus === 'saving'}
							onPick={(value) => {
								const refusal = filterPicks.refusal;
								if (refusal?.field) {
									fieldPicksRef.current = {
										...fieldPicksRef.current,
										[refusal.field]: withElementChoice(fieldPicksRef.current[refusal.field] || {}, refusal, value),
									};
								}
								filterPicks.pick(value);
								saveCard();
							}}
							onRepick={() => { fieldPicksRef.current = {}; filterPicks.clearRefusal(); saveCard(); }}
						/>
					</div>
				) : null}

				{onOverview ? (savedCard ? (
					<CardHome
						showTitle={!inShell}
						card={savedCard}
						cardId={currentCardId}
						parentChain={savedParentChain}
						elementSetsById={cachedElementSetsById}
						childCards={sortedChildCards}
						usage={usage}
						scopeTag={scopeTag}
						primaryDate={savedPrimaryDate}
						commentsAllowed={savedCommentsAllowed}
						editable={canEditCard}
						formLayout={savedFormLayout}
						elementAccess={elementAccess}
					/>
				) : null) : activeTab === CARD_TAB.danger ? (
					<CardDangerZone
						card={card}
						canDelete={!isNewRecord && !editingLocked && Boolean(currentCardId)}
						canExport={!isNewRecord && Boolean(currentCardId)}
						canViewHistory={!isNewRecord && !readOnly && Boolean(currentCardId)}
						canPurge={!isNewRecord && !editingLocked && Boolean(currentCardId) && scope === 'collection'}
						canRetire={admin && !isNewRecord && !editingLocked && Boolean(currentCardId) && scope === 'global'}
						inUse={usage?.inUse}
						lookedUpWords={lookedUpWords}
						onExport={() => setExportOpen(true)}
						onHistory={() => setHistoryOpen(true)}
						onDelete={deleteCard}
						onRetire={retireCurrentCard}
						onUnretire={unretireCurrentCard}
					/>
				) : (
					<div className='item-form-columns'>
						{/* Native fieldset disabling covers native controls; antd's
						    non-native pickers are covered by the handleChange gate. */}
						<fieldset disabled={editingLocked} className='item-form-main card-def-form__main'>

							{activeTab === CARD_TAB.basics ? <>
								<section className='form-panel'>
									<div className='micro-label form-panel__kicker'>Basics</div>
									<InputText elementName='name' elementLabel='Name' value={card?.name} onChange={handleChange} required={true} />
									<InputText elementName='description' elementLabel='Description' value={card?.description} onChange={handleChange} />
								</section>

								<section className='form-panel'>
									<div className='micro-label form-panel__kicker'>Identity</div>
									{/* One control for both halves of the identity: the tile
									    every reference to this card's items will wear. */}
									<InputIconSelect
										elementName='icon'
										elementLabel='Icon and color'
										value={card?.icon}
										onChange={handleChange}
										colorElementName='color'
										colorValue={card?.color}
										onColorChange={handleChange}
									/>
								</section>

								<section className='form-panel'>
									<div className='micro-label form-panel__kicker'>Hierarchy</div>
									<InputSelect
										elementName='parentCardId'
										elementLabel='Parent card (optional)'
										value={card?.parentCardId || undefined}
										onChange={handleChange}
										// A global card in use keeps its parent (KPR-280, G14), and so
										// does a published collection's looked-up card (KPR-281).
										disabled={onlyGrows}
										options={parentOptions}
										placeholder={allCardsLoading ? 'Loading card types...' : (parentOptions.length ? 'Select a parent card (optional)' : 'No other cards available')}
										allowClear={true}
										// Detaching a parent is not an edit, it is a
										// demolition: every inherited element leaves this
										// card and its descendants, and the next save of
										// each existing item rebuilds its element map
										// against the parentless definition and drops the
										// stored values for those elements for good. The
										// editor already puts a previewed confirmation in
										// front of the strictly smaller act of driving one
										// element (DrivenBackfillWarning); until the same
										// exists for this, the ✕ must not be able to send
										// a detach. `undefined` keeps the key out of the
										// PATCH body, which the API's hasOwnProperty gate
										// reads as "not mentioned" and leaves alone.
										clearValue={undefined}
									/>
									<p className='form-panel__hint'>
										{lookedUpWords
											? `${(savedCard?.name || '').trim() || 'This card'} ${lookedUpWords}, so its parent card cannot change.`
											: onlyGrows
												? 'This card is in use, so its parent card cannot change.'
												: retiredPresetName
													? `${retiredPresetName} is retired: keepr no longer offers it to build new cards on. Choose another parent card, or none.`
													: 'Assigning a parent lets this card inherit its fields. Nesting supports multiple levels.'}
									</p>
									<div className='card-def-form__lineage' aria-label='Lineage'>
										{lineageNodes.map((node, idx) => {
											const isCurrent = idx === lineageNodes.length - 1;
											const id = normalizeId(node?._id);
											return (
												<Fragment key={`${id || idx}-lineage`}>
													{idx > 0 ? <span className='card-def-form__lineage-sep' aria-hidden='true'>›</span> : null}
													<RecordBadge
														kind='card'
														record={isCurrent ? cardVisual : node}
														title={node?.name || '(untitled)'}
														size='xs'
														active={isCurrent}
														to={!isCurrent && id ? cardPagePath(node) : undefined}
													/>
												</Fragment>
											);
										})}
									</div>
								</section>

								{/* Use items as tags is a COLLECTION's setting for the card
								    (docs/plans/tags.md §3.2: a card may be global), set in
								    each collection's Card Library beside Allow private
								    items — the card editor only points there (T4d). */}
								{!isNewRecord ? (
									<section className='form-panel' data-testid='card-item-tags-note'>
										<div className='micro-label form-panel__kicker'>Use items as tags</div>
										<p className='form-panel__hint'>
											Whether this card&rsquo;s items can be put on other items as tags is set by each collection
											that uses the card, on its Cards tab, with the tag beside the card.
										</p>
										{owningCollection?._id ? (
											<Link to={`/collections/${normalizeId(owningCollection._id)}/cards`}>
												{`Open ${owningCollection.name || 'the collection'}’s cards`}
											</Link>
										) : null}
									</section>
								) : null}
							</> : null}

							{activeTab === CARD_TAB.display ? (
								<>
									<section className='form-panel'>
										<div className='micro-label form-panel__kicker'>Display value</div>
										<p className='form-panel__hint'>{displaySummary}</p>
										{saveError?.code === 'invalid_template' ? (
											<Alert
												className='card-def-display__error'
												type='error'
												showIcon
												message={saveError.message || `The title uses {{${saveError.token}}}, which is not an element of this card.`}
												description='Remove it from the title, or add an element with that name.'
											/>
										) : null}
										<DisplayTemplateEditor
											template={card?.displayTemplate || ''}
											onChange={(val) => handleChange('displayTemplate', val)}
											elements={displayElementOptions}
										/>
									</section>

									{/* How the items READ is this card's to say; how they are
									    ARRANGED is a layout, which cascades to collections and
									    readers and so lives outside this form's one draft. */}
									{!isNewRecord && currentCardId ? (
										<section className='form-panel'>
											<div className='micro-label form-panel__kicker'>Tile layout</div>
											<p className='form-panel__hint'>
												Which fields an item shows in the grid, and how they sit.
												Unset, the tile picks its own. Saved separately from this
												form, and a collection or a reader can override it.
											</p>
											{/* The arranger saves against the STORED card, and the API
											    checks every slot's element against it — so a draft's
											    unsaved elements (a just-attached element set most
											    visibly) would be offered and then refused. Same gate
											    as "Copy into this card": save first. */}
											<Tooltip title={isDirty ? 'Save your changes first' : ''}>
												<Button onClick={() => setTileLayoutOpen(true)} disabled={isDirty}>
													Arrange tile&hellip;
												</Button>
											</Tooltip>
										</section>
									) : null}

									{/* The FORM: the order and groups of the item form and the
									    read view. Card tier only and inherited by child cards,
									    so it is this card's to say — but it is its own record,
									    saved on its own like the tile. */}
									{!isNewRecord && currentCardId ? (
										<section className='form-panel' data-testid='form-layout-section'>
											<div className='micro-label form-panel__kicker'>Form layout</div>
											<p className='form-panel__hint'>
												{!savedFormLayout
													? 'Automatic: fields are grouped by each element’s Group option. Arrange them to order and group every field — inherited ones and element sets’ included.'
													: normalizeId(savedFormLayout.card_id) === normalizeId(currentCardId)
														? `Customized for this card: ${formatCount(savedFormLayout.body?.groups?.length || 0, 'group')}. Child cards inherit it until they customize their own.`
														: `Inherited from ${cachedCardsById?.[normalizeId(savedFormLayout.card_id)]?.name || 'a parent card'}. Customize it to change the form for this card and its child cards.`}
											</p>
											<Tooltip title={isDirty ? 'Save your changes first' : ''}>
												<Button onClick={() => setFormLayoutOpen(true)} disabled={isDirty}>
													Arrange form&hellip;
												</Button>
											</Tooltip>
										</section>
									) : null}

									{/* RELATED LISTS (KPE-029, KPR-720): named lists of the items
									    that link to this card's items, shown on the item view.
									    A collection card's, so a managers' setting — this tab is
									    already withheld from anyone who cannot edit the card —
									    and a saved card's, since a list may name the card itself. */}
									{!isNewRecord && currentCardId && scope === 'collection' && owningCollectionId ? (
										<RelatedListsOptions
											cardId={currentCardId}
											parentCardId={normalizeId(card?.parentCardId) || null}
											lists={draftRelatedLists}
											savedLists={savedRelatedLists}
											inherited={inheritedRelatedLists}
											collection={owningCollection}
											collectionId={owningCollectionId}
											cards={allCards}
											cardsById={cachedCardsById}
											getElementsForCard={getElementsForCard}
											limit={relatedListsLimit}
											saveError={saveError}
											disabled={editingLocked}
											onChange={(next) => {
												// Nothing stored and nothing left: the card goes back to what it
												// stored (the key absent, or []), so adding a list and taking
												// it back is not an edit.
												handleChange('options.relatedLists', next.length === 0 && savedRelatedLists.length === 0 ? savedCard?.options?.relatedLists : next);
												// A refusal is about the list as it was; editing clears it.
												setSaveError(prev => (isRelatedListsRefusal(prev) ? null : prev));
											}}
										/>
									) : null}

									{/* Paper. A page layout belongs to a collection's context
									    (its query blocks are compiled there), so the canvas
									    opens from a collection rather than from here. */}
									{!isNewRecord && currentCardId && card?.collection_id ? (
										<section className='form-panel'>
											<div className='micro-label form-panel__kicker'>Page layouts</div>
											<p className='form-panel__hint'>
												Printable sheets for this card's items &mdash; a face sheet, a
												label, a report. Letter, legal, A4, labels and plotter sizes.
											</p>
											<Button
												onClick={() => navigate(`/collections/${normalizeId(card.collection_id)}/cards/${currentCardId}/layouts/new`)}
											>
												New page layout&hellip;
											</Button>
										</section>
									) : null}
								</>
							) : null}

							{activeTab === CARD_TAB.elements ? (
								<section className='form-panel'>
									{/* G14 (KPR-280), said before anyone tries: the API refuses a
									    removal or a retype on a global card in use, and the save's
									    refusal repeats it in the server's own sentence. */}
									{usedByWords ? (
										<Alert
											type='info'
											showIcon
											className='card-def-form__alert'
											data-testid='card-only-grows'
											message={`${(savedCard?.name || '').trim() || 'This card'} is in use — ${usedByWords} — so it only grows.`}
											description={`You can add elements and change their labels and help. You cannot remove or retype an element, change the parent card or take off an element set.${holdsGroup ? ` ${GROUP_ONLY_GROWS_SENTENCE}` : ''} To stop new collections using it, retire it from the Danger zone.`}
										/>
									) : lookedUpWords ? (
										// G15 (KPR-281): a published collection's card that others look
										// up. One sentence says why; the rule is the same, and there is
										// no Retire for a collection card — it waits for the collection.
										<Alert
											type='info'
											showIcon
											className='card-def-form__alert'
											data-testid='card-only-grows'
											message={`${(savedCard?.name || '').trim() || 'This card'} ${lookedUpWords}, so it only grows.`}
											description={`You can add elements and change their labels and help. You cannot remove or retype an element, change the parent card or take off an element set.${holdsGroup ? ` ${GROUP_ONLY_GROWS_SENTENCE}` : ''}`}
										/>
									) : null}
									{/* Lookups may point into a published global collection: the
									    followed ones are in the picker already; this finds others. */}
									{lookupOwner.scope === 'collection' && globalTargets.collections.length ? (
										<div className='card-def-form__global-search'>
											<Input.Search
												{...NO_TEXT_ASSIST}
												allowClear
												loading={globalTargets.searching}
												placeholder='Find a global collection to look up…'
												aria-label='Find a global collection to look up'
												onSearch={globalTargets.search}
											/>
											<span className='meta-line'>
												{globalTargets.cards.length
													? `Cards of ${new Set(globalTargets.cards.map(c => c.globalCollection?.id)).size === 1 ? '1 global collection' : `${new Set(globalTargets.cards.map(c => c.globalCollection?.id)).size} global collections`} are offered under "Global collections" in each lookup.`
													: 'Follow a global collection, or find one here, to look up its cards.'}
											</span>
										</div>
									) : null}
									<CardDefElements
										elements={card?.elements || []}
										onChange={handleChange}
										newMoneyCode={newMoneyCode}
										showTitle={true}
										cardOptions={lookupCardOptions}
										cardOptionsLoading={lookupScopeLoading}
										targetCard={drivenTargetCard}
										collectionCards={allCards}
										collectionId={owningCollectionId || null}
										saveError={saveError}
										savedElements={savedElements}
										extraElements={draftSetElements}
										explainEmpty={carriedElementCount === 0}
										typesExcluded={cardTypesWithheld}
										columnTypesExcluded={columnTypesWithheld}
										columnCardOptions={columnCardOptions}
										onExtract={!editingLocked && !isNewRecord && scope === 'collection' ? () => setExtractOpen(true) : null}
										extractDisabledReason={extractDisabledReason}
										onCheckShapeChange={checkShapeChange}
										filesOffCollections={filesOffCollections}
										accessFor={accessFor}
										onCopyGroup={copyGroupOffered ? openCopyGroup : null}
									/>
									<CardElementSets
										attachedIds={draftSetIds}
										attached={draftSets}
										inherited={inheritedSets}
										available={availableSets}
										setsById={cachedElementSetsById}
										onChange={ids => handleChange('elementSets', ids)}
										onMaterialize={materializeDisabledReason ? null : materializeSet}
										materializeDisabledReason={materializeDisabledReason}
										primaryDateSuggestion={primaryDateSuggestion}
										onUsePrimaryDate={usePrimaryDateSuggestion}
										error={elementSetRefusal}
										disabled={editingLocked}
									/>
									<ToggleRow label='Primary date' description={primaryDateHelp} className='card-def-primary-date'>
										<PrimaryDateList
											value={primaryDateValue}
											options={primaryDateOptions}
											onChange={value => handleChange('options.primaryDate', value)}
											disabled={editingLocked}
											label='Primary date'
										/>
									</ToggleRow>
									{primaryDateRefusal ? (
										<Alert className='card-def-primary-date__error' type='error' showIcon message={primaryDateRefusal} />
									) : null}
									{/* Comments (KPR-305): the switch, then the two settings
									    that only mean something while it is on — shown
									    greyed out while it is off, so a manager sees what
									    turning comments on would offer. */}
									<ToggleRow
										label='Allow comments'
										description={commentsHelp}
										checked={commentsAllowed}
										onChange={value => handleChange('options.allowComments', value)}
										disabled={editingLocked}
									/>
									<ToggleRow
										label='Strict comment auditing'
										description={strictAuditingHelp}
										checked={strictCommentAuditing}
										onChange={value => handleChange('options.strictCommentAuditing', value)}
										disabled={editingLocked || !commentsAllowed}
										className='toggle-row--child'
									/>
									<ToggleRow
										label='Comments expanded by default'
										description={commentsExpandedHelp}
										checked={commentsExpanded}
										onChange={value => handleChange('options.commentsExpanded', value)}
										disabled={editingLocked || !commentsAllowed}
										className='toggle-row--child'
									/>
									{/* A list you WORK THROUGH rather than read. Sits with
									    the other card-level switches because it is one:
									    the card decides what finishing a row means. */}
									{/* Whether these items are working notes or records
									    that freeze and are corrected. */}
									<RecordOptions
										record={recordPolicySetting}
										disabled={editingLocked}
										onChange={value => handleChange('options.record', value)}
									/>
									<WorklistOptions
										worklist={worklist}
										elements={nameableElements}
										cardId={currentCardId || null}
										disabled={editingLocked}
										onChange={value => handleChange('options.worklist', value)}
									/>
								</section>
							) : null}

							<FormActionsFooter>{formActions}</FormActionsFooter>
						</fieldset>

						<aside className='item-form-side'>
							<section className='form-panel'>
								<div className='micro-label form-panel__kicker'>Scope</div>
								<div className='card-def-form__scope'>
									{scopeTag}
									{scopeText ? <p>{scopeText}</p> : null}
									{usageFact}
								</div>
							</section>

							<section className='form-panel'>
								<div className='micro-label form-panel__kicker'>
									Child cards
									{sortedChildCards.length ? (
										<span className='form-panel__count'>{sortedChildCards.length}</span>
									) : null}
								</div>
								{currentCardId ? (
									sortedChildCards.length ? (
										<div className='card-def-form__children'>
											{sortedChildCards.map(child => (
												<RecordBadge
													key={normalizeId(child?._id)}
													kind='card'
													record={child}
													title={child?.name || '(untitled card)'}
													size='sm'
													to={cardPagePath(child)}
												/>
											))}
										</div>
									) : (
										<p className='form-panel__hint'>No child cards yet.</p>
									)
								) : (
									<p className='form-panel__hint'>Save this card to add children.</p>
								)}
							</section>

							<div className='item-form-shortcuts'>
								<span><kbd>⌘↵</kbd> Save</span>
								<span><kbd>Esc</kbd> Cancel</span>
							</div>
						</aside>
					</div>
				)}

			</div>
			</CardPageFrame>
		</div>

		<MultiToggleDialog
			open={Boolean(shapeRefusal)}
			refusal={shapeRefusal}
			label={savedElements[shapeRefusal?.element]?.label?.singular || shapeRefusal?.element}
			collectionId={owningCollectionId}
			asksDetach={shapeAsksDetach}
			onConvert={convertShape}
			onCancel={cancelShape}
		/>
		<StaleRecordModal
			open={Boolean(staleCard)}
			noun='card'
			overlap={staleCard?.overlap || []}
			onReapply={reapplyStaleCard}
			onDiscard={discardStaleCard}
			onClose={() => setStaleCard(null)}
		/>

		<HistoryModal
			open={historyOpen}
			onClose={() => setHistoryOpen(false)}
			kicker='Card definition'
			badge={<RecordBadge kind='card' record={cardVisual} title={displayName} />}
			fetchPage={params => fetchCardHistory(currentCardId, params)}
			// A revert that left related lists out says so first: the reload waits for that notice to be closed.
			onReverted={(result, notice) => (notice ? notice.afterClose(() => window.location.reload()) : window.location.reload())}
		/>
		{copyGroupOpen ? (
			<CopyGroupModal
				open={copyGroupOpen}
				collectionId={copyCollectionId}
				card={card ? { _id: currentCardId || null, name: card.name, icon: card.icon, color: card.color } : null}
				namesInUse={copyNames}
				onCopy={handleCopyGroup}
				onClose={() => setCopyGroupOpen(false)}
			/>
		) : null}
		<ExtractElementSetModal
			open={extractOpen}
			card={savedCard}
			cards={allCards}
			setsById={cachedElementSetsById}
			onClose={() => setExtractOpen(false)}
			onExtracted={handleExtracted}
		/>
		<ExportModal
			open={exportOpen}
			onClose={() => setExportOpen(false)}
			target={exportTarget}
			title='Export card definition'
			baseName={card?.name || 'card'}
		/>

		{currentCardId && formLayoutOpen ? (
			<FormLayoutModal
				open={formLayoutOpen}
				onClose={() => setFormLayoutOpen(false)}
				card={savedCard || card}
				entries={savedFormEntries}
				cardsById={cachedCardsById}
			/>
		) : null}

		{/* Who can see or change one element: its own record in the collection
		    in hand, saved on its own like the tile layout below. */}
		{accessElement && elementAccess ? (
			<ElementAccessModal
				open={Boolean(accessElement)}
				onClose={() => setAccessElement(null)}
				onSaved={elementGrants.reload}
				collectionId={accessCollectionId}
				collection={tileCollection}
				cardId={currentCardId}
				cardName={savedCard?.name || ''}
				chainIds={accessChainIds}
				cardNamesById={accessCardNames}
				element={accessElement}
				group={accessElement.group || null}
				grants={elementAccess.grants}
				inherited={elementGrants.inherited}
			/>
		) : null}

		{/* The tile layout is its own record with its own tiers, so it saves on
		    its own rather than riding this form's Save. */}
		{currentCardId ? (
			<TileLayoutModal
				open={tileLayoutOpen}
				onClose={() => setTileLayoutOpen(false)}
				card={savedCard || card}
				elementDefs={savedTileElementDefs}
				collection={tileCollection}
				collectionId={tileCollectionId}
			/>
		) : null}

	</CollectionScope>;

};

const CardDefElementsComponent = (props) => {
	const {
		elements = [],
		onChange,
		showTitle = false,
		allowNew = true,
		cardOptions = [],
		cardOptionsLoading = false,
		targetCard = null,
		collectionCards = [],
		// The collection the card belongs to: a driven value's condition picks
		// an item lookup's value from its items (KPR-950).
		collectionId = null,
		saveError = null,
		savedElements = null,
		// An ELEMENT SET's editor (docs/plans/element-sets-and-me.md): the
		// set's key, under which every name is stored `<key>_<slug>` and shown
		// muted beneath the label; and the option keys a set element may not
		// carry (a driven value — the card decides those).
		namespace = null,
		optionKeysExcluded = NO_KEYS,
		// The data types neither picker offers here: the element-set editor's
		// `confirmations` (KPR-390 — a set cannot hold a "Me too" count; the
		// API refuses one, 400 invalid_options).
		typesExcluded = NO_KEYS,
		// Elements that are on the card without being rows here — the
		// attached element sets' — so a date bound or a formula may name them.
		extraElements = NO_SETS,
		// The card editor's empty list explains elements instead of showing
		// nothing (KPR-324). Off for the element-set editor, and off when the
		// card carries inherited or attached-set elements.
		explainEmpty = false,
		// "Make an element set…" (extract, docs/plans/element-sets-and-me.md):
		// the door in the section's ⋯, offered by the card editor alone;
		// `extractDisabledReason` says why it is off right now (unsaved,
		// dirty, not a collection card).
		onExtract = null,
		extractDisabledReason = null,
		// "Allow multiple" on a saved Choice asks first (CardDefElement).
		onCheckShapeChange = null,
		// The collections using this card whose attachments are off (G9).
		filesOffCollections = NO_COLLECTIONS,
		// The currency a new currency element starts in (ruled L3).
		newMoneyCode = KEEPR_CURRENCY,
		// Who can see or change each element (KPR-509): `(element) =>
		// { limited, onOpen } | null`, by its id; null where the API offers
		// no rules.
		accessFor = null,
		// The types a group's COLUMN pickers withhold beyond the two no column
		// can be (a global card's group has no card lookup, KPR-692), and the
		// cards a group's lookup columns may point at (the card's own
		// collection's, KPR-675).
		columnTypesExcluded = NO_KEYS,
		columnCardOptions = null,
		// "Copy a group from…" (KPR-859): the card editor's door to a group
		// another card or an installed starter has; null where it is not
		// offered (switched off, read-only, not a collection's card, the
		// element set editor).
		onCopyGroup = null,
	} = props;

	const [openKeys, setOpenKeys] = useState([]);
	// The name each element will save under (a new one's is derived from its
	// label, as the API derives it) — the key its saved counterpart is under.
	// Inside a set every name is namespaced, so the set's rule applies.
	const effectiveNames = useMemo(
		() => (namespace ? namespacedElementNames(elements, namespace) : effectiveElementNames(elements)),
		[elements, namespace]
	);

	const toggleOpen = (k) => setOpenKeys(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);

	// A stable identity per ROW, for React's key and for the drag list — the
	// elements themselves carry no id, and their names change as they are
	// typed.
	//
	// Kept in step DURING RENDER, not in an effect. An effect runs after the
	// paint, so every render that saw more elements than ids — the first one
	// after a card loads, and every remount of this panel with elements
	// already in hand (a tab switch) — keyed its rows `undefined`: React's
	// "unique key" warning, and then a full remount of the list a tick later
	// when the ids arrived, throwing away the rows' own state (an open row, a
	// focused field, a half-picked select). Reconciling here means a row has
	// its key on the very first paint it exists in.
	const [uiIds, setUiIds] = useState(() => (elements || []).map(() => ulid()));
	const elementCount = (elements || []).length;
	let reconciledIds = uiIds;
	if (reconciledIds.length !== elementCount) {
		reconciledIds = [...reconciledIds];
		while (reconciledIds.length < elementCount) reconciledIds.push(ulid());
		while (reconciledIds.length > elementCount) reconciledIds.pop();
		// Setting state while rendering THIS component is React's own
		// derived-state escape hatch: it re-renders before committing, and
		// the render below already uses the reconciled list either way.
		setUiIds(reconciledIds);
	}

	const removeElement = (index) => {
		setUiIds(prev => prev.filter((_, i) => i !== index));
		onChange('elements', (elements || []).filter((_, i) => i !== index));
	};

	// An element born NEXT TO another one rather than at the foot of the list
	// — a range's end element (DateOptions "Create an end element"). The row
	// identities move with it, so the rows that were open stay open and the
	// drag keys keep naming the same elements; an append through the effect
	// above would have handed the new row the identity of the one it pushed
	// down. `mutate` gets the array WITH the new element in it, by the name
	// the API will give it, so the link and the element land in one change.
	const insertElementAfter = (index, element, mutate = null) => {
		const list = [...(elements || [])];
		list.splice(index + 1, 0, element);
		const uiId = ulid();
		setUiIds(prev => {
			const next = [...prev];
			next.splice(index + 1, 0, uiId);
			return next;
		});
		setOpenKeys(prev => [...prev, uiId]);
		onChange('elements', mutate ? mutate(list) : list);
	};

	// A refused save points at ONE row, and its sentence lives in that row's
	// body — so the row has to be open to be read. It often is not: this panel
	// unmounts with its open/closed state every time the author visits another
	// tab, and the save that was refused can be pressed from any of them. Open
	// the row the refusal names, once per refusal, so nobody has to hunt for a
	// message the toast already faded on.
	const revealedRef = useRef(null);
	useEffect(() => {
		if (!saveError) {
			revealedRef.current = null;
			return;
		}
		if (revealedRef.current === saveError) return;
		// A refusal about a COLUMN names the group by `element` (and `groupIndex`
		// when the group has no name yet); its own `index` is the column's.
		const index = typeof saveError.groupIndex === 'number'
			? saveError.groupIndex
			: (typeof saveError.index === 'number' && !saveError.column
				? saveError.index
				: effectiveNames.findIndex(name => name && name === saveError.element));
		const key = uiIds[index];
		// The ids catch up a render later on a first mount; retry then.
		if (!key) return;
		revealedRef.current = saveError;
		setOpenKeys(prev => (prev.includes(key) ? prev : [...prev, key]));
	}, [saveError, uiIds, effectiveNames]);

	// While the explanation shows, its own Add element is the one door; a
	// second identical button in the header would be noise (and ambiguous).
	const showEmptyExplanation = explainEmpty && allowNew && !elements?.length;
	const addElement = () => {
			// A new element has NO type. Every option a row offers is
			// type-dependent, so the type is the first question, not a
			// default to undo: the row opens on the gallery
			// (docs/plans/card-editor-dates-groups-filters.md Part 1).
			const newElement = {
				name: '',
				dataType: '',
				label: { singular: '', plural: '' },
				options: { choices: [] },
			};
			const uiId = ulid();
			setUiIds(prev => [...prev, uiId]);
			setOpenKeys(prev => [...prev, uiId]);
			onChange('elements', [...(elements || []), newElement]);
	};
	const newElementButton = <Button icon={<PLUS />} onClick={addElement}>
		Add element
	</Button>;

	// const sensors = useSensors( 
	// 	useSensor( TouchSensor, { activationConstraint: {
	// 		delay: 150,
	// 		tolerance: 8,
	// 	}}),
	// 	useSensor( MouseSensor, { activationConstraint: { distance: 8 } } ),
	// );

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
	);

	const elementIds = reconciledIds;
	const handleDragEnd = (event) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const oldIndex = elementIds.indexOf(active.id);
		const newIndex = elementIds.indexOf(over.id);
		const reordered = arrayMove(elements, oldIndex, newIndex);
		setUiIds(prev => arrayMove(prev, oldIndex, newIndex));
		onChange('elements', reordered);
	};

	return (
		<div className='card-def-elements'>
			<div className='form-panel__kicker-row'>
				{showTitle ? (
					<div className='micro-label form-panel__kicker'>
						Elements
						{elements?.length ? <span className='form-panel__count'>{elements.length}</span> : null}
					</div>
				) : <span />}
				<span className='card-def-elements__header-actions'>
					{allowNew && !showEmptyExplanation ? newElementButton : null}
					{allowNew && onCopyGroup ? (
						<Button icon={<DUPLICATE />} onClick={onCopyGroup} data-testid='copy-group-open'>
							Copy a group from&hellip;
						</Button>
					) : null}
					{onExtract ? (
						<Dropdown
							trigger={['click']}
							placement='bottomRight'
							menu={buildActionMenu([
								{
									key: 'extract',
									group: 'record',
									glyph: 'duplicate',
									label: 'Make an element set…',
									disabled: Boolean(extractDisabledReason),
									title: extractDisabledReason || undefined,
									onClick: onExtract,
								},
							])}
						>
							<Button type='text' icon={<KEBAB />} aria-label='Elements actions' />
						</Dropdown>
					) : null}
				</span>
			</div>

			{showEmptyExplanation ? (
				<NoElementsEmptyState onAdd={addElement} />
			) : null}

			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				// onDragOver={handleDragEnd}
				onDragEnd={handleDragEnd}
			>
				<SortableContext
					items={elementIds}
					strategy={verticalListSortingStrategy}
				>
					<div className='card-def-elements__list'>
						{elements?.map((e, idx) => (
							<SortableElement
								key={elementIds[idx]}
								id={elementIds[idx]}
							>
								{(dragHandleProps) => (
									<CardDefElement
										element={e}
										index={idx}
										onChange={onChange}
										onDeleteElement={removeElement}
										onInsertElementAfter={insertElementAfter}
										dragHandleProps={dragHandleProps}
										panelKey={elementIds[idx]}
										isOpen={openKeys.includes(elementIds[idx])}
										onToggleOpen={() => toggleOpen(elementIds[idx])}
										cardOptions={cardOptions}
										cardOptionsLoading={cardOptionsLoading}
										targetCard={targetCard}
										collectionCards={collectionCards}
										collectionId={collectionId}
										siblingElements={elements}
										saveError={saveError}
										elementName={effectiveNames[idx]}
										savedElement={savedElements ? savedElements[effectiveNames[idx]] || null : null}
										namespace={namespace}
										optionKeysExcluded={optionKeysExcluded}
										typesExcluded={typesExcluded}
										extraElements={extraElements}
										onCheckShapeChange={onCheckShapeChange}
										filesOffCollections={filesOffCollections}
										newMoneyCode={newMoneyCode}
										access={accessFor ? accessFor(e) : null}
										columnTypesExcluded={columnTypesExcluded}
										columnCardOptions={columnCardOptions}
									/>
								)}
							</SortableElement>
						))}
					</div>
				</SortableContext>
			</DndContext>

			{(allowNew && elements?.length >= 3) ? (
				<div className='card-def-elements__foot'>{newElementButton}</div>
			) : null}
		</div>
	);

};

export const CardDefElements = memo(CardDefElementsComponent, (prev, next) => {
	return (
		prev.elements === next.elements &&
		prev.onChange === next.onChange &&
		prev.showTitle === next.showTitle &&
		prev.allowNew === next.allowNew &&
		prev.cardOptions === next.cardOptions &&
		prev.cardOptionsLoading === next.cardOptionsLoading &&
		prev.targetCard === next.targetCard &&
		prev.collectionCards === next.collectionCards &&
		prev.collectionId === next.collectionId &&
		prev.saveError === next.saveError &&
		prev.savedElements === next.savedElements &&
		prev.namespace === next.namespace &&
		prev.optionKeysExcluded === next.optionKeysExcluded &&
		prev.typesExcluded === next.typesExcluded &&
		prev.extraElements === next.extraElements &&
		prev.explainEmpty === next.explainEmpty &&
		prev.onCheckShapeChange === next.onCheckShapeChange &&
		prev.filesOffCollections === next.filesOffCollections &&
		prev.newMoneyCode === next.newMoneyCode &&
		prev.accessFor === next.accessFor &&
		prev.columnTypesExcluded === next.columnTypesExcluded &&
		prev.columnCardOptions === next.columnCardOptions &&
		prev.onCopyGroup === next.onCopyGroup
	);
});



export function CardDefElement(props) {

	const {
		index,
		element,
		onChange,
		onDeleteElement,
		onInsertElementAfter = null,
		dragHandleProps,
		isOpen,
		onToggleOpen,
		panelKey,
		cardOptions = [],
		cardOptionsLoading = false,
		targetCard = null,
		collectionCards = [],
		// The card's collection: a driven value's condition picks an item
		// lookup's value from its items (KPR-950).
		collectionId = null,
		// (read through `saveError` below)
		saveError: rawSaveError = null,
		// The name this element saves under (a new element's is derived from
		// its label the way the API derives it); the API's coded errors name
		// it. Defaults to the element's own name.
		elementName: elementNameProp = null,
		// The element under that name as the loaded definition holds it, or
		// null for a new one. Only a saved element's type change can be a
		// number <-> measurement conversion of stored item values.
		savedElement = null,
		namespace = null,
		optionKeysExcluded = NO_KEYS,
		typesExcluded = NO_KEYS,
		extraElements = NO_SETS,
		// The panel is read-only in the same cases the rest of the form is.
		editingLocked = false,
		// "Allow multiple" turned on or off on a SAVED Choice (owner ruling
		// B16): `async (elementName, nextElement) => null | { refusal }` asks
		// the editor's change-preview whether stored values need an answer
		// first. Absent (a standalone row), the switch flips and the save's
		// own 409 raises the same dialog.
		onCheckShapeChange = null,
		// G9: the collections using this card whose attachments are off.
		filesOffCollections = NO_COLLECTIONS,
		// The currency a new currency element starts in (ruled L3): the
		// collection's, else the author's own, else US dollars.
		newMoneyCode = KEEPR_CURRENCY,
		// Who can see or change it (KPR-509): `{ limited, onOpen }` for a
		// saved element while the API offers rules to this manager, else null.
		access = null,
		// A COLUMN of a repeating group (KPR-679): the same editor, hung from
		// `basePath` (`elements[i].options.columns[j]`) instead of
		// `elements[index]`, minus what a column cannot carry. `column` is
		// `{ group, onMove, canMoveUp, canMoveDown }` — the group's name for
		// the sentences, and the reorder doors the ⋯ menu offers (a drag is
		// not a keyboard or a phone's way to move a row).
		basePath = null,
		column = null,
		// What a group's columns may not be on this card (a global card's group
		// has no card lookup, KPR-692), handed to each column as the types its
		// pickers withhold.
		columnTypesExcluded = NO_KEYS,
		// A group's columns look up only the cards of the card's own collection.
		columnCardOptions = null,
	} = props;
	const isColumn = Boolean(column);
	// A refusal that names a column (`{ element: <group>, column }`) belongs to
	// that column's row, which is handed it translated (element := the
	// column's name, RepeatingGroupOptions). The group's own row, and every
	// other top-level element, must not read it as theirs: it carries the
	// column's `index` too, and the group's name.
	const saveError = rawSaveError && rawSaveError.column && !isColumn ? null : rawSaveError;
	const elementName = elementNameProp || element?.name || '';
	// Where this element lives in the card draft. A card's own element is
	// `elements[index]`; a COLUMN of a repeating group is edited by this same
	// component at `elements[i].options.columns[j]` (RepeatingGroupOptions,
	// KPR-679), so every path below hangs from `base` — and so does every DOM
	// id, which is why two rows never share one.
	const base = basePath || `elements[${index}]`;

	const [drivenModalOpen, setDrivenModalOpen] = useState(false);
	const [formulaModalOpen, setFormulaModalOpen] = useState(false);
	// The card cache resolves inherited elements for the calculated summary;
	// optional so this row still renders standalone (tests).
	const cardDefinitionsCache = useOptionalCardDefinitions();

	// An element with no type is an element still being authored: the row
	// body is the type gallery and nothing else, because every option below
	// depends on the answer. (A LEGACY element saved with no type is not one
	// of these — it was resolved to short text when the card loaded, which is
	// what the item form has always rendered for it; mapCardFromApi.)
	const dataType = element?.dataType || '';
	const untyped = !dataType;
	const typeMeta = getDataTypeMeta(dataType);
	// The types this row's pickers do not offer: the card's (an element set's
	// "Me too", a global card's group), and for a COLUMN the two that cannot
	// sit in a group. The reason stands where the choice would have been.
	// (A row keeps the type it already HAS, so a stored repeating group still
	// reads as one and its panel opens, whatever the picker withholds.)
	const typesWithheld = useMemo(() => {
		const list = isColumn ? [...new Set([...typesExcluded, ...COLUMN_TYPES_EXCLUDED])] : typesExcluded;
		return dataType && list.includes(dataType) && !isColumn ? list.filter(type => type !== dataType) : list;
	}, [isColumn, typesExcluded, dataType]);
	const typeWithheldNote = isColumn ? (
		<div className='form-panel__hint card-def-element__type-withheld' data-testid='column-type-note'>
			<div>{NESTING_SENTENCE}</div>
			<div>{COLUMN_TYPE_REASONS.confirmations}</div>
			{typesExcluded.includes('card-lookup') ? <div>{GLOBAL_COLUMN_LOOKUP_SENTENCE}</div> : null}
		</div>
	) : null;

	const dragHandleEl = dragHandleProps ? (
		<span
			ref={dragHandleProps.setActivatorNodeRef}
			{...dragHandleProps.attributes}
			{...dragHandleProps.listeners}
			onClick={(e) => e.stopPropagation()}
			onMouseDown={(e) => e.stopPropagation()}
			onTouchStart={(e) => e.stopPropagation()}
			style={{ cursor: 'grab', display: 'inline-flex', alignItems: 'center' }}
			aria-label="Drag to reorder"
		>
			<DRAG_HANDLE />
		</span>
	) : null;

	// The type, said the one way it is said everywhere: the chip. An untyped
	// element wears the dashed "Choose a type" placeholder.
	const typeBadgeEl = <DataTypeBadge dataType={dataType} size='sm' />;

	// Left-side header content: icon + label
	// Right-side header content: badges then drag handle
	// Removing an element never deletes what items hold under it (delete
	// audit F9): the values stay on the items, hidden, and an element with
	// the same name brings them back. The count is the save's to give — it
	// asks the server's change-preview then (hiddenValuesConfirm.js).
	const confirmDelete = () => {
		// A column leaves its group the same way: what entries hold under it is
		// not deleted, it stays on them, hidden (keepr-api KPR-675).
		if (isColumn) {
			confirmDestructive(Modal, {
				title: 'Remove column?',
				content: `"${element?.label?.singular || 'This column'}" leaves "${column.group}". Values entries already hold under it are not deleted: they stay on the entries, hidden, and adding a column with the same name back brings them back. When you save, keepr tells you how many items hold entries.`,
				okText: 'Remove',
				onOk: () => onDeleteElement(index),
			});
			return;
		}
		confirmDestructive(Modal, {
			title: 'Delete element?',
			content: `"${element?.label?.singular || 'This element'}" leaves the form. Values items already hold under it are not deleted: they stay on the items, hidden, and adding an element with the same name back brings them back. When you save, keepr tells you how many items hold one.`,
			okText: 'Delete',
			onOk: () => onDeleteElement(index),
		});
	};

	// Required is said by the pink mark after the name (the one required
	// treatment everywhere); red is reserved for destructive. The rest of the
	// badges are neutral chips — facts, not statuses.
	const badges = [];
	const choiceCount = choiceListOf(element).length;
	if (choiceCount) {
		badges.push(<Tag key="choices">{choiceCount} choice{choiceCount === 1 ? '' : 's'}</Tag>);
	}
	if (element?.options?.preferChoiceIcons) {
		badges.push(<Tag key="icons">icons</Tag>);
	}
	// What a measurement measures and its default unit ("Mass · lb"), the two
	// facts that decide what the entry form offers.
	if (dataType === 'measurement' && getMeasure(element?.options?.measure)) {
		const measureFact = `${measuresLabel(element.options) || getMeasure(element.options.measure).label} · ${unitSymbol(defaultUnitFor(element.options))}`;
		badges.push(<Tag key="measure" title={measureFact}>{measureFact}</Tag>);
	}
	// The currencies a money element takes ("USD · CAD"), the fact that
	// decides what the entry form offers; a long list says how many more.
	if (dataType === 'currency' && Array.isArray(element?.options?.currencies) && element.options.currencies.length) {
		const codes = element.options.currencies;
		badges.push(<Tag key="currencies">{codes.slice(0, 3).join(' · ')}{codes.length > 3 ? ` +${codes.length - 3}` : ''}</Tag>);
	}
	// A repeating group's size, without opening the panel: its columns and, when
	// the author set them, its limits on entries.
	if (dataType === REPEATING_GROUP) {
		const columnTotal = columnsOf(element).length;
		badges.push(<Tag key='columns'>{columnTotal ? formatCount(columnTotal, 'column') : 'no columns yet'}</Tag>);
	}
	// A driven element is not user-editable on the item form; that is worth
	// seeing without opening the panel. Calculated (formula) and driven-from-
	// another-card read differently in the header, as they do in the row.
	if (isFormulaDriven(element)) {
		badges.push(<Tag key="calculated">calculated</Tag>);
	} else if (element?.options?.drivenFrom) {
		badges.push(<Tag key="driven">driven</Tag>);
	}
	// A rule narrows who sees or changes it (KPR-509) — worth seeing from the
	// row, like a calculated value.
	if (access?.limited) {
		badges.push(<Tag key="access">access limited</Tag>);
	}

	// The whole collapsed row toggles the panel, so the ⋯ must not: the
	// wrapping span also catches the menu's own clicks, which bubble up the
	// React tree from the dropdown's portal.
	const actionMenu = (
		<span onClick={(e) => e.stopPropagation()}>
			<Dropdown
				trigger={['click']}
				menu={buildActionMenu([
					// A column moves by menu as well as by drag: a drag is not
					// a keyboard's way, nor a phone's.
					...(isColumn ? [
						{ key: 'move-up', group: 'record', icon: <ARROW_UP />, label: 'Move up', disabled: !column.canMoveUp, onClick: () => column.onMove(-1) },
						{ key: 'move-down', group: 'record', icon: <ARROW_DOWN />, label: 'Move down', disabled: !column.canMoveDown, onClick: () => column.onMove(1) },
					] : []),
					...(access ? [{ key: 'access', group: 'record', glyph: 'visibility', label: 'Who can see or change it…', onClick: access.onOpen }] : []),
					{ key: 'delete', group: 'destructive', glyph: 'delete', label: isColumn ? 'Remove column…' : 'Delete element…', onClick: confirmDelete },
				])}
			>
				<Button
					type='text'
					size='small'
					icon={<KEBAB />}
					aria-label={isColumn ? 'Column actions' : 'Element actions'}
				/>
			</Dropdown>
		</span>
	);

	// Auto-fill the plural label from the singular unless the user customized
	// it: only overwrite when the current plural is empty or still equals the
	// auto-plural of the previous singular value.
	const handleSingularChange = (name, value) => {
		const prevSingular = element?.label?.singular || '';
		const currentPlural = element?.label?.plural || '';
		onChange(name, value);
		// A column is named by one word; keepr fills the plural the API way.
		if (isColumn) return;
		if (!currentPlural || currentPlural === pluralize(prevSingular)) {
			onChange(`${base}.label.plural`, pluralize(value));
		}
	};

	// Option visibility is policy-driven by data type (config/elementOptionPolicy.js)
	// — minus what this editor withholds (a set's elements take no driven value).
	// A column carries none of what the API refuses on one (config/
	// repeatingGroups.js) — `identity` excepted: the User panel is where "Allow
	// multiple" lives, and `inGroup` hides the account link inside it.
	const keysExcluded = isColumn
		? [...optionKeysExcluded, ...COLUMN_OPTION_KEYS_EXCLUDED.filter(key => key !== 'identity')]
		: optionKeysExcluded;
	const optionKeys = getOptionKeysForElement({ ...element, dataType }).filter(key => !keysExcluded.includes(key));

	// null unless this saved element's type is crossing the numeric /
	// measurement line in the editor — then the instruction the save needs
	// ('convertFrom' | 'convertTo'), its measure, and the resolved unit.
	// (Never for a column: the API refuses a conversion instruction on one, and
	// a retyped column is refused while entries exist instead.)
	const conversion = savedElement && !isColumn ? planConversion({ ...element, dataType }, savedElement) : null;

	// A type change rewrites the options in the same breath: the keys the new
	// type does not offer are DROPPED (elementOptionPolicy.pruneOptionsForType),
	// not merely hidden, so no stale configuration rides invisibly along in
	// the payload. Required, help, the form group and a driven rule survive
	// every change. The conversion instruction above is planned from the SAVED
	// element afterwards, so the number <-> measurement flow is untouched.
	//
	// Coming BACK to the type the element was SAVED as is the exception: the
	// element is where it started, so the configuration it started with is
	// restored from the saved copy for anything the trip away pruned. Without
	// this, measurement -> number -> measurement returns an element with no
	// `measure` — the card is then refused (400 invalid_options) and the
	// meaning of every stored base value has been thrown away by two clicks
	// that, together, changed nothing. Same for a choice list, a lookup card
	// and a measurement's allowed units / display rules. Whatever the author
	// has edited since wins over the saved value.
	const handleTypeChange = (nextType) => {
		if (!nextType || nextType === dataType) return;
		// The type being LEFT settles a date bound (or a range) that NAMES
		// another element: the sibling keeps its own type, so the link is
		// between two different date types now and the API would refuse it.
		// The saved copy is restored against ITS own type, so a round trip
		// back to where the element started restores the link too.
		const pruned = pruneOptionsForType(element?.options, nextType, dataType);
		const returning = savedElement && (savedElement.dataType || 'text-small') === nextType;
		const nextOptions = returning ? { ...pruneOptionsForType(savedElement.options, nextType, nextType), ...pruned } : pruned;
		// A currency element is never without a currency: a new one starts
		// in the collection's, else the author's own, else US dollars
		// (`newMoneyCode`, ruled L3), which the author narrows or changes in
		// the panel.
		if (nextType === 'currency' && !(Array.isArray(nextOptions.currencies) && nextOptions.currencies.length)) {
			nextOptions.currencies = [newMoneyCode || KEEPR_CURRENCY];
		}
		// New money refuses negative amounts (ruling D8): an element that was
		// not SAVED as currency starts with "No negative amounts" on, which the
		// author can turn off. The API writes the same for a new currency
		// element that does not say (keepr-api utils/elementDefaults.js); a
		// currency element already saved keeps what it has.
		if (nextType === 'currency' && nextOptions.nonNegative === undefined
			&& !(savedElement && (savedElement.dataType || 'text-small') === 'currency')) {
			nextOptions.nonNegative = true;
		}
		// A new file element starts with "Leave blank when duplicating" on
		// (owner ruling G5): most file elements hold evidence of one occasion,
		// and a copy carrying last month's photo would look complete and be
		// false. The author turns it off for a manual or a standard photo. An
		// element already SAVED as a file keeps what it has. A COLUMN of a
		// repeating group gets none (KPR-934, ruled by Zach 2026-10-17): the API
		// refuses `duplicate` on a column (400 `invalid_options`) - whether a copy
		// keeps a group's entries is the GROUP's own option.
		if (nextType === 'file' && nextOptions.duplicate === undefined && !isColumn
			&& !(savedElement && savedElement.dataType === 'file')) {
			nextOptions.duplicate = DUPLICATE_BLANK;
		}
		// A new repeating group starts with no columns; the author adds them (a
		// group with none is refused on save). One returning to the type it was
		// SAVED as already has the columns it was saved with, restored above.
		if (nextType === REPEATING_GROUP && !Array.isArray(nextOptions.columns)) nextOptions.columns = [];
		onChange(`${base}.options`, nextOptions);
		onChange(`${base}.dataType`, nextType);
	};

	// Once the type is chosen the name is the next thing to type, so the
	// caret goes there rather than leaving the author to find it.
	const hadTypeRef = useRef(!!dataType);
	useEffect(() => {
		if (dataType && !hadTypeRef.current) {
			const input = document.getElementById(`${base}.label.singular`);
			if (input && typeof input.focus === 'function') input.focus();
		}
		hadTypeRef.current = !!dataType;
	}, [dataType, base]);

	// Changing the type of a SAVED element does not rewrite what its items
	// already hold — the one exception is number <-> measurement / currency,
	// which has its own conversion control below and says so there. The
	// coercion matrix knows whether the old shape reads as the new one, so
	// the row says it plainly, as a NOTE rather than a refusal: an author
	// correcting a type that was wrong from the start is the common case.
	// Nothing is transformed on save (the API's `values_kept_unconverted`),
	// so the note never describes a transform — only that the values stay
	// as they are and some may not read as the new type. The count comes at
	// save time, from the change-preview.
	const savedType = savedElement ? (savedElement.dataType || 'text-small') : '';
	const typeChangeMatch = (!untyped && savedType && savedType !== dataType && !conversion)
		? classify(savedType, dataType, { sourceOptions: savedElement?.options, targetOptions: element?.options })
		: null;
	const typeChangeNote = (() => {
		if (!typeChangeMatch) return null;
		// A saved COLUMN: retyping it is refused while any item holds an entry
		// in the group (409 type_change_needs_empty { element, column }). Said
		// before the save, with the way through.
		if (isColumn) {
			return 'keepr refuses this change while any item holds entries in this group. To keep those values, add a new column and remove this one: the old values stay on the entries, hidden.';
		}
		// Owner ruling G8: a file, item lookup, user or location element
		// changes type only while no item holds a value — the API answers
		// 409 type_change_needs_empty — because those values mean nothing as
		// another type. Said before the save, in the type's words.
		if (typeChangeNeedsEmpty(savedType, dataType)) {
			return 'keepr refuses this change while any item holds a value here. To keep those values, add a new element instead.';
		}
		// Made an item lookup (KPR-1136): a value typed here cannot become a
		// link, so when items hold one keepr asks before the save, and the
		// only answer is to clear them.
		if (dataType === 'card-lookup') {
			return 'An item lookup holds only links someone chose. If items hold a value here, keepr asks before saving, and those values can only be cleared. To keep them, add a new element instead.';
		}
		// Short text <-> Choice (KPE-033): what happens to the values, in words.
		const choiceWords = choiceTypeChangeWords(savedElement, dataType);
		if (choiceWords) return choiceWords;
		if (typeChangeMatch.class === 'unmappable') {
			return `Existing values are kept as they are and may not read as ${typeMeta?.label || dataType}.`;
		}
		const lossy = (typeChangeMatch.transforms || []).map(transformInfo).find(info => info && info.lossy);
		return lossy
			? `Existing values are kept as they are, not converted. Some may not read as ${typeMeta?.label || dataType}.`
			: null;
	})();

	// The save gate's refusal, and the API's own 400, land on the row that
	// needs the answer. Both name the element by index: an untyped element
	// may not have a name yet.
	const typeErrorAlert = (error) => (error ? (
		<Alert
			className='card-def-element__type-error'
			type='error'
			showIcon
			message={error.code === 'invalid_element_type'
				? `${error.dataType ? `“${error.dataType}” is not a data type this platform knows. ` : ''}Choose a data type`
				: 'Choose a data type'}
		/>
	) : null);

	// "Choose a data type" is a demand, and a demand the author has already
	// met must stop being made: the web's own refusal is dropped the moment
	// the row has a type, without waiting for the next save. The API's
	// `invalid_element_type` stands until then — the web never sends a type
	// the platform does not know, so a refusal that reaches here is about a
	// payload this row cannot second-guess.
	const typeErrorApplies = saveError
		&& (saveError.code === 'invalid_element_type' || (saveError.code === 'data_type_required' && untyped));
	const typeError = typeErrorApplies
		&& (saveError.index === index || (saveError.index === undefined && saveError.element && saveError.element === elementName))
		? saveError
		: null;

	// The API refuses an option key the element's type does not take (400
	// `invalid_options` carrying `option`). The sub-editors show their own
	// `invalid_options` (a bad bound, an unknown currency), which carry no
	// `option`; this one is about the key itself, so it sits by the type.
	// A text rule's own refusal (PR 2: a maxLength past the ceiling, a
	// pattern the API refused — `reason` says why) also names its `option`,
	// but without the `dataType` an off-type refusal carries: it belongs
	// under its control in the Text panel, not by the type.
	const textRuleError = saveError && saveError.code === 'invalid_options'
		&& (saveError.option === 'pattern' || saveError.option === 'maxLength') && !saveError.dataType
		&& saveError.element && saveError.element === elementName
		? saveError
		: null;
	// A default the element's rules refuse (PR 4: 400 invalid_options
	// { option: 'defaultValue', reason }, no `dataType` — the off-type refusal
	// carries one) belongs under the Default value field, like a text rule's.
	const defaultValueError = saveError && saveError.code === 'invalid_options'
		&& saveError.option === 'defaultValue' && !saveError.dataType
		&& saveError.element && saveError.element === elementName
		? saveError
		: null;
	// (A repeating group's own options are said in its panel, beside the
	// settings they are about — RepeatingGroupOptions.)
	const offTypeOptionError = saveError && saveError.code === 'invalid_options'
		&& typeof saveError.option === 'string' && saveError.element && saveError.element === elementName
		&& !textRuleError && !defaultValueError
		&& !(dataType === REPEATING_GROUP && GROUP_PANEL_OPTIONS.includes(saveError.option))
		? saveError
		: null;
	// What the API says about a COLUMN the editor has no field for: another
	// group (`nested_repeating_group`), a type that cannot sit in a group
	// (`column_type_not_allowed`), a lookup outside the collection
	// (`lookup_out_of_scope`) — its sentence, on the column's row, under the
	// type. (A column's other refusals — its type, its options, its units —
	// land on the control that owns them, as they do on an element.)
	const columnRefusal = isColumn && saveError && ['nested_repeating_group', 'column_type_not_allowed', 'lookup_out_of_scope'].includes(saveError.code)
		&& saveError.element && saveError.element === elementName
		? saveError
		: null;

	// A type change the API refuses while items hold values (409
	// type_change_needs_empty { element, count }, ruling G8): the server's
	// own sentence, under the type it refused.
	const typeChangeRefusal = saveError && saveError.code === 'type_change_needs_empty'
		&& saveError.element && saveError.element === elementName
		? saveError
		: null;

	// Unique values turned on over values two items already share (409
	// duplicates_exist): read under the switch, with the items as badges.
	const duplicatesRefusal = saveError && saveError.code === 'duplicates_exist'
		&& saveError.element && saveError.element === elementName
		? saveError
		: null;
	// ...and over more items than one save checks (409 backfill_too_large
	// naming backfillUniqueClaims — a conversion's own refusal sits in its
	// unit field instead).
	const uniqueCapRefusal = saveError && saveError.code === 'backfill_too_large'
		&& saveError.element === elementName && /backfillUniqueClaims/.test(saveError.message || '')
		? saveError
		: null;

	// Driven-field summary: the modal owns the configuration, this row owns the
	// at-a-glance answer to "where does this value come from?" plus the two
	// verbs (reconfigure, stop driving). Clearing writes `undefined` rather than
	// null so the key serializes away entirely — the API materializes managed
	// rules from the keys that are PRESENT on save, and a null would read as a
	// config rather than an absence.
	const drivenFrom = element?.options?.drivenFrom || null;
	const isFormula = isFormulaDriven(element);
	// The formula in words ("Volume A + Volume B") reads the card's merged
	// elements: the form's own (unsaved) elements plus the parent chain.
	const siblingElementsProp = props.siblingElements;
	const siblingElements = useMemo(() => siblingElementsProp || [], [siblingElementsProp]);
	// A COLUMN's formula (KPR-686) reads its entry's columns (its siblings here)
	// and the ITEM's elements — the card's own, which the group's row hands
	// down (`column.cardElements`).
	const columnCardElementsProp = column?.cardElements;
	const formulaOwnElements = useMemo(() => (isColumn ? (columnCardElementsProp || []) : siblingElements), [isColumn, columnCardElementsProp, siblingElements]);
	const cachedCardsById = cardDefinitionsCache?.cardsById;
	const cachedElementSetsById = cardDefinitionsCache?.elementSetsById;
	const formulaResolver = useMemo(() => formulaElementResolver({ cardsById: cachedCardsById, elementSetsById: cachedElementSetsById }, collectionCards), [cachedCardsById, cachedElementSetsById, collectionCards]);
	const formulaMergedElements = useMemo(() => (isFormula ? mergedElementsForCard({
		ownElements: [...formulaOwnElements, ...extraElements],
		parentCardId: targetCard?.parentCardId,
		getElementsForCard: formulaResolver,
	}) : []), [isFormula, formulaOwnElements, extraElements, targetCard?.parentCardId, formulaResolver]);
	// Every element a DATE option may point at: this card's own (under the
	// names the API will give them, so an unsaved sibling can be chosen the
	// moment it has a label) plus everything it inherits. The same merge the
	// formula editor does, for the same reason — an element bound and a range
	// end name an element on the card OR an ancestor.
	const isDate = isDateType(dataType);
	const ownNamedElements = useMemo(() => {
		const names = effectiveElementNames(siblingElements);
		return siblingElements.map((el, i) => (el ? { ...el, name: names[i] } : null)).filter(Boolean);
	}, [siblingElements]);
	const dateOptionElements = useMemo(() => {
		// (A column names no other element: DateOptions `inGroup`.)
		if (!isDate || isColumn) return [];
		const merged = mergedElementsForCard({
			ownElements: [...ownNamedElements, ...extraElements],
			parentCardId: targetCard?.parentCardId,
			getElementsForCard: formulaResolver,
		});
		return merged.length ? merged : ownNamedElements;
	}, [isDate, isColumn, ownNamedElements, extraElements, targetCard?.parentCardId, formulaResolver]);

	// The elements a SCAN into this lookup may be matched against — the
	// TARGET card's, because a scan looks the other record up. Without this,
	// `options.scan.match` was an API option no screen could write, and the
	// only thing a scan could ever be was the record's own id.
	// Nothing here costs anything on an element that is not a scanning
	// lookup, which is almost all of them: the whole pair short-circuits on
	// `scanning` rather than walking cards for every row of every card.
	const scanning = dataType === 'card-lookup' && optionKeys.includes('scan') && !!element.options?.scan;
	const scanTargetId = scanning ? normalizeId(element.options?.lookupCardId) : null;
	const scanTargetCard = useMemo(
		() => (scanTargetId ? (collectionCards || []).find(card => normalizeId(card?._id) === scanTargetId) || null : null),
		[scanTargetId, collectionCards]
	);
	const scanTargetElements = useMemo(() => (scanTargetId ? mergedElementsForCard({
		ownElements: formulaResolver(scanTargetId) || [],
		parentCardId: scanTargetCard?.parentCardId,
		getElementsForCard: formulaResolver,
	}) : []), [scanTargetId, scanTargetCard?.parentCardId, formulaResolver]);

	// The account link already carried by the card's EFFECTIVE list (own,
	// inherited, element sets') by an element other than this one: the API
	// refuses a second (400 invalid_options), so the switch is greyed here
	// with the holder's name (docs/plans/element-sets-and-me.md).
	const isUserType = dataType === 'user';
	const identityHolder = useMemo(() => {
		if (!isUserType || isColumn) return null;
		const merged = mergedElementsForCard({
			ownElements: [...ownNamedElements, ...extraElements],
			parentCardId: targetCard?.parentCardId,
			getElementsForCard: formulaResolver,
		});
		return (merged.length ? merged : ownNamedElements)
			.find(el => el && el.name !== elementName && isIdentityElement(el)) || null;
	}, [isUserType, isColumn, ownNamedElements, extraElements, targetCard?.parentCardId, formulaResolver, elementName]);

	// "Create an end element": a sibling of the same type, named after this
	// one, inserted right after this row and LINKED in the same change — the
	// card author asked for a range, not for two chores.
	const handleCreateEndElement = useCallback(() => {
		if (!onInsertElementAfter) return;
		const singular = `${(element?.label?.singular || 'Date').trim()} end`;
		const endElement = {
			name: '',
			dataType,
			label: { singular, plural: pluralize(singular) },
			options: {},
		};
		onInsertElementAfter(index, endElement, (list) => {
			// The name the API will give the new element, read off the list it
			// is now part of (a duplicate label is suffixed there, not here).
			const names = effectiveElementNames(list);
			return list.map((el, i) => (
				i === index ? { ...el, options: { ...(el?.options || {}), rangeEnd: names[index + 1] } } : el
			));
		});
	}, [onInsertElementAfter, element?.label?.singular, dataType, index]);

	// A rule driven from a card, in words: the dialog's own sentence (KPR-950),
	// labels only, never a stored name, a `$` system name or the filter's text.
	const cachedGetElementsForCard = cardDefinitionsCache?.getElementsForCard;
	const drivenRuleWords = useMemo(() => (drivenFrom && !isFormula ? describeStoredDrivenRule(drivenFrom, {
		cards: collectionCards,
		targetCard,
		getElementsForCard: cachedGetElementsForCard || formulaResolver,
	}) : ''), [drivenFrom, isFormula, collectionCards, targetCard, cachedGetElementsForCard, formulaResolver]);
	const drivenSummary = !drivenFrom
		? ''
		: isFormula
			? `Calculated: ${formulaSummary(drivenFrom.formula || '', formulaMergedElements, formulaResolver, isColumn ? { columns: ownNamedElements } : {})}`
			: drivenRuleWords;
	const openDrivenConfig = () => (isFormula ? setFormulaModalOpen(true) : setDrivenModalOpen(true));

	// The Choices editor and Use choice icons belong to a Choice, and only a
	// Choice (KPE-033): a list another type carries is inert — kept for a
	// change back, never shown or edited (the kept-list line below says it is
	// there).
	const showChoices = optionKeys.includes('choices');
	const showChoiceIcons = optionKeys.includes('preferChoiceIcons');
	// The list a save keeps on a non-Choice element is the SAVED one, exactly
	// (withInertChoicesAsStored): counted from there, so the line never names
	// a list the save would drop (a new element's, or one edited and then
	// retyped). A repeating group carries none.
	const keptChoiceCount = (dataType && dataType !== 'choice' && dataType !== REPEATING_GROUP && Array.isArray(savedElement?.options?.choices))
		? savedElement.options.choices.length
		: 0;

	// Which way the value is driven, as the Segmented reads it. It follows the
	// SAVED config, so picking Driven or Calculated only moves it once the
	// modal saves something; cancelling the modal leaves it where it was.
	const drivenMode = !drivenFrom ? 'none' : (isFormula ? 'formula' : 'driven');
	const handleDrivenMode = (mode) => {
		if (mode === drivenMode) return;
		if (mode === 'none') {
			onChange(`${base}.options.drivenFrom`, undefined);
			return;
		}
		if (mode === 'formula') setFormulaModalOpen(true);
		else setDrivenModalOpen(true);
	};

	// The boolean options, in display order (config/elementOptionPolicy.js
	// ELEMENT_OPTION_TOGGLES — the same names and one-liners the multi add
	// editor's inputs use). Only the ones the type's policy offers render, as
	// one divided run of toggle rows after the other option inputs; the
	// choice-icons row also stays for legacy elements that still hold choices.
	// 'allowMultiple' is offered by two families that mean different things by
	// it (a list of linked items, a list of dates — see elementOptionPolicy),
	// and a date's belongs to the Dates panel with the rules it interacts
	// with: it is refused where this element is one end of a span, or where
	// another element's bound names it. One switch, in one place.
	// A user element's two options live together in the User panel
	// (UserOptions), because one refuses the other.
	// 'defaultToMe' is offered only where it can fire: a lookup whose card
	// (or an ancestor, or a card under it) carries an account link. It stays
	// visible while it is ON regardless, so an author who moved the target
	// to a card nobody is linked through can see it and turn it off — the
	// save would be refused otherwise (400 default_to_me_unavailable).
	const lookupTargetId = dataType === 'card-lookup' ? normalizeId(element.options?.lookupCardId) : '';
	const lookupTargetHasIdentity = useMemo(() => (
		lookupTargetId ? lookupFamilyHasIdentity({ cardsById: cachedCardsById, elementSetsById: cachedElementSetsById, cardId: lookupTargetId }) : false
	), [lookupTargetId, cachedCardsById, cachedElementSetsById]);
	const defaultToMeOn = element.options?.defaultToMe === true;
	// The Default value field (DefaultValueField): offered by the policy, and
	// withheld beside a driven or unique value (the API refuses the pairing;
	// numbering is already filtered out of optionKeys) — kept while a default
	// is set, so a stored one can always be cleared.
	const defaultIsSet = explicitElementDefault({ ...element, dataType }) !== undefined;
	const showDefaultValue = optionKeys.includes('defaultValue') && dataType !== 'measurement'
		&& (defaultIsSet || !(element.options?.drivenFrom || element.options?.unique === true));
	const toggleVisible = {
		preferChoiceIcons: showChoiceIcons,
		allowMultiple: !isDate && !isUserType && optionKeys.includes('allowMultiple'),
		defaultToMe: optionKeys.includes('defaultToMe') && (lookupTargetHasIdentity || defaultToMeOn),
		// A currency's "No negative amounts" lives in its own panel
		// (CurrencyOptions), beside the bounds it interacts with.
		nonNegative: dataType !== 'currency' && optionKeys.includes('nonNegative'),
		// Unique values are refused beside a driven value (and a numbered one,
		// which the policy already withholds): hidden then, but kept visible
		// while ON so a stored switch can always be turned off.
		unique: element.options?.unique === true
			|| (optionKeys.includes('unique') && !element.options?.drivenFrom && !element.options?.sequence),
		// PR 7: an identifier (padding, a prefix, numbering) is never a
		// percentage and never grouped — the API refuses the pair — so the two
		// switches step aside beside one, but stay while ON so they can be
		// turned off.
		percent: element.options?.percent === true
			|| (optionKeys.includes('percent') && percentAvailable(element.options)),
		// KPR-1025: not beside an identifier, a percent or decimals 0 or 1.
		fractions: element.options?.fractions === true
			|| (optionKeys.includes('fractions') && fractionsAvailable(element.options)),
		thousands: element.options?.thousands === true
			|| (optionKeys.includes('thousands') && !isIdentifierNumber(element.options)),
		// KPR-1396: Manual order, only on an element free of what it cannot
		// live beside (the API refuses each pairing); kept while ON.
		rank: element.options?.rank === true
			|| (optionKeys.includes('rank') && rankAvailable(element.options)),
	};
	const toggleRows = ELEMENT_OPTION_TOGGLES
		.filter(row => (row.key in toggleVisible ? toggleVisible[row.key] : optionKeys.includes(row.key)))
		.map(row => ({ ...row, description: toggleDescription(row, dataType), value: !!element.options?.[row.key] }));
	// "Allow multiple" on a Choice changes the SHAPE of its default (a list of
	// choices, or one), so the switch converts the default in the same change:
	// on, one default becomes a list of one; off, a list keeps its first entry
	// in the choices' order (utils/choiceValues.js). Saving the old shape would
	// be refused (400 invalid_options on the default). A date's default is one
	// { kind, value } either way, and an item lookup or a user takes none.
	// The switch changes the shape of every STORED value too (owner ruling
	// B16), on every list type — a Choice, a date, a date and time, an item
	// lookup, a user (utils/shapeChange.js): on a saved element whose items
	// hold values in the old shape, the switch does not move until the author
	// answers the dialog, and the answer rides on the element as
	// `options.convertValues` until the card is saved. The date's switch lives
	// in the Dates panel and the user's in the User panel; both reach here
	// through handleMultiAwareChange.
	const [shapeDialog, setShapeDialog] = useState(null);
	const multiPath = `${base}.options.allowMultiple`;
	const isListType = SHAPE_LIST_TYPES.includes(dataType);
	// One write of the whole options object, so a switch turned back leaves no
	// `convertValues: undefined` behind to keep the draft dirty. A Choice
	// stores the switch as true/false (as it always has); the panels' types
	// drop the key when it is off, as their own switches always did.
	const applyMultiToggle = useCallback((checked, instruction) => {
		const options = { ...(element?.options || {}) };
		if (dataType === 'choice') {
			options.allowMultiple = checked;
			if (explicitElementDefault({ ...element, dataType }) !== undefined) {
				options.defaultValue = defaultForMultiToggle({ ...element, dataType }, checked);
			}
		} else if (checked) options.allowMultiple = true;
		else delete options.allowMultiple;
		// The flip's answer joins a "Photos only" answer already given (one
		// save, `['first', 'detach']`); a switch turned back drops its own.
		const answers = mergeConversionAnswers(element?.options?.[SHAPE_INSTRUCTION_KEY], instruction, { ...element, dataType, options }, savedElement);
		if (answers !== undefined) options[SHAPE_INSTRUCTION_KEY] = answers;
		else if (instruction && !savedElement) options[SHAPE_INSTRUCTION_KEY] = instruction;
		else delete options[SHAPE_INSTRUCTION_KEY];
		onChange(`${base}.options`, options);
	}, [onChange, dataType, base, element, savedElement]);
	const handleToggleChange = useCallback(async (path, checked) => {
		if (!isListType || path !== multiPath) {
			onChange(path, checked);
			return;
		}
		const nextDefault = dataType === 'choice' && explicitElementDefault({ ...element, dataType }) !== undefined
			? defaultForMultiToggle({ ...element, dataType }, !!checked)
			: undefined;
		const options = { ...(element?.options || {}), allowMultiple: !!checked, ...(nextDefault !== undefined ? { defaultValue: nextDefault } : {}) };
		if (!checked && dataType !== 'choice') delete options.allowMultiple;
		const next = { ...element, dataType, options };
		if (onCheckShapeChange && shapeDirection(next, savedElement)) {
			const answer = await onCheckShapeChange(elementName, next);
			if (answer?.refusal) {
				setShapeDialog({ refusal: answer.refusal, checked: !!checked });
				return;
			}
		}
		applyMultiToggle(!!checked, null);
	}, [onChange, multiPath, isListType, dataType, element, savedElement, elementName, onCheckShapeChange, applyMultiToggle]);
	// A file element's "Photos only" (PR 10b): on a saved element that took
	// any file, ask the change-preview first, as the multi switch does — files
	// that are not photos need the author's answer (`convertValues: 'detach'`)
	// before the change can save. Turning it back off drops the answer.
	const handleFileOptionChange = useCallback(async (path, value) => {
		if (path !== `${base}.options.accept`) return onChange(path, value);
		const options = { ...(element?.options || {}) };
		if (value) options.accept = value; else delete options.accept;
		// Its own answer goes (asked again below); a flip's answer stays.
		const kept = mergeConversionAnswers(conversionAnswers(options[SHAPE_INSTRUCTION_KEY]).filter(answer => answer !== FILE_DETACH), null, { ...element, dataType, options }, savedElement);
		if (kept === undefined) delete options[SHAPE_INSTRUCTION_KEY]; else options[SHAPE_INSTRUCTION_KEY] = kept;
		const next = { ...element, dataType, options };
		if (onCheckShapeChange && fileAcceptTightened(next, savedElement) && !shapeDirection(next, savedElement)) {
			const answer = await onCheckShapeChange(elementName, next);
			if (answer?.refusal) {
				setShapeDialog({ refusal: answer.refusal, detach: true, options });
				return undefined;
			}
		}
		return onChange(`${base}.options`, options);
	}, [onChange, base, element, dataType, savedElement, elementName, onCheckShapeChange]);
	// The Dates and User panels' onChange: their "Allow multiple" switch asks
	// first like the toggle row's; every other path passes straight through.
	const handleMultiAwareChange = useCallback((path, value) => {
		if (path === multiPath) return handleToggleChange(path, !!value);
		return onChange(path, value);
	}, [multiPath, handleToggleChange, onChange]);
	// The author's answer to the dialog, said on the element until the save
	// sends it — only while the element still flips the way it was given for
	// (utils/conversions.js withConversionInstructions drops a stale one).
	// One answer or two (`['first', 'detach']`, the 10b contract).
	const pendingAnswers = conversionAnswers(element?.options?.[SHAPE_INSTRUCTION_KEY]);
	const pendingDirection = pendingAnswers.length ? shapeDirection({ ...element, dataType }, savedElement) : null;
	// A file element's 'detach' answer (PR 10b), said under its File panel.
	const pendingDetach = pendingAnswers.includes(FILE_DETACH)
		&& fileAcceptTightened({ ...element, dataType }, savedElement);
	const pendingShape = pendingDirection ? (pendingAnswers.find(answer => answer === 'wrap' || answer === 'first') || null) : null;
	// Said in the type's words: "keeps only its earliest date", "its single
	// linked item becomes a list of one" (utils/shapeChange.js shapeWords).
	const pendingWords = pendingDirection ? shapeWords({ direction: pendingDirection, from: savedElement?.dataType, to: dataType }) : null;
	const pendingShapeNote = pendingShape ? (
		<Alert
			className='card-def-element__type-note'
			type={pendingShape === 'first' ? 'warning' : 'info'}
			showIcon
			data-testid='multi-convert-note'
			message={pendingShape === 'first'
				? (pendingWords.type === 'file'
					? 'When you save, each item keeps only its first file; the others move to its Other attachments.'
					: `When you save, each item keeps only its ${pendingWords.keeps} ${pendingWords.one}.`)
				: `When you save, each item's single ${pendingWords.one} becomes a list of one.`}
		/>
	) : null;
	// A date's and a user's switch live in their own panels, so the note sits
	// under the panel there; everyone else's switch is a toggle row.
	const multiInPanel = isDate || isUserType;
	// "Leave blank when duplicating" (options.duplicate: 'blank') belongs to
	// every data type, so no policy list offers it; it is a STRING on the wire
	// (the API keeps room for a later transform), which is why it is not one
	// of the boolean toggle rows above. Withheld where no copy ever carries
	// the value anyway — a driven or server-numbered element, a "Me too"
	// count (KPR-390) — but kept visible while it is on, so it can always be
	// turned off.
	const duplicateBlankOn = element.options?.duplicate === DUPLICATE_BLANK;
	const offersDuplicateBlank = !isColumn && (duplicateBlankOn || !(element.options?.drivenFrom || element.options?.sequence || dataType === 'confirmations'));
	const handleDuplicateBlankChange = useCallback(
		(name, checked) => onChange(name, checked ? DUPLICATE_BLANK : null),
		[onChange]
	);
	// "Tell followers when this changes" (options.followable, KPR-391). The
	// switch shows what the element DOES — a single choice is followed with
	// nothing stored — and the change writes only a departure from that
	// default; turning it back to the default clears the key (null), so a
	// card is not filled with `followable: true` on every status.
	const offersFollowable = optionKeys.includes('followable');
	const followableOn = isFollowableElement(element);
	const handleFollowableChange = useCallback(
		(name, checked) => onChange(name, checked === followableByDefault(element) ? null : checked),
		[onChange, element]
	);

	// The panel's fields sit on a two-column grid inside the form's 660px
	// measure: the two labels share a row, as do the default value and the
	// form group; the help text and the type control run full width.
	// The lookup picker (utils/lookupScope.js): the scope, plus — for THIS
	// element only — the out-of-scope card it is already saved with. The API
	// leaves a stored target alone and refuses one set anew, so offering it to
	// any other element would be offering a save that fails.
	const savedLookupId = savedElement?.dataType === 'card-lookup' ? normalizeId(savedElement?.options?.lookupCardId) : '';
	// (A column's picker is the group panel's: only the cards the card's own
	// collection owns — a column looks up nothing else, KPR-675.)
	const lookupOptions = useMemo(
		() => (isColumn && columnCardOptions ? columnCardOptions : cardOptions).filter(opt => !opt.outOfScope || opt.value === savedLookupId),
		[cardOptions, columnCardOptions, isColumn, savedLookupId]
	);
	const lookupOutOfScope = lookupOptions.some(opt => opt.outOfScope && opt.value === normalizeId(element.options?.lookupCardId));
	// A picker filter names the TARGET card's elements, so a new target
	// starts it over: the old conditions (and Strict with them) would only
	// be refused on save, naming elements the new card does not have.
	const hasLookupFilter = !!element.options?.filter;
	const handleLookupTargetChange = useCallback((name, value) => {
		onChange(name, value);
		if (hasLookupFilter && normalizeId(value) !== lookupTargetId) {
			onChange(`${base}.options.filter`, null);
			onChange(`${base}.options.strict`, false);
		}
	}, [onChange, hasLookupFilter, lookupTargetId, base]);

	// One column of this group, edited by this same component hung from the
	// column's path (RepeatingGroupOptions hands over the pieces that are
	// the group's to know: its key, its translated refusal, its reorder doors).
	const renderColumn = (c) => (
		<CardDefElement
			key={c.key}
			element={c.column}
			index={c.columnIndex}
			basePath={c.path}
			column={{
				group: element?.label?.singular || elementName,
				onMove: c.onMove,
				canMoveUp: c.canMoveUp,
				canMoveDown: c.canMoveDown,
				// A calculated column (KPR-686) reads its entry and its ITEM: the
				// group (under the name it saves as) and the card's own elements.
				groupElement: { ...element, name: elementName },
				cardElements: siblingElements,
			}}
			onChange={onChange}
			onDeleteElement={c.onRemove}
			dragHandleProps={c.dragHandleProps}
			panelKey={c.key}
			isOpen={c.isOpen}
			onToggleOpen={c.onToggleOpen}
			cardOptions={cardOptions}
			columnCardOptions={columnCardOptions}
			typesExcluded={columnTypesExcluded}
			cardOptionsLoading={cardOptionsLoading}
			targetCard={targetCard}
			collectionCards={collectionCards}
			siblingElements={c.siblings}
			saveError={c.error}
			elementName={c.elementName}
			savedElement={c.savedColumn}
			// Who can see or change this column (KPR-688): its saved column's
			// own rule, under the group's.
			access={access && access.columnFor ? access.columnFor(c.column) : null}
			filesOffCollections={filesOffCollections}
			newMoneyCode={newMoneyCode}
			editingLocked={editingLocked}
		/>
	);

	const elementDetail = <>
		<div className='card-def-element__grid'>
			{/* A card lookup's target card, first in the row: the next thing
			    to decide once the type is chosen, before the labels. */}
			{optionKeys.includes('lookupCardId') ? (
				<div className='card-def-element__field card-def-element__field--full'>
					<InputSelect
						elementName={`${base}.options.lookupCardId`}
						elementLabel='Lookup card type'
						value={element.options?.lookupCardId}
						onChange={handleLookupTargetChange}
						options={lookupOptions}
						placeholder={cardOptionsLoading ? 'Loading card types...' : 'Choose a card type'}
						allowClear={false}
					/>
					{lookupOutOfScope ? (
						<div className='form-panel__hint'>
							This card is outside the cards this element can look up. It keeps working as saved; once changed, it can&rsquo;t be chosen again.
						</div>
					) : null}
					{/* The picker filter: about the card chosen just above,
					    so it sits right under it (LookupFilterOptions). */}
					{optionKeys.includes('filter') ? (
						<LookupFilterOptions
							element={element}
							index={index}
							onChange={onChange}
							cardsById={cachedCardsById}
							getElementsForCard={cardDefinitionsCache?.getElementsForCard || null}
							disabled={editingLocked}
						/>
					) : null}
				</div>
			) : null}
			<div className={`card-def-element__field${isColumn ? ' card-def-element__field--full' : ''}`}>
				<InputText
					elementName={`${base}.label.singular`}
					elementLabel={isColumn ? 'Column name' : 'Label (singular)'}
					value={element?.label?.singular || ''}
					onChange={handleSingularChange}
					required={true}
				/>
			</div>
			{/* A column is named by one word. */}
			{isColumn ? null : (
				<div className='card-def-element__field'>
					<InputText
						elementName={`${base}.label.plural`}
						elementLabel='Label (plural)'
						value={element?.label?.plural || ''}
						onChange={onChange}
						required={true}
					/>
				</div>
			)}
			{/* Every data type: the words a reader gets on this field's label — a
			    hover hint on the entry form and the read view, and a "?" that opens
			    the same text for touch screens (FieldHelp). Blank = no affordance. */}
			<div className='card-def-element__field card-def-element__field--full'>
				<InputTextArea
					elementName={`${base}.options.help`}
					elementLabel='Help text'
					value={element.options?.help || ''}
					onChange={onChange}
					placeholder='Optional — what to enter, how to measure it, where to find it'
				/>
			</div>
		</div>

		<div className='micro-label card-def-element__section'>Options</div>
		<div className='card-def-element__grid'>
			{/* The type is settled in the gallery above before anything else
			    exists; changing it afterwards is an option like the rest —
			    and the one that decides which of the others are here at all. */}
			<div className='card-def-element__field card-def-element__field--full card-def-element__type'>
				<label className='d-block' htmlFor={`${base}.dataType`}>Change type…</label>
				<DataTypePicker
					variant='select'
					id={`${base}.dataType`}
					ariaLabel='Change type'
					value={dataType}
					onChange={handleTypeChange}
					exclude={typesWithheld}
				/>
				{typeWithheldNote}
				{/* The ceiling every write path holds a text-shaped value to
				    (utils/elementFormat/text.js), beside the type that sets it
				    — and the extended one once "Allow longer text" is on. */}
				{lengthLimitText(dataType, element.options) ? (
					<div className='form-panel__hint card-def-element__length-limit'>{lengthLimitText(dataType, element.options)}</div>
				) : null}
				{/* A "Me too" element has no options of its own (KPR-390): its
				    label IS the button, so that is the one thing to say here. */}
				{dataType === 'confirmations' ? (
					<div className='form-panel__hint card-def-element__confirm-note' data-testid='confirmations-note'>
						The button on each item says this element&rsquo;s label — &ldquo;{confirmationsLabel(element) || 'Me too'}&rdquo;. Each person adds or takes back their own; only managers see who.
					</div>
				) : null}
				{typeErrorAlert(typeError)}
				{offTypeOptionError ? (
					<Alert
						className='card-def-element__option-error'
						type='error'
						showIcon
						message={offTypeOptionError.message || `A ${typeMeta?.label || dataType} element has no option ${offTypeOptionError.option}.`}
					/>
				) : null}
				{columnRefusal ? (
					<Alert
						className='card-def-element__option-error'
						type='error'
						showIcon
						data-testid='column-refusal'
						message={columnRefusal.message || 'This column was refused.'}
					/>
				) : null}
				{typeChangeRefusal ? (
					<Alert
						className='card-def-element__option-error'
						type='error'
						showIcon
						data-testid='type-change-needs-empty'
						message={typeChangeRefusal.message || 'Items hold values here, so the type cannot change. Add a new element instead.'}
					/>
				) : null}
				{typeChangeNote ? (
					<Alert
						className='card-def-element__type-note'
						type='info'
						showIcon
						message={`${getDataTypeMeta(savedType)?.label || savedType} → ${typeMeta?.label || dataType}`}
						description={typeChangeNote}
					/>
				) : null}
				{keptChoiceCount ? (
					<div className='form-panel__hint card-def-element__kept-choices' data-testid='kept-choices-note'>
						{keptChoiceCount === 1
							? 'Its 1 choice is kept, unused, for a change back to Choice.'
							: `Its ${keptChoiceCount} choices are kept, unused, for a change back to Choice.`}
					</div>
				) : null}
			</div>
			{/* The value a new item starts with (DefaultValueField, PR 4), entered
			    in the element's own input. A measurement's default is entered with
			    its unit inside its own sub-editor below. Withheld beside a driven
			    or unique value (the API refuses the pairing) — but kept while one
			    is set, so it can always be cleared. */}
			{showDefaultValue ? (
				<div className={`card-def-element__field${['date', 'date-time', 'text-large', 'currency'].includes(dataType) ? ' card-def-element__field--full' : ''}`}>
					<DefaultValueField
						element={{ ...element, dataType, name: elementName }}
						idPrefix={`${base}.options`}
						onPatch={partial => Object.entries(partial).forEach(([key, value]) => onChange(`${base}.options.${key}`, value))}
						error={defaultValueError}
					/>
				</div>
			) : null}
			{/* A yes/no element's two states in the card author's own words
			    ("Paid" / "Unpaid"): the switch spells the current one out on
			    the form, and every display surface prints them instead of
			    true/false. Blank = Yes / No. */}
			{optionKeys.includes('trueLabel') ? (
				<div className='card-def-element__field'>
					<InputText
						elementName={`${base}.options.trueLabel`}
						elementLabel='Label for yes'
						value={element.options?.trueLabel || ''}
						onChange={onChange}
						placeholder='Yes'
					/>
				</div>
			) : null}
			{optionKeys.includes('falseLabel') ? (
				<div className='card-def-element__field'>
					<InputText
						elementName={`${base}.options.falseLabel`}
						elementLabel='Label for no'
						value={element.options?.falseLabel || ''}
						onChange={onChange}
						placeholder='No'
					/>
				</div>
			) : null}
			{/* Every data type: which panel the entry form groups this field
			    into (e.g. "When & who"). Blank = the default panel. */}
			{isColumn ? null : (
				<div className='card-def-element__field'>
					<InputText
						elementName={`${base}.options.group`}
						elementLabel='Form group'
						value={element.options?.group || ''}
						onChange={onChange}
						placeholder='e.g. When & who (blank = default panel)'
					/>
				</div>
			)}
			{/* A saved measurement element becoming a number: the (lossy) unit
			    its stored values are rewritten in. Absent for new elements and
			    unchanged types. */}
			{optionKeys.includes('convertTo') && conversion?.key === 'convertTo' ? (
				<div className='card-def-element__field card-def-element__field--full'>
					<ConversionUnitField
						index={index}
						elementName={elementName}
						plan={conversion}
						onChange={onChange}
						saveError={saveError}
					/>
				</div>
			) : null}

			{optionKeys.includes('drivenFrom') ? (
				<div className='card-def-element__field card-def-element__field--full card-def-element__driven mb-2'>
					<label className='d-block'>Driven value</label>
					{/* One joined control for the three modes. Driven and Calculated
					    open their configuration modal; None stops driving. A COLUMN
					    (KPR-686) is calculated over its entry and its item, never
					    driven from another card: Calculated and None alone. */}
					<Segmented
						aria-label='Driven value'
						value={drivenMode}
						onChange={handleDrivenMode}
						options={isColumn ? [
							{ label: 'Calculated', value: 'formula' },
							{ label: 'None', value: 'none' },
						] : [
							{ label: 'Driven from a card', value: 'driven' },
							{ label: 'Calculated', value: 'formula' },
							{ label: 'None', value: 'none' },
						]}
					/>
					{drivenFrom ? (
						<div className='card-def-element__driven-summary'>
							<span className='card-def-element__driven-text'>{drivenSummary}</span>
							<Button
								size='small'
								icon={<SLIDERS />}
								aria-label='Configure driven value'
								title='Configure driven value'
								onClick={openDrivenConfig}
							>
								Configure…
							</Button>
						</div>
					) : null}
					<div className='form-panel__hint'>
						{isColumn
							? 'A calculated column is worked out for every entry from the entry’s other columns and the item’s own fields, never typed. A column that already holds values cannot become calculated: add a new column for it.'
							: 'A driven value is filled in by the platform, never typed: taken from related records, or worked out with a formula.'}
					</div>
				</div>
			) : null}
		</div>

		{toggleRows.length || offersDuplicateBlank || offersFollowable ? (
			<div className='card-def-element__toggles'>
				{toggleRows.map((row, i) => (
					<InputSwitch
						key={row.key}
						elementName={`${base}.options.${row.key}`}
						elementLabel={row.label}
						description={row.description}
						value={row.value}
						onChange={handleToggleChange}
						first={i === 0}
					/>
				))}
				{multiInPanel ? null : pendingShapeNote}
				{duplicatesRefusal ? (
					<UniqueDuplicatesAlert refusal={duplicatesRefusal} cardsById={cachedCardsById} />
				) : null}
				{uniqueCapRefusal ? (
					<Alert
						className='card-def-element__option-error card-def-element__duplicates'
						type='error'
						showIcon
						message={`${Number(uniqueCapRefusal.count || 0).toLocaleString('en-US')} items hold a value — more than one save checks (${Number(uniqueCapRefusal.cap || 0).toLocaleString('en-US')}). An administrator can turn this on with the backfillUniqueClaims maintenance task.`}
					/>
				) : null}
				{offersDuplicateBlank ? (
					<InputSwitch
						key='duplicate'
						elementName={`${base}.options.duplicate`}
						elementLabel={DUPLICATE_BLANK_TOGGLE.label}
						description={DUPLICATE_BLANK_TOGGLE.description}
						value={element.options?.duplicate === DUPLICATE_BLANK}
						onChange={handleDuplicateBlankChange}
						first={!toggleRows.length}
					/>
				) : null}
				{offersFollowable ? (
					<InputSwitch
						key='followable'
						elementName={`${base}.options.followable`}
						elementLabel={FOLLOWABLE_TOGGLE.label}
						description={FOLLOWABLE_TOGGLE.description}
						value={followableOn}
						onChange={handleFollowableChange}
						first={!toggleRows.length && !offersDuplicateBlank}
					/>
				) : null}
			</div>
		) : null}

		{/* A repeating group: how many entries, and its columns — each edited by
		    this very component, hung from the column's own path. */}
		{dataType === REPEATING_GROUP ? (
			<RepeatingGroupOptions
				element={element}
				index={index}
				basePath={base}
				elementName={elementName}
				savedElement={savedElement}
				onChange={onChange}
				saveError={rawSaveError}
				disabled={editingLocked}
				renderColumn={renderColumn}
			/>
		) : null}

		{defaultToMeOn && optionKeys.includes('defaultToMe') && !lookupTargetHasIdentity ? (
			<div className='form-panel__hint' data-testid='default-to-me-unavailable'>
				No record of this lookup card can belong to anyone yet — give the card a user element marked as its account link, or turn this off.
			</div>
		) : null}

		{/* The half of a scan that decides what the decoded string IS, right
		    under the switch that turns the camera on. */}
		{scanning ? (
			<ScanOptions
				element={element}
				index={index}
				basePath={base}
				targetElements={scanTargetElements}
				targetLabel={scanTargetCard?.label?.singular || scanTargetCard?.name || 'the looked-up record'}
				onChange={onChange}
			/>
		) : null}

		{/* The conditional half of Required, right under the switch it
		    qualifies. */}
		{optionKeys.includes('required') && !isColumn ? (
			<RequiredWhenOptions
				element={element}
				elements={siblingElements}
				index={index}
				onChange={onChange}
				disabled={editingLocked}
			/>
		) : null}

		{optionKeys.includes('decimals') ? (
			<NumberOptions
				index={index}
				basePath={base}
				element={{ ...element, dataType, name: elementName }}
				onChange={onChange}
			/>
		) : null}

		{/* Server numbering, under the number options it configures the
		    display half of. */}
		{optionKeys.includes('sequence') ? (
			<SequenceOptions
				index={index}
				element={{ ...element, dataType, name: elementName }}
				onChange={onChange}
				disabled={editingLocked}
			/>
		) : null}

		{/* A text element's rules (PR 2): its own maximum length, and a
		    short text's case and pattern; "Trim spaces" is a toggle row
		    above. A list a short text carries is inert (KPE-033): the
		    rules are offered whatever it holds. */}
		{optionKeys.includes('maxLength') ? (
			<TextOptions
				idPrefix={`${base}.options`}
				dataType={dataType}
				options={element.options || {}}
				onPatch={partial => Object.entries(partial).forEach(([key, value]) => onChange(`${base}.options.${key}`, value))}
				error={textRuleError}
			/>
		) : null}

		{/* A rating's scale; its half stars are a toggle row below. */}
		{optionKeys.includes('allowHalf') ? (
			<RatingOptions
				index={index}
				basePath={base}
				element={{ ...element, dataType, name: elementName }}
				onChange={onChange}
			/>
		) : null}

		{optionKeys.includes('country') ? (
			<PhoneOptions
				index={index}
				basePath={base}
				element={{ ...element, dataType, name: elementName }}
				onChange={onChange}
			/>
		) : null}

		{optionKeys.includes('accept') && dataType === 'file' ? (
			<FileOptions
				index={index}
				basePath={base}
				element={{ ...element, dataType, name: elementName }}
				onChange={handleFileOptionChange}
			/>
		) : null}
		{dataType === 'file' && filesOffCollections.length ? (
			<Alert
				className='card-def-element__type-note'
				type='info'
				showIcon
				data-testid='files-off-door'
				message={<FilesOffDoor collections={filesOffCollections} />}
			/>
		) : null}
		{optionKeys.includes('accept') && dataType === 'file' && pendingDetach ? (
			<Alert
				className='card-def-element__type-note'
				type='info'
				showIcon
				data-testid='file-detach-note'
				message='When you save, the files here that are not photos move to each item’s Other attachments.'
			/>
		) : null}

		{optionKeys.includes('accept') && dataType === 'location' ? (
			<LocationOptions
				index={index}
				basePath={base}
				element={{ ...element, dataType, name: elementName }}
				onChange={onChange}
			/>
		) : null}

		{/* A date's bounds, its range pairing and its clock step. Offered for
		    date, date-time and time — `min` is on every one of them, which is
		    what the policy list keys the panel off. */}
		{optionKeys.includes('min') && isDate ? (
			<DateOptions
				index={index}
				basePath={base}
				element={{ ...element, dataType, name: elementName }}
				onChange={handleMultiAwareChange}
				elements={dateOptionElements}
				inGroup={isColumn}
				onCreateEndElement={onInsertElementAfter && !isColumn ? handleCreateEndElement : null}
				saveError={saveError}
				// A date holds one value (KPR-1146): Multiple values is shown
				// only where the saved element still holds a list, to turn off.
				savedMulti={!!savedElement && ['date', 'date-time'].includes(savedElement.dataType) && savedElement.options?.allowMultiple === true}
			/>
		) : null}

		{optionKeys.includes('identity') ? (
			<UserOptions
				index={index}
				basePath={base}
				element={{ ...element, dataType, name: elementName }}
				onChange={handleMultiAwareChange}
				saveError={saveError}
				inGroup={isColumn}
				identityHolder={identityHolder}
			/>
		) : null}
		{multiInPanel ? pendingShapeNote : null}
		<MultiToggleDialog
			open={!!shapeDialog}
			refusal={shapeDialog?.refusal}
			label={element?.label?.singular}
			asksDetach={Boolean(shapeDialog?.detach)}
			onConvert={(conversion) => {
				if (shapeDialog.detach) {
					const answers = mergeConversionAnswers(shapeDialog.options?.[SHAPE_INSTRUCTION_KEY], conversion, { ...element, dataType, options: shapeDialog.options }, savedElement);
					onChange(`${base}.options`, { ...shapeDialog.options, [SHAPE_INSTRUCTION_KEY]: answers === undefined ? conversion : answers });
				}
				else applyMultiToggle(shapeDialog.checked, conversion);
				setShapeDialog(null);
			}}
			onCancel={() => setShapeDialog(null)}
		/>

		{optionKeys.includes('currencies') ? (
			<CurrencyOptions
				index={index}
				basePath={base}
				element={{ ...element, dataType, name: elementName }}
				onChange={onChange}
				saveError={saveError}
				conversion={conversion?.key === 'convertFrom' ? conversion : null}
			/>
		) : null}

		{optionKeys.includes('measure') ? (
			<MeasurementOptions
				index={index}
				basePath={base}
				element={{ ...element, dataType, name: elementName }}
				onChange={onChange}
				saveError={saveError}
				conversion={conversion?.key === 'convertFrom' ? conversion : null}
				savedOwnUnits={savedElement?.dataType === 'measurement' ? savedElement.options?.ownUnits || null : null}
			/>
		) : null}

		{showChoices ? (
			<CardDefElementChoices
				index={index}
				basePath={base}
				element={element}
				onChange={onChange}
			/>
		) : null}

		{isColumn ? null : (
			<DrivenFromModal
				open={drivenModalOpen}
				onCancel={() => setDrivenModalOpen(false)}
				onSave={(config) => {
					onChange(`${base}.options.drivenFrom`, config);
					setDrivenModalOpen(false);
				}}
				element={element}
				targetCard={targetCard}
				collectionCards={collectionCards}
				collectionId={collectionId}
			/>
		)}
		{/* A COLUMN's formula (KPR-686) is read in one entry's scope: its
		    group's columns first, the item's elements (the card's) after. */}
		<FormulaModal
			open={formulaModalOpen}
			onCancel={() => setFormulaModalOpen(false)}
			onSave={(config) => {
				onChange(`${base}.options.drivenFrom`, config);
				setFormulaModalOpen(false);
			}}
			element={{ ...element, name: elementName || element?.name, dataType }}
			targetCard={targetCard}
			collectionCards={collectionCards}
			siblingElements={formulaOwnElements}
			entry={isColumn && column?.groupElement ? { group: column.groupElement, columns: ownNamedElements } : null}
		/>
	</>;

	// const elementLabel = (
	// 	<div className='card-def-element-row' onClick={(e) => e.stopPropagation()}>
	// 		<div className='card-def-element-row__label'>
	// 			{typeIconEl}
	// 			<span>{element?.label?.singular || '(untitled)'}</span>
	// 		</div>
	// 		<div className='card-def-element-row__extra'>
	// 			{badges.length ? (
	// 				<div className='card-def-element-header__badges'>{badges}</div>
	// 			) : null}
	// 			{actionMenu}
	// 			{dragHandleEl}
	// 		</div>
	// 	</div>
	// );

	// const elementLabel = (
	// 	<div
	// 		className='card-def-element-row'
	// 		onClick={(e) => e.stopPropagation()}
	// 		style={{
	// 		display: 'flex',
	// 		alignItems: 'center',
	// 		justifyContent: 'space-between',
	// 		width: '100%',
	// 		gap: 8,
	// 		}}
	// 	>
	// 		<div
	// 		className='card-def-element-row__label'
	// 		style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}
	// 		>
	// 			{typeIconEl}
	// 			<span
	// 				style={{
	// 				overflow: 'hidden',
	// 				textOverflow: 'ellipsis',
	// 				whiteSpace: 'nowrap',
	// 				}}
	// 			>
	// 				{element?.label?.singular || '(untitled)'}
	// 			</span>
	// 		</div>
	// 		<div
	// 			className='card-def-element-row__extra'
	// 			style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}
	// 		>
	// 			{badges.length ? (
	// 				<div className='card-def-element-header__badges' style={{ display: 'inline-flex', gap: 4 }}>
	// 				{badges}
	// 				</div>
	// 			) : null}
	// 			{actionMenu}
	// 			{dragHandleEl}
	// 		</div>
	// 	</div>
	// );

	// An untyped element's whole body: the gallery, and the reason it is the
	// only thing here. No name, no options — every one of them depends on the
	// answer, and a form that asks in the wrong order is asking twice.
	const typeGallery = (
		<div className='card-def-element__gallery'>
			<p className='form-panel__hint'>
				What does this field hold? Everything else about it — its name, its options, how it is entered — follows from the answer.
			</p>
			{typeErrorAlert(typeError)}
			<DataTypePicker
				variant='gallery'
				value={dataType}
				onChange={handleTypeChange}
				exclude={typesWithheld}
			/>
			{typeWithheldNote}
		</div>
	);

	// A row with no name yet reads as "New element" rather than "(untitled)":
	// an element being authored is not a broken one.
	const rowName = (element?.label?.singular || '').trim()
		|| (untyped ? (isColumn ? 'New column' : 'New element') : '(untitled)');

	const elementLabel = (
		<div className="card-def-element-row">
			<div className="card-def-element-row__label">
				{typeBadgeEl}
				<span className="card-def-element-row__text">
					<Typography.Text ellipsis={{ tooltip: rowName }}>
						{rowName}
					</Typography.Text>
					{/* Inside a set the stored name is namespaced by the set's
					    key — what filters, formulas and exports call it. */}
					{namespace && !untyped ? (
						<span className="card-def-element-row__name" data-testid="element-stored-name">{elementName}</span>
					) : null}
				</span>
				{element?.options?.required ? (
					<span className="required-indicator" aria-label="Required">*</span>
				) : null}
			</div>
			<div className="card-def-element-row__extra">
				{badges.length ? (
					<span className="card-def-element-header__badges">{badges}</span>
				) : null}
				{actionMenu}
				{dragHandleEl}
			</div>
		</div>
	);

	return (<>
		{/* No `collapsible='header'`: the whole row is the toggle; the ⋯ and
		    the drag handle opt out by stopping propagation. */}
		<Collapse
			className='card-def-element-collapse'
			activeKey={isOpen ? [panelKey] : []}
			onChange={onToggleOpen}
			items={[
				{
					key: panelKey,
					label: elementLabel,
					children: untyped ? typeGallery : elementDetail,
				}
			]}
		/>
	</>);

};



export function CardDefElementChoices(props) {

	const {
		index,
		element,
		onChange,
		showTitle = true,
		showNewButton = true,
		// Where the element lives in the draft (a column of a repeating group
		// is not `elements[index]`).
		basePath = null,
	} = props;

	const path = `${basePath || `elements[${index}]`}.options.choices`;
	const choices = element?.options?.choices || [];

	// Row identities, reconciled during render for the reason the element
	// rows above are: an effect-filled list keys the first paint `undefined`
	// (React's "unique key" warning) and remounts every choice a tick later,
	// taking the row's own state with it.
	const [choiceUiIds, setChoiceUiIds] = useState(() => choices.map(() => ulid()));
	let reconciledChoiceIds = choiceUiIds;
	if (reconciledChoiceIds.length !== choices.length) {
		reconciledChoiceIds = [...reconciledChoiceIds];
		while (reconciledChoiceIds.length < choices.length) reconciledChoiceIds.push(ulid());
		while (reconciledChoiceIds.length > choices.length) reconciledChoiceIds.pop();
		setChoiceUiIds(reconciledChoiceIds);
	}

	const addChoice = () => {
		const next = [...choices, { value: '', label: '', color: '', icon: '', }];
		setChoiceUiIds(prev => [...prev, ulid()]);
		onChange(path, next);
	};

	const removeChoice = (choiceIdx) => {
		const next = choices.filter((_, i) => i !== choiceIdx);
		setChoiceUiIds(prev => prev.filter((_, i) => i !== choiceIdx));
		onChange(path, next);
	};

	// DnD for choices
	const choiceIds = reconciledChoiceIds;
	// const sensors = useSensors( 
	// 	useSensor( TouchSensor, { activationConstraint: {
	// 		delay: 150,
	// 		tolerance: 8,
	// 	}}),
	// 	useSensor( MouseSensor, { activationConstraint: { distance: 8 } } ),
	// );

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
	);

	const onChoiceDragEnd = ({ active, over }) => {
		if (!over || active.id === over.id) return;
		const oldIndex = choiceIds.indexOf(active.id);
		const newIndex = choiceIds.indexOf(over.id);
		const reordered = arrayMove(choices, oldIndex, newIndex);
		setChoiceUiIds(prev => arrayMove(prev, oldIndex, newIndex));
		onChange(path, reordered);
	};

	return <>
		{showTitle && <div className='micro-label card-def-element__section'>Choices</div>}
		{showNewButton && <Button size='small' icon={<PLUS />} onClick={addChoice}>New choice</Button>}

		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragEnd={onChoiceDragEnd}
		>
			<SortableContext
				items={choiceIds}
				strategy={verticalListSortingStrategy}
			>
				{choices.map((c, cIdx) => (
					<SortableChoice
						key={choiceIds[cIdx]}
						id={choiceIds[cIdx]}
					>
						{(dragHandleProps) => (
							<CardDefElementChoice
								// key={cIdx}
								index={index}
								basePath={basePath}
								choiceIndex={cIdx}
								choice={c}
								onChange={onChange}
								onRemove={removeChoice}
								dragHandleProps={dragHandleProps}
							/>
						)}
					</SortableChoice>
				))}
			</SortableContext>
		</DndContext>
	</>;

};



export function CardDefElementChoice(props) {

	const {
		index,
		choiceIndex,
		choice,
		onChange,
		onRemove,
		dragHandleProps,
		basePath = null,
	} = props;

	const base = `${basePath || `elements[${index}]`}.options.choices[${choiceIndex}]`;

	// The stored choice value is derived from the label (there is no separate
	// value input in this form). Follow label edits until the value has
	// diverged (e.g. set by the API or the legacy collection form).
	const handleLabelChange = (name, newLabel) => {
		const prevLabel = choice?.label || '';
		onChange(name, newLabel);
		if (!choice?.value || choice.value === slugify(prevLabel)) {
			onChange(`${base}.value`, slugify(newLabel));
		}
	};

	const handle = dragHandleProps ? (
		<span
			ref={dragHandleProps.setActivatorNodeRef}
			{...dragHandleProps.attributes}
			{...dragHandleProps.listeners}
			style={{ cursor: 'grab', display: 'inline-flex', alignItems: 'center', marginRight: 8 }}
		>
			<DRAG_HANDLE />
		</span>
	) : null;

	return (
		<div className='choice-row'>
			<div className='choice-row__fields'>
				{handle}
				<InputText
					elementName={`${base}.label`}
					elementLabel='Label'
					value={choice.label}
					onChange={handleLabelChange}
					required={true}
				/>
				<InputIconSelect
					elementName={`${base}.icon`}
					elementLabel='Icon and color'
					value={choice.icon}
					onChange={onChange}
					colorElementName={`${base}.color`}
					colorValue={choice.color}
					onColorChange={onChange}
				/>

				<Button
					type='text'
					danger
					icon={<CLOSE />}
					aria-label='Remove choice'
					title='Remove choice'
					onClick={() => onRemove(choiceIndex)}
				>
					Remove
				</Button>
			</div>
		</div>
	);

};
